import React from 'react';
import {
    FormGroup,
    Button,
    FormText,
    Badge,
    UncontrolledTooltip,
} from 'reactstrap';
import {
    orderBy, flatten, uniqBy, isEmpty, intersection,
} from 'lodash-es';
import { v4 as uuid } from 'uuid';
import * as moment from 'moment';
import {
    faUserClock,
    faRecycle,
    faCircleNotch,
    faSave,
    faPrint,
    faSort,
    faFileContract,
    faArrowAltCircleLeft,
    faPlusCircle,
    faTrash,
    faArrowUpRightFromSquare,
    faCheck,
    faExclamationTriangle,
    faLockOpen,
    faLock,
    faTasks,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@s20/components/common/Link';
import { crewLeadLabel } from '@s20/i18n';
import { formatOffset } from '@s20/common/util/date';
import { getCounty } from '@s20/common/util/location';
import { clone as cloneUtil } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    FormCheckbox,
    UncontrolledCollapseUnderlineHeader,
    CollapseUnderlineHeaderWithIcon,
    CollapseTableElement,
    FormLabel,
    onFieldChange,
    onReactSelectChanged,
    FlexCenterRow,
    toasty,
    FlexEndRow,
    FormBlocker,
    SmallButton,
    Input,
    Textarea,
    IconButton,
    Flex,
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes,
    MomentDateFormat,

    AppNavPaths,
    ChargeTypeResourceTypes,
    CustomFormContext,
} from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { Can } from '../Can';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ValidatedSelect, {
    CompactSelectStyles,
    SuperCompactSelectStyles,
} from '../common/forms/ValidatedSelect';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import { ChargeTypeUnitNames, ChargeTypeUnits } from '../chargeType/ChargeType';
import { NumericInput } from '../common/forms/NumericInput';
import ReorderListWidget from '../widgets/ReorderListWidget';
import { CountyLabel } from '../uscounties/CountyLabel';
import { PriceDisplay, FormatPrice } from '../common/PriceDisplay';
import { TipAlert } from '../common/TipAlert';
import AddressFormNew from '../address/AddressFormNew';
import CustomForms from '../customForm/CustomForms';
import { ModalForm } from '../common/forms/ModalForm';
import { getCompanyContactsOptions } from '../contact/Contact';
import { Modal } from '../common/Modal';
import InvoiceHistoryForm from './InvoiceHistoryForm';
import {
    Invoice,
    InvoiceStatus,
    InvoiceChargeDetailType,
    InvoiceChargeDetailAdjustment,
} from './Invoice';

const api_date_format = 'YYYYMMDD';

const getGroupBillQuantity = (invoiceTimesheets, uid, configuredQuantity) => {
    const firstCharge = invoiceTimesheets
        .flatMap((timesheet) => timesheet.invoiceCharges)
        .find((charge) => charge.invoiceChargeTypeGroupUID === uid && charge.chargeTypeGroupId == null);

    return firstCharge?.chargeTypeUnitsId === ChargeTypeUnits.Hours
        ? firstCharge.billQty ?? 0
        : configuredQuantity;
};

const filterContractTimesheets = (invoice, contractTimesheets) => {
    const ids = invoice.invoiceTimesheets.map((x) => x.timesheetId);

    return contractTimesheets.filter((x) => !ids.includes(x.timesheetId));
};

const formatAddress = (location) => {
    if (!location) return '';
    return `${location.address1}${location.address2 ? ' ' + location.address2 : ''} ${location.city}, ${location.state} ${location.zip}`;
};

// Extract zip code or postal code from jobLocation string
// Supports US zip codes (5 digits) and Canadian postal codes (A1A 1A1 or A1A1A1)
// Gets the second match if there are two, otherwise gets the first
const extractZipCodeFromJobLocation = (jobLocation) => {
    if (!jobLocation) return null;

    // Find all US zip codes (5 digits) and Canadian postal codes (letter-number-letter-letter-number-letter)
    const zipCodeRegex = /\b(?:\d{5}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)\b/g;
    const matches = jobLocation.match(zipCodeRegex);

    if (!matches) return null;

    // If there are 2 or more matches, return the second one
    // Otherwise return the first (and only) one
    const selectedMatch = matches.length >= 2 ? matches[1] : matches[0];

    // Normalize postal code format to include space if it's Canadian
    if (selectedMatch.length === 6 && /^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/.test(selectedMatch)) {
        // Canadian postal code without space - add space in the middle
        return selectedMatch.substring(0, 3) + ' ' + selectedMatch.substring(3);
    }

    return selectedMatch;
};

const emptyAdjustmentHelper = () => ({
    date: '',
    useChargeTypeGroups: false,
    timesheet: null,
    chargeTypeGroup: null,
    chargeType: null,
    resource: '',
    employeeId: null,
    equipmentId: null,
    billingRate: null,
    invoiceChargeTypeGroupUID: null,
    rate: '',
    qbInventoryItem: '',
});

export default class InvoiceSlideout extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.timesheetDetailsRef = React.createRef();
        this.timesheetsWidgetRef = React.createRef();
        this.invoiceDetailsWidgetRef = React.createRef();
        this.editChargeTypeGroupRef = React.createRef();
        this.addChargeTypeGroupRef = React.createRef();
        this.addressFormRef = React.createRef();

        const stateBase = {
            originalDetails: '',
            originalAdjustments: JSON.stringify([]),
            invoice: {},
            adjustments: [],
            customers: [],
            invoiceCustomer: null,
            companyContactsOptions: [],
            projectManagerContactOptions: [],
            contracts: [],
            contractsFiltered: [],
            timesheetDetailsOpen: false,
            showInvoiceHistory: false,
            hourlyChargesOpen: true,
            isPrinting: false,
            isNew: false,
            reverting: false,
            current: {},
            billValid: true,
            contractTimesheets: [],
            isLoadingContractTimesheets: false,
            showAddTimesheetModal: false,
            showCustomerOverrideModal: false,
            showDeleteInvoiceModal: false,
            showRestartInvoiceModal: false,
            adjustmentModalIndex: null,
            showAddChargeTypeGroupsForm: false,
            showEditChargeTypeGroupsForm: false,
            toggleConfirmDraftModal: false,
            showRemoveTimesheetModal: false,
            showAddChargeTypeGroupModal: false,
            selectedContractTimesheet: {},
            selectedTimesheetToDelete: {},
            saving: false,
            approving: false,
            printing: false,
            drafting: false,
            deleting: false,
            selectedSortableInvoiceDetails: [],
            selectedSortableTimesheets: [],
            showReorderWidget: false,
            showTimesheetsReorderWidget: false,
            inventoryItems: [],
            selectedCTGroup: null,
            addCTGroupDate: null,
            associateInventoryItem: false,
            overrideChargeTypeRules: false,
            adjustmentHelper: emptyAdjustmentHelper(),
            contract: null,
            captureCounty: false,
            usCounties: [],
            validationError: null,
            selectedBillingCharges: [],
            selectAllBillingCharges: false,
            ...new BaseFormViewModel(),
            vbOffices: [],
        };

        this.state = stateBase;
        this.onChange = this.onChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    getEditedGroupDetails = () => this.state?.editedGroupDetails?.filter((x) => x.invoiceChargeTypeGroupUID != null) || [];

    onAddChargeTypeGroup = () => {
        const invoiceChargeTypeGroupUID = uuid();

        const { invoice, addCTGroupDate } = this.state;

        const unusedCharges = flatten(
            (invoice.invoiceTimesheets ?? []).map((x) => x.invoiceCharges),
        ).filter((x) => x.invoiceChargeTypeGroupUID == null && x.chargeTypeGroupId == null && x.dateDisplay == addCTGroupDate);

        const ctGroupCharges = flatten(
            (invoice.invoiceTimesheets ?? []).map((x) => x.invoiceCharges),
        ).filter((x) => x.invoiceChargeTypeGroupUID != null && x.chargeTypeGroupId == null);

        const details = ctGroupCharges.filter((x) => x.invoiceChargeTypeGroupUID == invoiceChargeTypeGroupUID);

        const clonedDetails = cloneUtil(details);
        const clonedUnusedCharges = cloneUtil(unusedCharges);

        this.setState({
            showAddChargeTypeGroupsForm: true,
            originalGroupDetails: details,
            editedGroupDetails: clonedDetails,
            originalUnusedCharges: unusedCharges,
            editedUnusedCharges: clonedUnusedCharges,
            AddCTGroupUID: invoiceChargeTypeGroupUID,
            addCTGroupDate: null,
            selectedChargeTypeGroup: null,
            overrideChargeTypeRules: false,
        });
    };

    onAddChargeTypeGroupCharge_Add = () => {
        const {
            invoice, AddCTGroupUID, addCTGroupDate, selectedChargeTypeGroup,
        } = this.state;

        const allInvoiceCharges = (invoice.invoiceTimesheets ?? [])
            .flatMap((ts) => ts.invoiceCharges ?? []);

        const unusedCharges = [];

        for (let i = 0; i < selectedChargeTypeGroup.chargeTypes.length; i += 1) {
            const { billingRateTypeId, chargeTypeId } = selectedChargeTypeGroup.chargeTypes[i];

            const matches = allInvoiceCharges.filter((x) =>
                x.invoiceChargeTypeGroupUID == null &&
                x.chargeTypeGroupId == null &&
                new Date(x.dateDisplay).toDateString() === new Date(addCTGroupDate).toDateString() &&
                x.billingRateTypeId === billingRateTypeId &&
                x.chargeTypeId === chargeTypeId,
            );

            unusedCharges.push(...matches);
        }

        const ctGroupCharges = flatten(
            (invoice.invoiceTimesheets ?? []).map((x) => x.invoiceCharges),
        ).filter((x) => x.invoiceChargeTypeGroupUID != null && x.chargeTypeGroupId == null);

        const details = ctGroupCharges.filter((x) => x.invoiceChargeTypeGroupUID == AddCTGroupUID);

        const clonedDetails = cloneUtil(details);
        const clonedUnusedCharges = cloneUtil(unusedCharges);

        this.setState({
            showAddChargeTypeGroupModal: true,
            originalGroupDetails: details,
            editedGroupDetails: clonedDetails,
            originalUnusedCharges: unusedCharges,
            editedUnusedCharges: clonedUnusedCharges,
            isEdit_ChargeTypeGroup: false,
        });
    };

    onAddChargeTypeGroupCharge_Edit = () => {
        this.setState({
            showAddChargeTypeGroupModal: true,
            isEdit_ChargeTypeGroup: true,
        });
    };

    onAddChargesToGroup = () => {
        let {
            selectedCTGroup, editedUnusedCharges, editedGroupDetails, isEdit_ChargeTypeGroup, AddCTGroupUID,
        } = this.state;

        const uid = isEdit_ChargeTypeGroup ? selectedCTGroup.invoiceChargeTypeGroupUID : AddCTGroupUID;

        const addCharges = editedUnusedCharges.filter((x) => x.isSelected);
        addCharges.map((x) => x.invoiceChargeTypeGroupUID = uid);

        // Add
        for (let i = 0; i < addCharges.length; i += 1) {
            editedGroupDetails.push(addCharges[i]);
        }

        // Remove
        editedUnusedCharges = editedUnusedCharges.filter((x) => !x.isSelected);

        this.setState({
            editedGroupDetails,
            editedUnusedCharges,
            showAddChargeTypeGroupModal: false,
        });
    };

    onViewChangeHistory = () => {
        this.setState({ showInvoiceHistory: true });
    };

    onCloseInvoiceHistory = () => {
        this.setState({ showInvoiceHistory: false });
    };

    onAddTimesheetClicked = async () => {
        // get latest ts's
        const { invoice } = this.state;

        const { contractId } = invoice;
        let workorderNumber = invoice.workOrderNumber ?? '';

        workorderNumber = workorderNumber.replaceAll('/', '*');
        if (contractId) {
            this.setState({ isLoadingContractTimesheets: true });
            this.toggleAddTimesheetModal();

            const weekDate = moment(invoice.invoiceDate).startOf('isoWeek').format(api_date_format);

            let [ts] = await Promise.all([
                httpClient.js(
                    ApiRoutes.invoice.timesheets(contractId, encodeURIComponent(workorderNumber), weekDate),
                ),
            ]);
            // get invoice timesheet id
            // let ids = invoice.invoiceTimesheets.map(x => x.id);
            ts = filterContractTimesheets(invoice, ts);

            this.setState({ contractTimesheets: ts });
        }
        this.setState({ isLoadingContractTimesheets: false });
    };

    onAdjustmentChanged = (i, ev) => {
        const { adjustments } = { ...this.state };
        const val = ev.target.value;
        const { name } = ev.target;
        adjustments[i][name] = val;
        const usage = Number(adjustments[i].userUsage ?? 1) || 1;
        const amount = adjustments[i].chargeQty * usage * adjustments[i].billingRate;
        adjustments[i].chargeAmount = Math.round(amount * 100) / 100;

        this.setState({ adjustments });

        //Update Percentage Surcharge
        this.updatePercentageSurcharge();
    };

    onApprove = async () => {
        const {
            invoice,
            adjustments,
            invoiceCustomer,
            contract,
        } = this.state;

        const { invoiceChargeDetails } = invoice;

        // 1) Filter to only line items that are not removed.
        const lineItems = invoiceChargeDetails.filter((cd) =>
            cd.invoiceChargeDetailTypeId === InvoiceChargeDetailType.LineItem &&
            cd.current &&
            !cd.removed,
        );

        // Validate that every charge detail has a valid inventory item.
        const invalidInventoryItems = lineItems.filter(
            (charge) =>
                !charge.userQuickBooksInventoryItem ||
                charge.userQuickBooksInventoryItem === 'NOT CONFIGURED',
        );

        // Validate that every charge detail has a valid sales account.
        const invalidSalesAccounts = lineItems.filter(
            (charge) =>
                charge.current &&
                (!charge.userQuickBooksSalesAccount ||
                    charge.userQuickBooksSalesAccount === 'NO SALES ACCOUNT'),
        );

        // Optionally, perform similar validations for adjustments.
        const invalidAdjustmentInventoryItems = adjustments.filter(
            (adj) =>
                !adj.userQuickBooksInventoryItem ||
                adj.userQuickBooksInventoryItem === 'NOT CONFIGURED',
        );
        const invalidAdjustmentSalesAccounts = adjustments.filter(
            (adj) =>
                !adj.userQuickBooksSalesAccount ||
                adj.userQuickBooksSalesAccount === 'NO SALES ACCOUNT',
        );

        if (invalidInventoryItems.length > 0 || invalidAdjustmentInventoryItems.length > 0) {
            toasty.error(
                'Inventory items must be linked to charge details or adjustments before approval.',
            );
            return;
        }

        if (invalidSalesAccounts.length > 0 || invalidAdjustmentSalesAccounts.length > 0) {
            toasty.error(
                'Sales accounts must be linked to charge details or adjustments before approval.',
            );
            return;
        }

        if (invoiceCustomer.isIntercompany && !invoice.vbOfficeId) {
            toasty.error(
                'A vendor bill office must be selected before approval.',
            );
            return;
        }

        if (invoiceCustomer.isIntercompany && contract.endCompanyId && !invoice.endInvoiceOfficeId) {
            toasty.error(
                'End Invoice office must be selected before approval.',
            );
            return;
        }

        if (invoiceCustomer?.isIntercompany && !invoice.jobLocationAddress) {
            toasty.error(
                'Job location is required. Click Edit and enter the full address.',
            );
            return;
        }

        // Filter expanded charge type groups
        const unvalidChargeGroupAmount = invoiceChargeDetails.filter((cd) => {
            if (!cd.isExpanded) {
                return false;
            }
            const details = invoiceChargeDetails.filter((potentialChild) => potentialChild.parentInvoiceChargeId === cd.id);
            if (details.length == 0) {
                return false;
            }
            return cd.userChargeAmount !== details.reduce((sum, x) => sum + (x.userChargeAmount ?? 0), 0);
        },
        );
        if (unvalidChargeGroupAmount.length > 0) {
            toasty.error(
                'The amount for charge groups must equal the sum of their details, please fix the following charge groups before approval: ' + unvalidChargeGroupAmount.map((x) => x.userDescription).join(', '),
            );
            return;
        }

        // const contractRequireGLString = (contract?.requireGLString === true);
        // if (glStringsEnabled && contractRequireGLString && (invoice.glString == null || invoice.glString.length == 0)) {
        //    toasty.error(
        //        'GL String cannot be empty'
        //    );
        //    return;
        // }

        this.setState({ approving: true });
        try {
            await this.save();
            const response = await httpClient.post(ApiRoutes.invoice.approve(invoice.id), null, httpClient.format.none);
            if (response.ok) {
                toasty.success('Approved');
                this.onRefresh();
            } else {
                const serviceResponse = await response.json();
                toasty.error(
                    serviceResponse?.message ?? 'There was an issue approving the edited record. Please try your request again or contact support for assistance.',
                );
            }
        } catch (error) {
            console.error(error);
            toasty.error(
                'There was an issue approving the edited record. Please try your request again or contact support for assistance.',
            );
        } finally {
            this.resetForm();
            this.setState({ approving: false });
            this.onClose();
        }
    };

    onChange = onFieldChange;

    onClose = () => {
        // let { saving, originalEdit, edit } = { ...this.state }
        // if (!saving && !isEqual({ ...originalEdit }, { ...edit })) {
        //    let confirmed = window.confirm("You have unsaved changes, are you sure you want to leave?");
        //    if (!confirmed)
        //        return;
        // }

        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    onDelete = async () => {
        const { invoice, selectedBillingCharges } = this.state;

        this.setState({ deleting: true });

        try {
            if ((invoice ?? {}).id) {
                const deleteUrl = ApiRoutes.invoice.delete(invoice.id);
                const response = await httpClient.delete(deleteUrl, selectedBillingCharges, httpClient.format.none);
                if (response.ok) {
                    toasty.success('Deleted.');
                    this.onClose();
                } else if (response.status === 400) {
                    const serviceResponse = await response.json();
                    toasty.error(
                        serviceResponse?.message ?? 'There was an issue deleting the record.  Please try your request again or contact support for assistance.',
                    );
                } else {
                    const responseText = await response.text();
                    toasty.error(
                        responseText ?? 'There was an issue deleting the record.  Please try your request again or contact support for assistance.',
                    );
                }
            }
        } catch (ex) {
            console.error(ex);
            toasty.error(
                'There was an issue deleting the record.  Please try your request again or contact support for assistance.',
            );
        } finally {
            this.toggleDeleteInvoiceModal();
            this.setState({
                deleting: false,
                showDeleteInvoiceModal: false,
            });
        }
    };

    onDeleteChargeTypeGroup = (ctGroup) => {
        const { invoice } = this.state;

        // Remove charge detail from group
        const charges = flatten(
            (invoice.invoiceTimesheets ?? []).map((x) => x.invoiceCharges),
        ).filter((x) => x.chargeTypeGroupId == null && x.invoiceChargeTypeGroupUID == ctGroup.invoiceChargeTypeGroupUID);

        charges.map((x) => x.invoiceChargeTypeGroupUID = null);

        // Remove group
        for (let i = 0; i < invoice.invoiceTimesheets.length; i += 1) {
            const it = invoice.invoiceTimesheets[i].invoiceCharges;
            invoice.invoiceTimesheets[i].invoiceCharges = it.filter((x) => x.invoiceChargeTypeGroupUID != ctGroup.invoiceChargeTypeGroupUID);
        }

        this.setState({
            invoice,
        });
    };

    onDraft = async () => {
        this.setState({ drafting: true });
        const { invoice } = { ...this.state };

        if (
            invoice.useTCNumber
            && (invoice.tcNumber == null || invoice.tcNumber.length == 0)
        ) {
            toasty.error('TC # must be provided.');
            return;
        }

        if (invoice.useWorkRequest) {
            if (
                invoice.purchaseOrderNumber == null
                || invoice.purchaseOrderNumber.length == 0
            ) {
                toasty.error('PO # must be provided.');
                return;
            }

            if (
                invoice.workRequest == null
                || invoice.workRequest.length == 0
            ) {
                toasty.error('Work Request must be provided.');
                return;
            }
        }

        try {
            await this.silentSave();
            await httpClient.post(ApiRoutes.invoice.draft(invoice.id));
            toasty.success('Draft Created');
            this.onRefresh();
        } catch {
            toasty.error(
                'There was an issue creating the draft.  Please try your request again or contact support for assistance.',
            );
        } finally {
            this.resetForm();
            this.setState({ drafting: false, showConfirmDraftModal: false });
            // this.onClose();
        }
    };

    onEditChargeTypeGroup = (ctGroup) => {
        const { invoice, chargeTypeGroups } = this.state;

        const [chargeTypeGroup] = chargeTypeGroups.filter((x) => x.id == ctGroup.chargeTypeGroupId);

        const overrideChargeTypeRules = !!(chargeTypeGroup.hasTotalCharges && chargeTypeGroup.numTotalCharges);
        let billingRateTypeId = 1;

        const charpeTypeIds = [];
        for (let i = 0; i < chargeTypeGroup.chargeTypes.length; i += 1) {
            charpeTypeIds.push(chargeTypeGroup.chargeTypes[i].chargeTypeId);
            billingRateTypeId = chargeTypeGroup.chargeTypes[i].billingRateTypeId;
        }

        const unusedCharges = flatten(
            (invoice.invoiceTimesheets ?? []).map((x) => x.invoiceCharges),
        ).filter((x) => x.invoiceChargeTypeGroupUID == null
            && x.chargeTypeGroupId == null
            && new Date(x.dateDisplay).toDateString() == new Date(ctGroup.dateDisplay).toDateString()
            && x.billingRateTypeId == billingRateTypeId
            && charpeTypeIds.includes(x.chargeTypeId));

        const ctGroupCharges = flatten(
            (invoice.invoiceTimesheets ?? []).map((x) => x.invoiceCharges),
        ).filter((x) => x.invoiceChargeTypeGroupUID != null && x.chargeTypeGroupId == null);

        const details = ctGroupCharges.filter((x) => x.invoiceChargeTypeGroupUID == ctGroup.invoiceChargeTypeGroupUID);

        const clonedDetails = cloneUtil(details);
        const clonedUnusedCharges = cloneUtil(unusedCharges);

        this.setState({
            showEditChargeTypeGroupsForm: true,
            isEdit_ChargeTypeGroup: true,
            selectedCTGroup: ctGroup,
            originalGroupDetails: details,
            editedGroupDetails: clonedDetails,
            originalUnusedCharges: unusedCharges,
            editedUnusedCharges: clonedUnusedCharges,
            overrideChargeTypeRules,
        });
    };

    onOverrideContractChanged = (selection) => {
        const { invoice } = this.state;
        invoice.overrideContractId = selection.value;
        invoice.overrideContractNumber = selection.label;

        this.setState({
            invoice,
        });
    };

    onOverrideCustomerChanged = (selection) => {
        let { invoice, contracts, contractsFiltered } = this.state;
        invoice.overrideCustomerCompanyId = selection.value;
        invoice.overrideCustomerCompanyName = selection.label;
        invoice.overrideContractId = null;
        invoice.overrideContractNumber = null;

        contractsFiltered = contracts.filter(
            (x) => x.companyId == selection.value,
        );

        this.setState({
            invoice,
            contractsFiltered,
        });
    };

    onOverrideDelete = () => {
        const { invoice } = this.state;
        invoice.overrideCustomerCompanyId = null;
        invoice.overrideCustomerCompanyName = null;
        invoice.overrideContractId = null;
        invoice.overrideContractNumber = null;

        this.setState({
            invoice,
        });
    };

    onVendorBillOfficeChanged = (selection) => {
        const { invoice } = this.state;
        invoice.vbOfficeId = selection.value;

        this.setState({
            invoice,
        });
    };

    onEndInvoiceOfficeChanged = (selection) => {
        const { invoice } = this.state;
        invoice.endInvoiceOfficeId = selection.value;

        this.setState({
            invoice,
        });
    };

    onPrint = () => {
        this.setState({ printing: true });
        const { invoice } = { ...this.state };
        const route = ApiRoutes.report.invoice(invoice.id);

        this.setState({ printing: false });
        httpClient.open(route, '_self');
    };

    onRefresh = () => {
        const { invoice } = this.state;
        const model = {
            invoiceId: invoice.id,
        };
        this.open(model);
    };

    onRemoveDraftTimesheet = async () => {
        const { invoice, selectedTimesheetToDelete } = this.state;

        this.toggleRemoveTimesheetModal();

        try {
            const model = {
                invoiceId: invoice.id,
                timesheetId: selectedTimesheetToDelete.timesheetId,
            };
            await httpClient.post(
                ApiRoutes.invoice.draftTimesheetDelete(),
                model,
            );
            toasty.success('Timesheet Removed');
            this.onRefresh();
        } catch {
            toasty.error(
                'There was an issue removing the timesheet.  Please try your request again or contact support for assistance.',
            );
        } finally {
            this.resetForm();
            this.setState({ drafting: false });
            // this.onClose();
        }
    };

    onRemoveInvoiceChargeDetailClicked = (icd) => {
        const { invoice } = { ...this.state };
        const { invoiceChargeDetails } = { ...invoice };

        const index = invoiceChargeDetails.findIndex((x) => x.id === icd.id);
        const chargeDetail = { ...invoiceChargeDetails[index] };
        chargeDetail.removed = !chargeDetail.removed;
        chargeDetail.show = !chargeDetail.isAdjustment;
        invoice.invoiceChargeDetails[index] = { ...chargeDetail };
        this.setState({ invoice: { ...invoice } });

        //Update Percentage Surcharge
        this.updatePercentageSurcharge(invoice);

        //Remove CTG Childs if Exist
        const childs = invoiceChargeDetails.filter((x) => x.parentInvoiceChargeId === icd.id);
        childs.forEach((c) => {
            this.onRemoveInvoiceChargeDetailClicked(c);
        });
    };

    onRemoveTimesheetClicked = (ts) => {
        const { invoice } = this.state;

        if (invoice.invoiceStatusId == InvoiceStatus.Draft) {
            this.setState({
                selectedTimesheetToDelete: ts,
            });

            this.toggleRemoveTimesheetModal();
        } else {
            invoice.invoiceTimesheets = invoice.invoiceTimesheets.filter(
                (x) => x !== ts,
            );
        }

        this.setState({
            invoice,
        });
    };

    onReorderInvoiceChargeDetails = () => {
        // clone the list of charge types, to avoid mutating state.
        // pass to the widget.
        this.setState((state) => {
            const { invoice } = { ...state };
            const { invoiceChargeDetails } = { ...invoice };

            const selectedSortableInvoiceDetails = invoiceChargeDetails.filter((cd) => cd.current && !cd.removed && !cd.parentInvoiceChargeId && !cd.invoiceChargeTypeGroupUID);

            return {
                showReorderWidget: true,
                selectedSortableInvoiceDetails,
            };
        });
    };

    onReorderTimesheets = () => {
        // clone the list of timesheets, to avoid mutating state.
        // pass to the widget.
        this.setState((state) => {
            const { invoice } = { ...state };

            let selectedSortableTimesheets = invoice.invoiceTimesheets;
            selectedSortableTimesheets = selectedSortableTimesheets.map(
                (obj) => ({
                    ...obj,
                    sortableText:
                        `${obj.timesheetNumber
                        } - ${moment(obj.jobDate).format('M/D/YYYY')}`,
                }),
            );

            return {
                showTimesheetsReorderWidget: true,
                selectedSortableTimesheets,
            };
        });
    };

    updatePercentageSurcharge = () => {
        const { invoice, adjustments } = this.state;

        const subtotal = invoice.invoiceChargeDetails
            .filter((x) => x.billingUnitName !== ChargeTypeUnitNames.Percentage && !x.parentInvoiceChargeId && !x.invoiceChargeTypeGroupUID && !x.removed)
            .reduce((sum, x) => sum + (x.userChargeAmount ?? 0), 0);

        const adjustmentSubtotal = adjustments
            .filter((x) => x.billingUnitName !== ChargeTypeUnitNames.Percentage && !x.parentInvoiceChargeId && !x.removed)
            .reduce((sum, x) => sum + (x.chargeAmount ?? 0), 0);

        const totalSubtotal = subtotal + adjustmentSubtotal;

        invoice.invoiceChargeDetails = invoice.invoiceChargeDetails.map((x) => {
            if (x.billingUnitName === ChargeTypeUnitNames.Percentage) {
                return {
                    ...x,
                    chargeAmount: (x.billingRate ?? 0) * totalSubtotal / 100,
                    userChargeAmount: (x.billingRate ?? 0) * totalSubtotal / 100,
                };
            }
            return x;
        });

        this.setState({
            invoice,
        });
    };

    onRestart = async () => {
        const { invoice } = this.state;

        this.setState({
            restarting: true,
        });

        try {
            if ((invoice ?? {}).id) {
                const resp = await httpClient.post(
                    ApiRoutes.invoice.restart(invoice.id),
                );

                invoice.id = resp.data;

                toasty.success('Invoice restarted.');

                this.setState({
                    invoice,
                    restarting: false,
                });
                this.onRefresh();
            }
        } catch {
            toasty.error(
                'There was an issue restarting the invoice.  Please try your request again or contact support for assistance.',
            );

            this.setState({
                restarting: false,
            });
        } finally {
            this.toggleRestartInvoiceModal();
        }
    };

    onRevert = async () => {
        this.setState({ reverting: true });

        const { invoice } = { ...this.state };

        try {
            await this.save();
            await httpClient.post(ApiRoutes.invoice.revert(invoice.id));
            toasty.success('Reverted to Draft');
            this.onRefresh();
        } catch {
            toasty.error(
                'There was an error returning this invoice to draft status.  Please try your request again or contact support for assistance.',
            );
        } finally {
            this.resetForm();
            this.setState({ reverting: false });
        }
    };

    onSaveChargeTypeGroup = () => {
        const {
            selectedChargeTypeGroup, overrideChargeTypeRules, selectedCTGroup, isEdit_ChargeTypeGroup, chargeTypeGroups,
        } = this.state;

        const editedGroupDetails = this.getEditedGroupDetails();

        // Is there anything to do?
        if (editedGroupDetails.length == 0) {
            toasty.error('No charges are in this group.');
            return;
        }

        const targetGroup = isEdit_ChargeTypeGroup ? selectedCTGroup : selectedChargeTypeGroup;

        const chargeTypeGroup = isEdit_ChargeTypeGroup
            ? chargeTypeGroups.find((x) => x.id === selectedCTGroup.chargeTypeGroupId)
            : selectedChargeTypeGroup;

        if (overrideChargeTypeRules && targetGroup.hasTotalCharges) {
            if (targetGroup.numTotalCharges < editedGroupDetails.length) {
                toasty.error(`Charge group must contain up to ${targetGroup.numTotalCharges} charges`);
                return;
            }
        } else if (!overrideChargeTypeRules) {
            // Validate against group rules
            for (let i = 0; i < chargeTypeGroup.chargeTypes.length; i += 1) {
                const chargeType = chargeTypeGroup.chargeTypes[i];

                const items = editedGroupDetails.filter((x) => x.chargeTypeId == chargeType.chargeTypeId);
                if (items.length !== chargeType.quantity) {
                    toasty.error(`Charge group must contain (${chargeType.quantity}) "${chargeType.description}" charges`);
                    return;
                }
            }
        }

        this.saveChargeTypeGroup(overrideChargeTypeRules);
    };

    onSelectChange = onReactSelectChanged;

    onSelectedContractTimesheetChanged = (selection) => {
        this.setState({
            selectedContractTimesheet: selection,
        });
    };

    onSubmit = async () => {
        this.setState({ saving: true });

        try {
            await this.save();
        } catch {
            toasty.error(
                'There was an issue saving the edited record.  Please try your request again or contact support for assistance.',
            );
        } finally {
            this.setState({ saving: false });
        }
    };

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
    };

    onViewTimesheetClicked = async (ts) => {
        await this.context.setFormOpened(true);
        this.timesheetDetailsRef.current.open(ts.timesheetId);
    };

    addContractTimesheet = () => {
        // Need to check if its in draft, if it is, add the timesheet and then reset the status to new
        // otherise use current method
        let {
            invoice, selectedContractTimesheet, contractTimesheets, useLastTimesheetDate,
        } = this.state;

        // Validation logic
        if (!selectedContractTimesheet || Object.keys(selectedContractTimesheet).length === 0) {
            this.setState({
                validationError: 'Please select a timesheet before adding.',
            });
            return;
        }

        // Clear validation error if any
        this.setState({ validationError: null });

        if (selectedContractTimesheet) invoice.invoiceTimesheets.push(selectedContractTimesheet);

        contractTimesheets = filterContractTimesheets(
            invoice,
            contractTimesheets,
        );

        if (useLastTimesheetDate && selectedContractTimesheet.jobDate > invoice.invoiceDate) {
            invoice.invoiceDate = selectedContractTimesheet.jobDate;
        }

        this.setState({
            invoice,
            selectedContractTimesheet: {},
            contractTimesheets,
            showAddTimesheetModal: false,
        });
    };

    buildAdjustmentDescription = (i) => {
        const { adjustmentHelper } = this.state;
        const adjustments = [...this.state.adjustments];

        if (i == null || !adjustments[i]) return;

        adjustments[i] = { ...adjustments[i] };

        let chargeType = '';
        let rateDesc = '';
        let resource = '';

        if (
            adjustmentHelper.useChargeTypeGroups
            && adjustmentHelper.chargeTypeGroup !== null
        ) {
            chargeType = ` ${adjustmentHelper.chargeTypeGroup.invoiceDescription}`;
            adjustments[i].chargeQty = adjustmentHelper.chargeTypeGroup.billQuantity;
            adjustments[i].billingRate = adjustmentHelper.chargeTypeGroup.billRate;
            adjustments[i].userQuickBooksInventoryItem = adjustmentHelper.chargeTypeGroup.quickBooksInventoryItem?.name;
            adjustments[i].userQuickBooksSalesAccount = adjustmentHelper.chargeTypeGroup.quickBooksInventoryItem?.salesAccountName;
            // ChargeTypeGroupService fabricates a display-only placeholder (Id/SalesAccountId
            // default to 0, non-nullable long) when the group has no inventory item configured —
            // 0 isn't a real id, so `||` (not `??`) is required to treat it as unset.
            adjustments[i].inventoryItemId = adjustmentHelper.chargeTypeGroup.quickBooksInventoryItem?.id || null;
            adjustments[i].salesAccountId = adjustmentHelper.chargeTypeGroup.quickBooksInventoryItem?.salesAccountId || null;
        } else {
            chargeType = adjustmentHelper.chargeType == null
                ? ''
                : ` ${adjustmentHelper.chargeType.description}`;
            resource = adjustmentHelper.resource.length > 0
                ? ` - ${adjustmentHelper.resource}`
                : '';

            if (adjustmentHelper.billingRate != null) {
                adjustments[i].billingRate = parseFloat(
                    adjustmentHelper.billingRate.rate,
                );
                adjustments[i].userQuickBooksInventoryItem = adjustmentHelper.billingRate.qbInventoryItem;
                adjustments[i].userQuickBooksSalesAccount = adjustmentHelper.billingRate.qbSalesAccount;
                adjustments[i].inventoryItemId = adjustmentHelper.billingRate.qbInventoryItemId ?? null;
                adjustments[i].salesAccountId = adjustmentHelper.billingRate.qbSalesAccountId ?? null;
                rateDesc = adjustmentHelper.billingRate.description;
            } else {
                adjustments[i].billingRate = 0;
                adjustments[i].userQuickBooksInventoryItem = '';
                adjustments[i].userQuickBooksSalesAccount = '';
                adjustments[i].inventoryItemId = null;
                adjustments[i].salesAccountId = null;
            }
        }

        const usage = Number(adjustments[i].userUsage ?? 1) || 1;
        adjustments[i].chargeAmount = adjustments[i].chargeQty * usage * adjustments[i].billingRate;

        // custom
        if (rateDesc.length > 20) {
            adjustments[i].description = `${adjustmentHelper.date} ${rateDesc}${resource}`;
        } else {
            adjustments[i].description = adjustmentHelper.date + chargeType + resource;
        }

        adjustments[i].jobStartDate = adjustmentHelper.date;
        adjustments[i].jobEndDate = adjustmentHelper.date;
        adjustments[i].isCredit = adjustmentHelper.chargeType?.isCredit ?? false;

        adjustments[i].invoiceTimesheetId = adjustmentHelper.useChargeTypeGroups
            ? null
            : adjustmentHelper.timesheet?.id ?? null;
        adjustments[i].chargeTypeId = adjustmentHelper.useChargeTypeGroups
            ? null
            : adjustmentHelper.chargeType?.id ?? null;
        adjustments[i].chargeTypeGroupId = adjustmentHelper.useChargeTypeGroups
            ? adjustmentHelper.chargeTypeGroup?.id ?? null
            : null;
        adjustments[i].invoiceChargeTypeGroupUID = adjustmentHelper.useChargeTypeGroups
            ? adjustmentHelper.invoiceChargeTypeGroupUID ?? null
            : null;
        adjustments[i].employeeId = adjustmentHelper.employeeId ?? null;
        adjustments[i].equipmentId = adjustmentHelper.equipmentId ?? null;
        adjustments[i].billingUnitName = adjustmentHelper.chargeType?.unitsName ?? null;

        this.setState({
            adjustments,
        });
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (model) => {
        // reset adjustments and validation on the way back in.
        this.props.toggleShow(true);
        this.context.setFormOpened(true);

        this.setState({
            loading: true,
            formValidated: false,
            timesheetDetailsOpen: false,
        });

        await this.populateState();

        let { isNew } = { ...this.state };
        let invoice = new Invoice();

        if ((model ?? {}).invoiceId) {
            [invoice] = await Promise.all([
                httpClient.js(ApiRoutes.invoice.byId(model.invoiceId)),
            ]);
        } else {
            // get related timesheets
            try {
                [invoice] = await Promise.all([
                    httpClient.post(
                        ApiRoutes.invoice.create(),
                        model.billingIds,
                    ),
                ]);
            } catch (err) {
                console.error(err);
                toasty.error(
                    'Unable to load the invoice due to an internal error.',
                );
            } finally {
                isNew = true;
            }
        }

        const dispatchLocations = uniqBy(
            invoice.invoiceTimesheets.map((x) => ({
                value: x.dispatchLocationId,
                label: x.dispatchLocationName,
            })),
            'value',
        );

        const seenIds = new Set();
        invoice.invoiceChargeDetails = invoice.invoiceChargeDetails.map((c) => {
            if (!c.id || c.id === 0 || seenIds.has(c.id)) {
                c.id = uuid();
            }
            seenIds.add(c.id);
            return c;
        });

        invoice.invoiceSummaries = invoice.invoiceSummaries.map((c) => {
            if (c.id === 0) c.id = uuid();
            return c;
        });

        const weekStart = moment(invoice.invoiceDate)
            .subtract(6, 'days')
            .format('YYYYMMDD');
        const usCountyId = (invoice.usCountyId ?? null);
        const jobTypeId = (invoice.jobTypeId ?? null);
        const [
            contract,
            staff,
            equipment,
            chargeTypes,
            chargeTypeGroups,
            inventoryItems,
            salesAccounts,
            serviceItems,
            customers,
            contracts,
            invoiceCustomer,
            invoiceContract,
            vbOffices,
        ] = await Promise.all([
            httpClient.js(
                ApiRoutes.contract.filteredRates(invoice.contractId, weekStart, usCountyId),
            ),
            httpClient.js(
                ApiRoutes.invoice.slideoutStaff(
                    invoice.dispatchOfficeLocationId,
                ),
            ),
            httpClient.js(
                ApiRoutes.invoice.slideoutEquipment(
                    invoice.dispatchOfficeLocationId,
                ),
            ),
            httpClient.js(ApiRoutes.chargeType.byContract(invoice.contractId)),
            usCountyId || !jobTypeId ?
                httpClient.js(
                    ApiRoutes.chargeTypeGroup.byContract(invoice.contractId, usCountyId),
                ) :
                httpClient.js(
                    ApiRoutes.chargeTypeGroup.byContractAndJobType(invoice.contractId, jobTypeId),
                ),
            httpClient.js(ApiRoutes.typeAheads.quickBooksInventoryItems()),
            httpClient.js(ApiRoutes.typeAheads.quickBooksSalesAccounts()),
            httpClient.js(ApiRoutes.typeAheads.serviceItems()),
            httpClient.js(ApiRoutes.typeAheads.customers()),
            httpClient.js(ApiRoutes.typeAheads.contracts()),
            httpClient.js(ApiRoutes.company.byId(invoice.customerCompanyId)),
            httpClient.js(ApiRoutes.contract.byId(invoice.contractId)),
            httpClient.js(ApiRoutes.typeAheads.vendorBillOffices()),
        ]);

        const allowCustomizedEmailSubject = !!invoiceCustomer?.customizeEmailSubject;

        if (allowCustomizedEmailSubject && invoice && !invoice.invoicingEmailSubject) {
            const dispatchOfficeLocationName =
                vbOffices?.find((o) => o.value === invoice.dispatchOfficeLocationId)?.label ?? '';

            invoice.invoicingEmailSubject =
                `${invoice.customerCompanyName ?? ''}` +
                `${dispatchOfficeLocationName ? ` ${dispatchOfficeLocationName}` : ''}` +
                ` invoices for ${new Date(invoice.invoiceDate).toISOString().slice(0, 10)}`;
        }

        const companyContacts = await getCompanyContactsOptions(
            invoice.customerCompanyId,
        );

        const { tenantSettings } = this.context?.tenant;

        this.resetForm();

        // Intelligent zip code pre-fill from jobLocation if zipCode is null
        if (!invoice.zipCode && invoice.jobLocation) {
            const extractedZip = extractZipCodeFromJobLocation(invoice.jobLocation);
            if (extractedZip) {
                invoice.zipCode = extractedZip;
            }
        }
        this.setState({
            invoice,
            dispatchLocations,
            loading: false,
            restarting: false,
            isNew,
            inventoryItems: inventoryItems.map((item) => ({ value: item.label, label: item.label })),
            salesAccounts: salesAccounts.map((item) => ({ value: item.label, label: item.label })),
            serviceItems,
            associateInventoryItem: tenantSettings.useQuickBooksInvoicing,
            captureCounty: tenantSettings.captureCounty,
            chargeTypes,
            chargeTypeGroups,
            staff,
            equipment,
            contract,
            customers,
            invoiceCustomer,
            invoiceContract,
            contracts,
            glStringsEnabled: tenantSettings.glStringsEnabled,
            useLastTimesheetDate: tenantSettings.setInvoiceDateBasedOnLastTimesheet,
            companyContactsOptions: companyContacts.companyContactsOptions,
            projectManagerContactOptions: companyContacts.projectManagerContactOptions,
            vbOffices,
        });
    };

    async populateState() {
        const usCounties = await httpClient.js(ApiRoutes.USCounties.all());
        this.setState({ usCounties });
    }

    resetForm = () => {
        this.setState({
            adjustments: [],
            formValidated: false,
            deleting: false,
            saving: false,
            approving: false,
            printing: false,
            isNew: false,
        });
    };

    save = async () => {
        let { adjustments, invoice, invoiceCustomer } = cloneUtil({ ...this.state });

        if (invoiceCustomer?.isIntercompany && !invoice.jobLocationAddress) {
            toasty.error('Job location is required. Click Edit and enter the full address.');
            return;
        }

        invoice.invoiceChargeDetails = invoice.invoiceChargeDetails.map(
            (cd) => {
                if (cd.id !== null) cd.id = typeof cd.id === 'string' ? null : cd.id;
                return cd;
            },
        );

        invoice.invoiceSummaries = invoice.invoiceSummaries.map((cd) => {
            if (cd.id !== null) cd.id = typeof cd.id === 'string' ? null : cd.id;
            return cd;
        });

        adjustments = adjustments.map((a) => {
            a.id = typeof a.id === 'string' ? null : a.id;
            return a;
        });

        if (adjustments.length) invoice.invoiceChargeDetails.push(...adjustments);

        const resp = await httpClient.put(
            ApiRoutes.invoice.update(invoice.id),
            invoice,
        );
        if (resp.data) {
            invoice.id = resp.data;

            this.setState({ invoice, isNew: false }, () => {
                toasty.success('Edit saved.');
                this.resetForm();
                this.onRefresh();
            });
        } else {
            // error
            toasty.error(resp.message);
        }
    };

    saveChargeTypeGroup = () => {
        const {
            selectedCTGroup, selectedChargeTypeGroup, originalGroupDetails, addCTGroupDate,
            invoice, AddCTGroupUID, isEdit_ChargeTypeGroup,
        } = this.state;

        const editedGroupDetails = this.getEditedGroupDetails();
        const chargeTypeGroup = isEdit_ChargeTypeGroup ? selectedCTGroup : selectedChargeTypeGroup;
        const uid = isEdit_ChargeTypeGroup ? selectedCTGroup.invoiceChargeTypeGroupUID : AddCTGroupUID;
        const expandOnCrossDispatch = isEdit_ChargeTypeGroup ? selectedCTGroup.expandOnCrossDispatch : selectedChargeTypeGroup.expandOnCrossDispatch;

        // Remove deleted charges
        for (let d = 0; d < originalGroupDetails.length; d += 1) {
            const { billingDetailId } = originalGroupDetails[d];

            const editedCharge = editedGroupDetails.find((x) => x.billingDetailId == billingDetailId && x.billingRateTypeId == editedGroupDetails[d].billingRateTypeId);
            if (!editedCharge) {
                originalGroupDetails[d].invoiceChargeTypeGroupUID = null;
            }
        }

        // Add charges
        for (let d = 0; d < editedGroupDetails.length; d += 1) {
            const { billingDetailId } = editedGroupDetails[d];

            const charge = flatten(invoice.invoiceTimesheets.map((x) => x.invoiceCharges)).find((x) => x.billingDetailId == billingDetailId && x.billingRateTypeId == editedGroupDetails[d].billingRateTypeId);
            charge.invoiceChargeTypeGroupUID = uid;
            charge.expandOnCrossDispatch = expandOnCrossDispatch;
        }

        const calBillQty = getGroupBillQuantity(invoice.invoiceTimesheets, uid, chargeTypeGroup.billQuantity);

        // If this is an add - Add new charge row for ct group
        if (!isEdit_ChargeTypeGroup) {
            invoice.invoiceTimesheets[0].invoiceCharges.push({
                id: 0,
                timesheetId: invoice.invoiceTimesheets[0].timesheetId,
                chargeTypeName: `${moment(addCTGroupDate).format('MM/DD/YY')} ${chargeTypeGroup.invoiceDescription}`,
                billingDetailId: 0, // header, not used
                chargeTypeId: 17, // header, not used
                billQty: calBillQty,
                chargeQty: chargeTypeGroup.billAsDailyRate ? 1 : calBillQty,
                billingRateTypeId: 2,
                quickBooksInventoryItem: chargeTypeGroup.quickBooksInventoryItem?.name ?? '',
                quickBooksSalesAccount: chargeTypeGroup.quickBooksInventoryItem?.salesAccountName ?? '',
                invoiceChargeTypeGroupUID: uid,
                chargeTypeGroupId: chargeTypeGroup.id,
                current: true,
                billingRate: chargeTypeGroup.billRate ?? 0,
                chargeAmount: chargeTypeGroup.billRate * calBillQty ?? 0,
                date: addCTGroupDate,
                dateDisplay: addCTGroupDate,
                hasTotalCharges: chargeTypeGroup.hasTotalCharges,
                numTotalCharges: chargeTypeGroup.numTotalCharges,
                usCountyGroupId: chargeTypeGroup.usCountyGroupId,
                jobTypeId: chargeTypeGroup.jobTypeId,
                isTaxable: chargeTypeGroup.isTaxable,
                calculatedBillingRateTypeName: chargeTypeGroup.calculatedBillingRateTypeName,
                expandOnCrossDispatch,
            });
        }

        const groupHeader = flatten(invoice.invoiceTimesheets.map((x) => x.invoiceCharges))
            .find((x) =>
                x.invoiceChargeTypeGroupUID === uid &&
                x.chargeTypeGroupId > 0,
            );

        if (groupHeader) {
            groupHeader.expandOnCrossDispatch = expandOnCrossDispatch;
        };

        this.setState({
            invoice,
            showEditChargeTypeGroupsForm: false,
            showAddChargeTypeGroupsForm: false,
            overrideChargeTypeRules: false,
        });
    };

    silentSave = async () => {
        let { invoice, adjustments } = cloneUtil({ ...this.state });

        invoice.invoiceChargeDetails = invoice.invoiceChargeDetails.map(
            (cd) => {
                if (cd.id !== null) cd.id = typeof cd.id === 'string' ? null : cd.id;
                return cd;
            },
        );

        invoice.invoiceSummaries = invoice.invoiceSummaries.map((cd) => {
            if (cd.id !== null) cd.id = typeof cd.id === 'string' ? null : cd.id;
            return cd;
        });

        adjustments = adjustments.map((a) => {
            a.id = typeof a.id === 'string' ? null : a.id;
            return a;
        });

        if (adjustments.length) invoice.invoiceChargeDetails.push(...adjustments);

        const resp = await httpClient.put(
            ApiRoutes.invoice.update(invoice.id),
            invoice,
        );
        if (resp.data) {
            invoice.id = resp.data;
            this.setState({ invoice });

            // toasty.success('Edit saved.');
            this.resetForm();
            // this.onRefresh();
        } else {
            // error
            // toasty.error(resp.message);
        }
    };

    sortChargeDetailsByTimesheetOrder = () => {
        const { invoice } = { ...this.state };
        const timesheets = invoice.invoiceTimesheets;
        const charges = invoice.invoiceChargeDetails;
        invoice.invoiceChargeDetails = [];

        let index = 1;
        for (let i = 0; i < timesheets.length; i += 1) {
            const ts = timesheets[i];

            const tsCharges = charges.filter(
                (x) => x.timesheetNumber == ts.timesheetNumber,
            );
            for (let r = 0; r < tsCharges.length; r += 1) {
                index += 1;

                tsCharges[r].order = index;
                invoice.invoiceChargeDetails.push(tsCharges[r]);
            }
        }

        this.setState({ invoice });
    };

    toggleAddChargeTypeGroupModal = () => {
        const { showAddChargeTypeGroupModal } = this.state;
        this.setState({
            showAddChargeTypeGroupModal: !showAddChargeTypeGroupModal,
        });
    };

    toggleAddTimesheetModal = () => {
        const { showAddTimesheetModal } = this.state;
        this.setState({ showAddTimesheetModal: !showAddTimesheetModal });
    };

    openAdjustmentModal = (i) => {
        const { adjustments, chargeTypeGroups } = this.state;
        const adjustment = adjustments[i];
        if (!adjustment) return;

        this.setState({
            adjustmentModalIndex: i,
            adjustmentHelper: {
                ...emptyAdjustmentHelper(),
                date: adjustment.jobStartDate ?? '',
                useChargeTypeGroups: adjustment.invoiceChargeTypeGroupUID != null,
                invoiceChargeTypeGroupUID: adjustment.invoiceChargeTypeGroupUID ?? null,
                chargeTypeGroup: (chargeTypeGroups ?? []).find(
                    (x) => x.id === adjustment.chargeTypeGroupId,
                ) ?? null,
            },
        });
    };

    closeAdjustmentModal = () => {
        this.setState({
            adjustmentModalIndex: null,
            adjustmentHelper: emptyAdjustmentHelper(),
        });
    };

    toggleConfirmDraftModal = () => {
        const { invoice } = { ...this.state };

        if (
            invoice.useTCNumber
            && (invoice.tcNumber == null || invoice.tcNumber.length == 0)
        ) {
            toasty.error('TC # must be provided.');
            return;
        }

        if (invoice.useWorkRequest) {
            if (
                invoice.purchaseOrderNumber == null
                || invoice.purchaseOrderNumber.length == 0
            ) {
                toasty.error('PO # must be provided.');
                return;
            }

            if (
                invoice.workRequest == null
                || invoice.workRequest.length == 0
            ) {
                toasty.error('Work Request must be provided.');
                return;
            }
        }

        Modal.show({
            title: 'Create Draft',
            onSubmit: () => this.onDraft(),
            SaveButtonProps: {
                label: 'Create',
                savingLabel: 'Creating',
            },
            body: () => (
                <>
                    This action will generate Charge Details and the
                    Invoice Summary, please make sure all timesheets
                    are included.
                </>
            ),
        });
    };

    toggleDeleteInvoiceModal = () => {
        const { showDeleteInvoiceModal, invoice } = this.state;

        const newState = {
            showDeleteInvoiceModal: !showDeleteInvoiceModal,
        };

        const isRentalInvoice = (invoice.invoiceTimesheets ?? []).some((x) => x.isRentalTimesheet);
        if (!showDeleteInvoiceModal && isRentalInvoice) {
            const billingCharges = this.getBillingCharges();
            const selectedBillingCharges = billingCharges.map((charge) => charge.id);
            newState.selectedBillingCharges = selectedBillingCharges;
            newState.selectAllBillingCharges = true;
        }

        this.setState(newState);
    };

    toggleRemoveTimesheetModal = () => {
        Modal.show({
            title: 'Remove Timesheet',
            onSubmit: () => this.onRemoveDraftTimesheet(),
            SaveButtonProps: {
                label: 'Remove',
                savingLabel: 'Removing',
            },
            body: () => (
                <FormText style={{ marginBottom: '20px' }}>
                    Adding or removing any timesheets when in the
                    draft stage will remove and recalculate all
                    Invoice Charge Details and the Invoice Summary.
                    The Invoice status will reset to New.
                </FormText>
            ),
        });
    };

    toggleRestartInvoiceModal = () => {
        Modal.show({
            title: 'Restart Invoice',
            onSubmit: () => this.onRestart(),
            SaveButtonProps: {
                label: 'Restart',
                savingLabel: 'Restarting',
                icon: faRecycle,
                color: 'warning',
            },
            body: () => (
                <>
                    <p>
                        This action will &quot;regenerate&quot; the invoice and
                        charges back to a NEW state. The invoice number
                        assignment and timesheets will be retained.
                    </p>

                    <p style={{ color: 'red' }}>
                        <b>
                            WARNING: All manual invoice and charge edits
                            will be lost.
                        </b>
                    </p>
                </>
            ),
        });
    };

    onEditLocation = () => {
        const { invoice } = { ...this.state };
        const selectedAddress = {
            ...(invoice.jobLocationAddress || {}),
            countryId: invoice.jobLocationAddress?.countryId ?? invoice.countryId,
            countryCode: invoice.jobLocationAddress?.countryCode ?? invoice.countryCode,
            usStateId: invoice.jobLocationAddress?.usStateId ?? invoice.usStateId,
            usCountyId: invoice.jobLocationAddress?.usCountyId ?? invoice.usCountyId,
        };
        const addressFormTitle = 'Edit Job Location';
        this.addressFormRef.current.open(selectedAddress, addressFormTitle);
    };

    onSaveAddress = (address) => {
        const { invoice } = this.state;
        const hasChanged =
            invoice.jobLocationAddress?.address1 !== address.address1 ||
            invoice.jobLocationAddress?.address2 !== address.address2 ||
            invoice.jobLocationAddress?.city !== address.city ||
            invoice.jobLocationAddress?.state !== address.state ||
            invoice.jobLocationAddress?.zip !== address.zip;
        if (hasChanged) {
            invoice.jobLocationAddress = {
                ...address,
                countryId: address.countryId ?? invoice.countryId,
                countryCode: address.countryCode ?? invoice.countryCode,
                usCountyId: address.usCountyId,
                usStateId: address.usStateId,
            };
            invoice.jobLocation = formatAddress(address);
            invoice.zipCode = address.zip;
            invoice.usCountyId = address.usCountyId;
            invoice.jobLocationId = null;
            this.setState({ invoice });
        }
    };

    getBillingCharges = () => {
        const { invoice } = this.state;
        const isRentalInvoice = (invoice.invoiceTimesheets ?? []).some((x) => x.isRentalTimesheet);

        // For non-rental invoices, return empty array (no charge selection needed)
        if (!isRentalInvoice) {
            return [];
        }

        // Get all charges from all timesheets
        const allCharges = flatten(
            (invoice.invoiceTimesheets ?? [])
                .map((x) => x.invoiceCharges),
        ).filter((charge) => charge && charge.id);

        const rentalCharges = allCharges.filter((charge) =>
            charge.resourceTypeId === 6 && !charge.isOneTimeCharge && !charge.chargeTypeGroupId,
        );

        return rentalCharges;
    };

    initializeBillingChargesSelection = () => {
        const billingCharges = this.getBillingCharges();
        const selectedBillingCharges = billingCharges.map((charge) => charge.id);
        this.setState({
            selectedBillingCharges,
            selectAllBillingCharges: true,
        });
    };

    toggleSelectAllBillingCharges = () => {
        const { selectAllBillingCharges } = this.state;
        const billingCharges = this.getBillingCharges();
        if (selectAllBillingCharges) {
            this.setState({
                selectedBillingCharges: [],
                selectAllBillingCharges: false,
            });
        } else {
            this.setState({
                selectedBillingCharges: billingCharges.map((charge) => charge.id),
                selectAllBillingCharges: true,
            });
        }
    };

    toggleBillingChargeSelection = (chargeId) => {
        const { selectedBillingCharges } = this.state;
        const billingCharges = this.getBillingCharges();
        let newSelectedCharges;
        if (selectedBillingCharges.includes(chargeId)) {
            newSelectedCharges = selectedBillingCharges.filter((id) => id !== chargeId);
        } else {
            newSelectedCharges = [...selectedBillingCharges, chargeId];
        }

        const selectAllBillingCharges = newSelectedCharges.length === billingCharges.length;
        this.setState({
            selectedBillingCharges: newSelectedCharges,
            selectAllBillingCharges,
        });
    };

    redistributeExpandedAmounts = (invoice, changedIndex, parentInvoiceChargeId) => {
        const parent = invoice.invoiceChargeDetails.find(
            (x) => x.id === parentInvoiceChargeId || Number(x.id) === Number(parentInvoiceChargeId),
        );

        if (!parent) return;

        const children = invoice.invoiceChargeDetails
            .map((x, i) => ({ ...x, index: i }))
            .filter((x) => x.parentInvoiceChargeId === parentInvoiceChargeId && !x.removed);

        if (children.length === 0) return;

        const parentAmount = Number(parent.userChargeAmount) || 0;
        let changedAmount = 0;

        if (changedIndex >= 0) {
            const changedChild = invoice.invoiceChargeDetails[changedIndex];
            const changedQty = Number(changedChild.userChargeQty ?? 0) || 0;
            const changedUsage = Number(changedChild.userUsage ?? 1) || 1;
            const changedRate = Number(changedChild.userBillingRate ?? 0) || 0;

            changedAmount = changedQty * changedUsage * changedRate;

            invoice.invoiceChargeDetails[changedIndex].userChargeAmount = changedAmount;
        }


        const siblings = children.filter((c) => c.index !== changedIndex && !c.locked);
        const lockedAmount = children
            .filter((c) => c.index !== changedIndex && c.locked)
            .reduce((sum, c) => sum + (Number(invoice.invoiceChargeDetails[c.index].userChargeAmount) || 0), 0);

        const remainingAmount = parentAmount - changedAmount - lockedAmount;

        if (siblings.length === 0) return;

        const siblingRawAmounts = siblings.map((c) => {
            const qty = Number(c.userChargeQty ?? 0) || 0;
            const usage = Number(c.userUsage ?? 1) || 1;
            const rate = Number(c.userBillingRate ?? 0) || 0;
            return qty * usage * rate;
        });

        const siblingRawTotal = siblingRawAmounts.reduce((a, b) => a + b, 0);

        const exactAmounts = siblingRawAmounts.map((r) =>
            siblingRawTotal !== 0 ? (r / siblingRawTotal) * remainingAmount : remainingAmount / siblings.length,
        );

        const flooredAmounts = exactAmounts.map((a) => Math.floor(a * 100) / 100);
        const distributed = flooredAmounts.reduce((a, b) => a + b, 0);
        const remainingCents = Math.round((remainingAmount - distributed) * 100);

        const finalAmounts = [...flooredAmounts];
        siblings
            .map((_, i) => ({ i, remainder: exactAmounts[i] - flooredAmounts[i] }))
            .sort((a, b) => b.remainder - a.remainder)
            .slice(0, remainingCents)
            .forEach(({ i }) => {
                finalAmounts[i] += 0.01;
            });

        siblings.forEach((sibling, i) => {
            const qty = Number(invoice.invoiceChargeDetails[sibling.index].userChargeQty ?? 0) || 0;
            const usage = Number(invoice.invoiceChargeDetails[sibling.index].userUsage ?? 1) || 1;
            invoice.invoiceChargeDetails[sibling.index].userChargeAmount = finalAmounts[i];
            invoice.invoiceChargeDetails[sibling.index].userBillingRate = qty > 0 && usage > 0 ? Math.round((finalAmounts[i] / (qty * usage)) * 10000) / 10000 : 0;
        });

        this.setState({ invoice });
    };

    setChargeGroupMembership = (chargeDetailId, invoiceChargeTypeGroupUID) => {
        const { invoice } = { ...this.state };
        const chargeDetail = invoice.invoiceChargeDetails.find((x) => x.id === chargeDetailId);
        if (!chargeDetail) return;

        chargeDetail.current = invoiceChargeTypeGroupUID == null;
        chargeDetail.invoiceChargeTypeGroupUID = invoiceChargeTypeGroupUID;

        this.setState({ invoice });
    };

    revertChargesForGroup = (invoiceChargeTypeGroupUID) => {
        if (!invoiceChargeTypeGroupUID) return;

        const { invoice } = { ...this.state };
        invoice.invoiceChargeDetails
            .filter((x) => x.invoiceChargeTypeGroupUID === invoiceChargeTypeGroupUID)
            .forEach((x) => {
                x.current = true;
                x.invoiceChargeTypeGroupUID = null;
            });

        this.setState({ invoice });
    };


    renderAdjustmentDescriptionModal = () => {
        const {
            adjustmentModalIndex, adjustmentHelper, invoice,
            chargeTypes, chargeTypeGroups, staff, equipment, contract,
        } = this.state;

        if (adjustmentModalIndex == null) return null;

        // Employees/equipment assigned to the selected timesheet (from TimesheetDetail, not InvoiceCharge),
        // further restricted to those eligible for the selected charge type.
        const timesheet_employees = adjustmentHelper.timesheet
            ?.assignedEmployees
            ?? [];
        const timesheet_equipment = adjustmentHelper.timesheet
            ?.assignedEquipment
            ?? [];

        const charge_type_user_groups_ids = (
            (
                adjustmentHelper.chargeType
                ?? {}
            )
                .applicationGroup
            ?? {}
        )
            .userGroups
            ?? [];
        const eligible_staff_ids = staff
            .filter(
                (
                    x,
                ) => !!intersection(
                    x.groupIds,
                    charge_type_user_groups_ids,
                ).length,
            )
            .map((x) => x.id);
        const filtered_staff = timesheet_employees.filter(
            (
                x,
            ) => eligible_staff_ids.includes(
                x.id,
            ),
        );

        const charge_type_eq_types = (
            adjustmentHelper.chargeType
            ?? {}
        )
            .equipmentTypes
            ?? [];
        const charge_type_eq_types_ids = charge_type_eq_types.map(
            (
                x,
            ) => x.id,
        );
        const eligible_equipment_ids = equipment
            .filter(
                (
                    x,
                ) => charge_type_eq_types_ids.includes(
                    x.equipmentTypeId,
                ),
            )
            .map((x) => x.id);
        const filtered_equipment = timesheet_equipment.filter(
            (
                x,
            ) => eligible_equipment_ids.includes(
                x.id,
            ),
        );

        // Rate can't be picked until a Resource is chosen, for charge types that require one.
        const chargeTypeRequiresResource = (
            adjustmentHelper.chargeType?.resourceTypeId === ChargeTypeResourceTypes.Employee
            || adjustmentHelper.chargeType?.resourceTypeId === ChargeTypeResourceTypes.Equipment
            || adjustmentHelper.chargeType?.resourceTypeId === ChargeTypeResourceTypes.Rental
        );
        const resourceSelected = !!adjustmentHelper.employeeId || !!adjustmentHelper.equipmentId;
        const rateDisabled = !adjustmentHelper.chargeType || (chargeTypeRequiresResource && !resourceSelected);

        const [chargeType] = contract.contractChargeTypes.filter(
            (
                x,
            ) => x.chargeTypeId
                == (
                    adjustmentHelper.chargeType
                    ?? {}
                )
                    .id,
        );

        const billingRates = [];

        if (chargeType) {
            let rates = [];

            if (chargeType.billingRate) {
                rates.push(chargeType.billingRate);
            }

            if (chargeType.usCountyGroupBillingRates?.length > 0) {
                rates = [...rates, ...chargeType.usCountyGroupBillingRates.map((rate) => ({
                    ...rate.billingRate,
                    usCountyGroupName: rate.usCountyGroup?.description,
                }))];
            }

            rates.forEach((rate, index) => {
                const regDesc = rate
                    .regularRateDescription
                    != null
                    && rate.regularRateDescription
                        .length
                    > 0
                    ? rate.regularRateDescription
                    : 'REG';

                const otDesc = rate.overtimeRateDescription
                    != null
                    && rate.overtimeRateDescription
                        .length
                    > 0
                    ? rate.overtimeRateDescription
                    : 'OT';

                const regQBItemDesc = rate.regularQuickBooksInventoryItem
                    != null
                    && rate.regularQuickBooksInventoryItem
                        .length
                    > 0
                    ? rate.regularQuickBooksInventoryItem
                    : '';

                const otQBItemDesc = rate.overtimeQuickBooksInventoryItem
                    != null
                    && rate.overtimeQuickBooksInventoryItem
                        .length
                    > 0
                    ? rate.overtimeQuickBooksInventoryItem
                    : '';

                const regQBSalesDesc = rate.regularQuickBooksSalesAccount
                    != null
                    && rate.regularQuickBooksSalesAccount
                        .length
                    > 0
                    ? rate.regularQuickBooksSalesAccount
                    : '';

                const otQBSalesDesc = rate.overtimeQuickBooksSalesAccount
                    != null
                    && rate.overtimeQuickBooksSalesAccount
                        .length
                    > 0
                    ? rate.overtimeQuickBooksSalesAccount
                    : '';

                const baseIndex = index * 2;

                billingRates.push({
                    id: baseIndex + 1,
                    rate: rate.rate,
                    description: `${regDesc}${rate.usCountyGroupName ? ` (${rate.usCountyGroupName})` : ''}`,
                    qbInventoryItem: regQBItemDesc,
                    qbInventoryItemId: rate.regularQuickBooksInventoryItemId ?? null,
                    qbSalesAccount: regQBSalesDesc,
                    qbSalesAccountId: rate.regularQuickBooksSalesAccountId ?? null,
                }, {
                    id: baseIndex + 2,
                    rate: rate.otRate,
                    description: `${otDesc}${rate.usCountyGroupName ? ` (${rate.usCountyGroupName})` : ''}`,
                    qbInventoryItem: otQBItemDesc,
                    qbInventoryItemId: rate.overtimeQuickBooksInventoryItemId ?? null,
                    qbSalesAccount: otQBSalesDesc,
                    qbSalesAccountId: rate.overtimeQuickBooksSalesAccountId ?? null,
                });
            });
        }

        return (
            <ModalForm
                isOpen
                onSubmit={() => this.closeAdjustmentModal()}
                toggle={() => this.closeAdjustmentModal()}
                title="Adjustment Description"
                SaveButtonProps={{
                    label: 'Close',
                    icon: faCheck,
                    outline: true,
                }}
                CancelButtonProps={{
                    hidden: true,
                }}
            >
                <FormGroup>
                    <FormCheckbox
                        small
                        id="useChargeTypeGroups"
                        checked={adjustmentHelper.useChargeTypeGroups}
                        onChange={(
                            e,
                        ) => {
                            const { checked } = e.target;
                            if (!checked) {
                                this.revertChargesForGroup(adjustmentHelper.invoiceChargeTypeGroupUID);
                            }

                            adjustmentHelper.useChargeTypeGroups = checked;
                            adjustmentHelper.invoiceChargeTypeGroupUID = checked
                                ? (adjustmentHelper.invoiceChargeTypeGroupUID ?? uuid())
                                : null;
                            adjustmentHelper.timesheet = null;
                            adjustmentHelper.chargeTypeGroup = null;
                            adjustmentHelper.chargeType = null;
                            adjustmentHelper.resource = '';
                            adjustmentHelper.employeeId = null;
                            adjustmentHelper.equipmentId = null;
                            adjustmentHelper.billingRate = null;
                            this.setState(
                                {
                                    adjustmentHelper,
                                },
                            );
                            this.buildAdjustmentDescription(adjustmentModalIndex);
                        }}
                        labelText="Charge Type Groups"
                    />
                </FormGroup>

                {adjustmentHelper.useChargeTypeGroups && (
                    <Input
                        label="Date"
                        id="adjustmentDate"
                        name="adjustmentDate"
                        value={
                            adjustmentHelper.date
                                ? moment(
                                    adjustmentHelper.date,
                                    MomentDateFormat.MonthDayYearSlashesNoLeading,
                                ).format(
                                    MomentDateFormat.DateOnlyUTCHyphenated,
                                )
                                : ''
                        }
                        onChange={(
                            ev,
                        ) => {
                            const val = ev
                                ?.target
                                ?.value;
                            adjustmentHelper.date = moment(
                                val,
                            ).format(
                                MomentDateFormat.MonthDayYearSlashesNoLeading,
                            );
                            this.setState(
                                {
                                    adjustmentHelper,
                                },
                            );
                            this.buildAdjustmentDescription(
                                adjustmentModalIndex,
                            );
                        }}
                        type="date"
                    />
                )}

                {!adjustmentHelper.useChargeTypeGroups && (

                    <ValidatedSelect
                        styles={CompactSelectStyles}
                        label="Timesheet"
                        minMenuHeight={
                            100
                        }
                        maxMenuHeight={
                            200
                        }
                        id="adjustmentTimesheetDropdown"
                        name="adjustmentTimesheetDropdown"
                        hideClear
                        options={
                            invoice.invoiceTimesheets
                            ?? []
                        }
                        getOptionLabel={(
                            option,
                        ) => option.jobDateTimesheetNumberDisplay
                            ?? option.timesheetNumber}
                        getOptionValue={(
                            option,
                        ) => option.id}
                        onChange={(
                            selection,
                        ) => {
                            adjustmentHelper.timesheet = selection;
                            adjustmentHelper.date = moment(
                                selection.jobDate,
                            ).format(
                                MomentDateFormat.MonthDayYearSlashesNoLeading,
                            );
                            adjustmentHelper.chargeType = null;
                            adjustmentHelper.resource = '';
                            adjustmentHelper.employeeId = null;
                            adjustmentHelper.equipmentId = null;
                            adjustmentHelper.billingRate = null;
                            this.setState(
                                {
                                    adjustmentHelper,
                                },
                            );
                            this.buildAdjustmentDescription(
                                adjustmentModalIndex,
                            );
                        }}
                    />
                )}

                {adjustmentHelper.useChargeTypeGroups && (

                    <ValidatedSelect
                        styles={CompactSelectStyles}
                        label="Charge Type Group"
                        minMenuHeight={
                            100
                        }
                        maxMenuHeight={
                            200
                        }
                        id="chargeTypeDropdown"
                        name="chargeTypeDropdown"
                        hideClear
                        isDisabled={
                            !adjustmentHelper.date
                        }
                        options={
                            chargeTypeGroups
                        }
                        getOptionLabel={(
                            option,
                        ) => option.invoiceDescription}
                        getOptionValue={(
                            option,
                        ) => option.id}
                        onChange={(
                            selection,
                        ) => {
                            adjustmentHelper.chargeTypeGroup = selection;
                            adjustmentHelper.resource = '';
                            adjustmentHelper.employeeId = null;
                            adjustmentHelper.equipmentId = null;
                            adjustmentHelper.billingRate = null;
                            this.setState(
                                {
                                    adjustmentHelper,
                                },
                            );
                            this.buildAdjustmentDescription(
                                adjustmentModalIndex,
                            );
                        }}
                    />
                )}

                {adjustmentHelper.useChargeTypeGroups && (
                    <FormGroup>
                        <b>Charges to Group</b>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            <table className="table table-sm table-striped">
                                <tbody>
                                    {invoice.invoiceChargeDetails
                                        .filter((cd) =>
                                            cd.billingUnitName != ChargeTypeUnitNames.Percentage
                                            && cd.chargeTypeGroupId == null
                                            && !cd.removed
                                            && cd.parentInvoiceChargeId == null
                                            && (
                                                (cd.current && cd.invoiceChargeTypeGroupUID == null)
                                                || cd.invoiceChargeTypeGroupUID === adjustmentHelper.invoiceChargeTypeGroupUID
                                            ))
                                        .map((cd) => (
                                            <tr key={cd.id}>
                                                <td width="30">
                                                    <FormCheckbox
                                                        id={`groupCharge_${cd.id}`}
                                                        checked={cd.invoiceChargeTypeGroupUID === adjustmentHelper.invoiceChargeTypeGroupUID}
                                                        onChange={(e) => {
                                                            this.setChargeGroupMembership(
                                                                cd.id,
                                                                e.target.checked ? adjustmentHelper.invoiceChargeTypeGroupUID : null,
                                                            );
                                                        }}
                                                    />
                                                </td>
                                                <td>{cd.userDescription ?? cd.description}</td>
                                                <td>
                                                    <PriceDisplay amount={cd.userChargeAmount ?? cd.chargeAmount ?? 0} />
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </FormGroup>
                )}

                {!adjustmentHelper.useChargeTypeGroups && (
                    <>
                        <ValidatedSelect
                            label="Charge"
                            styles={
                                CompactSelectStyles
                            }
                            minMenuHeight={
                                100
                            }
                            maxMenuHeight={
                                200
                            }
                            id="chargeTypeDropdown"
                            name="chargeTypeDropdown"
                            hideClear
                            isDisabled={
                                !adjustmentHelper.timesheet
                            }
                            options={
                                chargeTypes
                            }
                            getOptionLabel={(
                                option,
                            ) => option.description}
                            getOptionValue={(
                                option,
                            ) => option.id}
                            onChange={(
                                selection,
                            ) => {
                                adjustmentHelper.chargeType = selection;
                                adjustmentHelper.resource = '';
                                adjustmentHelper.employeeId = null;
                                adjustmentHelper.equipmentId = null;
                                adjustmentHelper.billingRate = null;
                                this.setState(
                                    {
                                        adjustmentHelper,
                                    },
                                );
                                this.buildAdjustmentDescription(
                                    adjustmentModalIndex,
                                );
                            }}
                        />

                        {adjustmentHelper.chargeType?.resourceTypeId === ChargeTypeResourceTypes.Employee && !!filtered_staff.length && (

                            <ValidatedSelect
                                label="Resource"
                                styles={
                                    CompactSelectStyles
                                }
                                minMenuHeight={
                                    100
                                }
                                maxMenuHeight={
                                    200
                                }
                                id="staffDropdown"
                                name="staffDropdown"
                                hideClear
                                options={
                                    filtered_staff
                                }
                                getOptionLabel={(
                                    option,
                                ) => option.name}
                                getOptionValue={(
                                    option,
                                ) => option.id}
                                onChange={(
                                    selection,
                                ) => {
                                    adjustmentHelper.resource = selection.name;
                                    adjustmentHelper.employeeId = selection.id;
                                    adjustmentHelper.equipmentId = null;
                                    this.setState(
                                        {
                                            adjustmentHelper,
                                        },
                                    );
                                    this.buildAdjustmentDescription(
                                        adjustmentModalIndex,
                                    );
                                }}
                            />
                        )}

                        {(adjustmentHelper.chargeType?.resourceTypeId === ChargeTypeResourceTypes.Equipment
                            || adjustmentHelper.chargeType?.resourceTypeId === ChargeTypeResourceTypes.Rental)
                            && !!filtered_equipment.length && (

                                <ValidatedSelect
                                    label="Resource"
                                    styles={
                                        CompactSelectStyles
                                    }
                                    minMenuHeight={
                                        100
                                    }
                                    maxMenuHeight={
                                        200
                                    }
                                    id="equipmentDropdown"
                                    name="equipmentDropdown"
                                    hideClear
                                    options={
                                        filtered_equipment
                                    }
                                    getOptionLabel={(
                                        option,
                                    ) => option.description}
                                    getOptionValue={(
                                        option,
                                    ) => option.id}
                                    onChange={(
                                        selection,
                                    ) => {
                                        adjustmentHelper.resource = selection.description;
                                        adjustmentHelper.equipmentId = selection.id;
                                        adjustmentHelper.employeeId = null;
                                        this.setState(
                                            {
                                                adjustmentHelper,
                                            },
                                        );
                                        this.buildAdjustmentDescription(
                                            adjustmentModalIndex,
                                        );
                                    }}
                                />
                            )}

                        <ValidatedSelect
                            label="Rate"
                            styles={
                                CompactSelectStyles
                            }
                            minMenuHeight={
                                100
                            }
                            maxMenuHeight={
                                200
                            }
                            id="rateDropdown"
                            name="rateDropdown"
                            hideClear
                            isDisabled={
                                rateDisabled
                            }
                            options={
                                billingRates
                            }
                            getOptionLabel={(
                                option,
                            ) => option.description}
                            getOptionValue={(
                                option,
                            ) => option.id}
                            onChange={(
                                selection,
                            ) => {
                                adjustmentHelper.billingRate = selection;
                                this.setState(
                                    {
                                        adjustmentHelper,
                                    },
                                );
                                this.buildAdjustmentDescription(
                                    adjustmentModalIndex,
                                );
                            }}
                        />
                    </>
                )}
            </ModalForm>
        );
    };

    renderBillingChargesTable = () => {
        const billingCharges = this.getBillingCharges();
        if (billingCharges && billingCharges.length > 0) {
            return (
                <div className="mt-3">
                    <p className="mb-2">Selected charges will be included when the invoice for this period is regenerated.</p>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <table className="table table-sm table-striped">
                            <thead>
                                <tr>
                                    <th width="50">
                                        <FormCheckbox
                                            id="selectAllBillingCharges"
                                            checked={this.state.selectAllBillingCharges}
                                            onChange={this.toggleSelectAllBillingCharges}
                                        />
                                    </th>
                                    <th>Billing Charge</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Qty / Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {billingCharges.map((charge) => (
                                    <tr key={charge.id}>
                                        <td>
                                            <FormCheckbox
                                                id={`billingCharge-${charge.id}`}
                                                checked={this.state.selectedBillingCharges.includes(charge.id)}
                                                onChange={() => this.toggleBillingChargeSelection(charge.id)}
                                            />
                                        </td>
                                        <td>{charge.chargeTypeName || 'Billing Charge'}</td>
                                        <td>{charge.startDateDisplay}</td>
                                        <td>{charge.endDateDisplay}</td>
                                        <td>
                                            {charge.chargeQty || charge.billQty || 0}
                                            {' '}
                                            @
                                            <PriceDisplay amount={charge.userBillingRate || charge.billingRate || charge.userChargeAmount || charge.chargeAmount || 0} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }
        return null;
    };

    render() {
        let {
            invoice,
            customers,
            invoiceCustomer,
            contractsFiltered,
            formValidated,
            validationMessage,
            hourlyChargesOpen,
            loading,
            saving,
            cancelling,
            approving,
            printing,
            showAddTimesheetModal,
            showDeleteInvoiceModal,
            showRestartInvoiceModal,
            selectedContractTimesheet,
            isAddingContractTimesheet,
            showAddChargeTypeGroupModal,
            contractTimesheets,
            isLoadingContractTimesheets,
            isNew,
            reverting,
            deleting,
            showConfirmDraftModal,
            adjustments,
            showReorderWidget,
            showTimesheetsReorderWidget,
            selectedSortableTimesheets,
            dispatchLocations,
            associateInventoryItem,
            inventoryItems,
            salesAccounts,
            serviceItems,
            chargeTypeGroups,
            contract,
            overrideChargeTypeRules,
            usCounties,
            companyContactsOptions,
            projectManagerContactOptions,
            selectedCTGroup,
            selectedChargeTypeGroup,
            invoiceContract,
            vbOffices,
        } = this.state;

        const shouldAutoCheckOverride =
            selectedCTGroup?.hasTotalCharges &&
            selectedCTGroup?.numTotalCharges;

        const effectiveOverride = shouldAutoCheckOverride ? true : overrideChargeTypeRules;

        const editedGroupDetails = this.getEditedGroupDetails();

        const contractRequireGLString = contract?.requireGLString === true;

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        let chargeDetailTotal = 0;

        const crewLead = crewLeadLabel(tenantSettings);

        const ctGroupCharges = flatten(
            (invoice.invoiceTimesheets ?? []).map((x) => x.invoiceCharges),
        ).filter((x) => x.invoiceChargeTypeGroupUID != null && x.chargeTypeGroupId == null);

        const nonRentalCharges = flatten(
            (invoice.invoiceTimesheets ?? [])
                .filter((x) => !x.isRentalTimesheet)
                .map((x) => x.invoiceCharges));

        let charges = flatten(
            (invoice.invoiceTimesheets ?? [])
                .filter((x) => !x.isRentalTimesheet)
                .map((x) => x.invoiceCharges),
        ).filter((x) => x.chargeTypeUnitsId == ChargeTypeUnits.Hours && x.invoiceChargeTypeGroupUID == null);

        if (charges.length > 0) {
            charges = orderBy(
                charges,
                ['date', 'resourceTypeId', 'resourceName', 'billingRateTypeId'],
                ['asc', 'asc', 'asc', 'desc'],
            );
        }

        const flatCharges = nonRentalCharges.filter(
            (x) => x.chargeTypeUnitsId == ChargeTypeUnits.Flat
                && x.invoiceChargeTypeGroupUID == null
                && x.resourceTypeId !== ChargeTypeResourceTypes.Sale,
        );

        const salesCharges = nonRentalCharges.filter(
            (x) => x.chargeTypeUnitsId == ChargeTypeUnits.Flat
                && x.resourceTypeId === ChargeTypeResourceTypes.Sale,
        );

        let invoiceChargeTypeGroups = flatten(
            (invoice.invoiceTimesheets ?? []).map((x) => x.invoiceCharges),
        ).filter((x) => x.chargeTypeGroupId > 0);

        invoiceChargeTypeGroups = invoiceChargeTypeGroups.sort((a, b) => {
            const dateCompare = new Date(a.date) - new Date(b.date);
            if (dateCompare !== 0) return dateCompare;
            if (a.billingRateTypeId !== b.billingRateTypeId) {
                return b.billingRateTypeId - a.billingRateTypeId;
            }
            if (a.billingRate !== b.billingRate) {
                return b.billingRate - a.billingRate;
            }
        });
        const dispatchObj = (dispatchLocations ?? []).find((x) => x.value === invoice.dispatchOfficeLocationId) ?? null;
        const dispatchOfficeLocationName = dispatchObj != null ? dispatchObj.label : '';
        const county = getCounty(usCounties, {
            usCountyId: invoice.usCountyId,
            contractUnionWorkTypeId: invoice.contractUnionWorkTypeId,
        });

        const isRentalInvoice = (invoice.invoiceTimesheets ?? []).some((x) => x.isRentalTimesheet);
        const allowCustomizedEmailSubject = !!invoiceCustomer?.customizeEmailSubject;

        return (
            tenantSettings.billingEnabled
            && tenantSettings.invoicingEnabled && (
                <>
                    <SlideForm
                        size="col-xl-12 col-md-12 col-xs-12"
                        loading={loading || !tenantSettings}
                        show={this.props.show}
                        fullScreen
                        id="timesheetForm"
                        formIcon={faUserClock}
                        formTitle="Invoice"
                        ref={this.formRef}
                        setIsValidated={(value) => {
                            this.setState({ formValidated: value });
                        }}
                        isValidated={formValidated}
                        className="pb-2 w-100"
                        onSubmit={this.onSubmit}
                        onClose={this.onClose}
                        errors={this.state.errors}
                        onClearErrors={this.onClearErrors}
                        error={validationMessage}
                        readOnly={!!saving}
                        hideToolbar
                    >
                        <FormBlocker
                            showProgress={false}
                            show={this.state.customFormOpened}
                        />
                        <UncontrolledCollapseUnderlineHeader
                            headerText="Invoice Details"
                        >
                            <div className="invoice-details">
                                <FlexEndRow>
                                    {Boolean(invoice?.id) && (
                                        <Can
                                            I="history"
                                            a="invoice"
                                        >
                                            <button
                                                type="button"
                                                className="btn shadow-none"
                                                onClick={this.onViewChangeHistory}
                                            >
                                                <span className="cursor-pointer">
                                                    <FontAwesomeIcon
                                                        icon={faTasks}
                                                        className="mr-2"
                                                    />
                                                    <small>View Change History</small>
                                                </span>
                                            </button>
                                        </Can>
                                    )}
                                    {(invoice?.invoiceStatusId
                                        == InvoiceStatus.New) && (
                                            <button
                                                type="button"
                                                className="btn shadow-none"
                                                onClick={this.onAddTimesheetClicked}
                                                disabled={isEmpty(invoice.workOrderNumber)}
                                            >
                                                <span className="cursor-pointer">
                                                    <i className="fa fa-plus mr-2" />
                                                    <small>Add Timesheet</small>
                                                </span>
                                            </button>
                                        )}
                                </FlexEndRow>

                                <Flex
                                    alignItems="center"
                                    justifyContent="between"
                                >
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="invoiceDate"
                                            label="Invoice Date"
                                        />

                                        {invoice?.invoiceStatusId <= InvoiceStatus.Draft && (
                                            <Input
                                                id="invoiceDate"
                                                name="invoiceDate"
                                                className="ml-3"
                                                value={
                                                    moment(
                                                        invoice?.invoiceDate,
                                                    ).format(
                                                        MomentDateFormat.DateOnlyUTCHyphenated,
                                                    ) ?? ''
                                                }
                                                onChange={(ev) => {
                                                    const val = ev?.target?.value;
                                                    this.setState(
                                                        (state) => (
                                                            (state.invoice.invoiceDate = moment(
                                                                val,
                                                            ).format(
                                                                MomentDateFormat.DateOnlyUTCHyphenated,
                                                            )),
                                                            state
                                                        ),
                                                    );
                                                }}
                                                required
                                                type="date"
                                                error="Invoice Date is required."
                                            />
                                        )}

                                        {invoice?.invoiceStatusId > InvoiceStatus.Draft && (
                                            <span
                                                id="invoiceDate"
                                                name="invoiceDate"
                                            >
                                                {moment(
                                                    invoice?.invoiceDate,
                                                ).format(
                                                    MomentDateFormat.MonthDayYearSlashesLeading,
                                                ) ?? ''}
                                            </span>
                                        )}

                                        <FormLabel
                                            htmlFor="accountingDate"
                                            label="Accounting Date"
                                            className="ml-5"
                                        />

                                        {invoice?.invoiceStatusId <= InvoiceStatus.Draft && (
                                            <Input
                                                id="accountingDate"
                                                name="accountingDate"
                                                formGroupClassName="ml-3"
                                                value={invoice?.accountingDate ?
                                                    moment(
                                                        invoice?.accountingDate,
                                                    ).format(
                                                        MomentDateFormat.DateOnlyUTCHyphenated,
                                                    ) : ''
                                                }
                                                onChange={(ev) => {
                                                    const val = ev?.target?.value;
                                                    this.setState(
                                                        (state) => (
                                                            (state.invoice.accountingDate = val
                                                                ? moment(
                                                                    val,
                                                                ).format(
                                                                    MomentDateFormat.DateOnlyUTCHyphenated,
                                                                )
                                                                : ''),
                                                            state
                                                        ),
                                                    );
                                                }}
                                                type="date"
                                            />
                                        )}

                                        {invoice?.invoiceStatusId > InvoiceStatus.Draft && (
                                            <span
                                                id="accountingDate"
                                                name="accountingDate"
                                            >
                                                {moment(
                                                    invoice?.accountingDate
                                                    || invoice?.invoiceDate,
                                                ).format(
                                                    MomentDateFormat.MonthDayYearSlashesLeading,
                                                ) ?? ''}
                                            </span>
                                        )}

                                        {Boolean(
                                            invoice?.invoiceStatusId
                                            <= InvoiceStatus.Draft
                                            && !invoice.accountingDate
                                            && invoice.invoiceDate,
                                        ) &&
                                            (
                                                <div className="ml-3">
                                                    The current date will be used for accounting.
                                                </div>
                                            )}
                                    </Flex>

                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="lastModified"
                                            label="Last Modified:"
                                        />

                                        {invoice?.lastModifiedBy ?? '-'}
                                        {' @ '}
                                        {invoice?.lastModifiedOn ?? '-'}
                                    </Flex>
                                </Flex>

                                {invoice?.invoiceNumber != null && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="invoiceNumber"
                                            label="Invoice #"
                                        />

                                        <span
                                            id="customer"
                                            name="customer"

                                        >
                                            {invoice?.invoiceNumber ?? ''}
                                            {invoice?.invoiceVersion > 1 ? `-${invoice?.invoiceVersion}` : ''}
                                        </span>
                                    </Flex>
                                )}

                                {Boolean(isRentalInvoice) && Boolean(invoice.rentalProjectNumber) && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="rentalProjectNumber"
                                            label="Project #"
                                        />

                                        <span
                                            id="rentalProjectNumber"
                                            name="rentalProjectNumber"

                                        >
                                            {invoice.rentalProjectNumber}

                                            <Link
                                                to={`${AppNavPaths.Project}/${invoice.rentalProjectId}`}
                                                target="_blank"
                                                className="ml-1"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faArrowUpRightFromSquare}
                                                    size="sm"
                                                />
                                            </Link>
                                        </span>
                                    </Flex>
                                )}

                                {!invoice?.allowMultipleWONumbers && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="workOrderNumber"
                                            label="Work Order #"
                                        />
                                        <Input
                                            id="workOrderNumber"
                                            name="invoice.workOrderNumber"
                                            className="ml-3"
                                            value={
                                                (invoice ?? {})
                                                    .workOrderNumber ?? ''
                                            }
                                            onChange={this.onChange}
                                            maxLength={44}
                                            displayMaxLength={false}
                                        />
                                    </Flex>
                                )}

                                {invoice.useWorkRequest && (
                                    <>
                                        <Flex alignItems="center">
                                            <FormLabel
                                                htmlFor="purchaseOrderNumber"
                                                label="PO #"
                                            />
                                            <Input
                                                id="purchaseOrderNumber"
                                                name="invoice.purchaseOrderNumber"
                                                className="ml-3"
                                                value={
                                                    (invoice ?? {})
                                                        .purchaseOrderNumber
                                                    ?? ''
                                                }
                                                onChange={this.onChange}
                                            />

                                            <UncontrolledTooltip target="purchaseOrderNumber">
                                                This field may also correspond to Job# for some customers.
                                            </UncontrolledTooltip>
                                        </Flex>
                                        <Flex alignItems="center">
                                            <FormLabel
                                                htmlFor="workRequest"
                                                label="Work Request"
                                            />
                                            <Input
                                                id="workRequest"
                                                name="invoice.workRequest"
                                                className="ml-3"
                                                value={
                                                    (invoice ?? {})
                                                        .workRequest ?? ''
                                                }
                                                onChange={this.onChange}
                                            />
                                            <UncontrolledTooltip target="workRequest">
                                                This field may also correspond to PM# for some customers.
                                            </UncontrolledTooltip>
                                        </Flex>
                                    </>
                                )}

                                {invoice.useTCNumber && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="tcNumber"
                                            label="TC #"
                                        />
                                        {invoice?.invoiceStatusId
                                            == InvoiceStatus.New && (
                                                <Input
                                                    id="tcNumber"
                                                    name="invoice.tcNumber"
                                                    className="ml-3"
                                                    value={
                                                        (invoice ?? {}).tcNumber
                                                        ?? ''
                                                    }
                                                    onChange={this.onChange}
                                                />
                                            )}

                                        {invoice?.invoiceStatusId
                                            > InvoiceStatus.New && (
                                                <span
                                                    id="tcNumber"
                                                    name="invoice.tcNumber"

                                                >
                                                    {invoice?.tcNumber ?? ''}
                                                </span>
                                            )}
                                    </Flex>
                                )}

                                {tenantSettings.glStringsEnabled
                                    && contractRequireGLString && (
                                        <Flex alignItems="center">
                                            <FormLabel
                                                htmlFor="glString"
                                                label="GL String"
                                            />
                                            <Input
                                                id="glString"
                                                name="invoice.glString"
                                                className="ml-3"
                                                value={
                                                    (invoice ?? {}).glString
                                                    ?? ''
                                                }
                                                onChange={this.onChange}
                                            />
                                        </Flex>
                                    )}
                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="memo"
                                        label="Memo"
                                    />
                                    {invoice?.invoiceStatusId
                                        <= InvoiceStatus.Draft && (
                                            <Input
                                                id="memo"
                                                name="invoice.memo"
                                                className="ml-3"
                                                value={(invoice ?? {}).memo ?? ''}
                                                onChange={this.onChange}
                                            />
                                        )}

                                    {invoice?.invoiceStatusId
                                        > InvoiceStatus.Draft && (
                                            <span
                                                id="memo"
                                                name="memo"

                                            >
                                                {invoice?.memo ?? ''}
                                            </span>
                                        )}
                                </Flex>

                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="jobLocation"
                                        label="Job Location"
                                    />
                                    <Input
                                        id="jobLocation"
                                        name="invoice.jobLocation"
                                        className="ml-3"
                                        value={
                                            (invoice ?? {}).jobLocation ?? ''
                                        }
                                        onChange={this.onChange}
                                        readOnly={invoiceCustomer?.isIntercompany}
                                        endAddon={(
                                            <>
                                                {!(invoice?.jobLocationAddress) && invoiceCustomer?.isIntercompany && (
                                                    <>
                                                        <FontAwesomeIcon
                                                            id="invalid-job-location"
                                                            icon={faExclamationTriangle}
                                                            className="text-warning mb-2"
                                                            style={{ marginTop: '-4px' }}
                                                        />
                                                        <UncontrolledTooltip target="invalid-job-location">
                                                            There is a formatting issue with this address that will cause it to not sync with Netsuite correctly. Please re-enter the address using the Edit Address tool.
                                                        </UncontrolledTooltip>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    />
                                    {invoiceCustomer?.isIntercompany && (
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="site-link ml-4"
                                            onClick={() => this.onEditLocation()}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                </Flex>
                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="zipCode"
                                        label="Job Zip Code"
                                    />
                                    {invoice?.invoiceStatusId
                                        <= InvoiceStatus.Draft && (
                                            <Input
                                                id="zipCode"
                                                name="invoice.zipCode"
                                                className="ml-3"
                                                disabled={invoice.invoiceStatus === 'Exported'}
                                                value={
                                                    (invoice ?? {}).zipCode ?? ''
                                                }
                                                onChange={this.onChange}
                                            />
                                        )}

                                    {invoice?.invoiceStatusId
                                        > InvoiceStatus.Draft && (
                                            <span
                                                id="zipCode"
                                                name="zipCode"

                                            >
                                                {invoice?.zipCode ?? ''}
                                            </span>
                                        )}
                                </Flex>

                                {Boolean(county) && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="usCountyId"
                                            label={(
                                                <CountyLabel
                                                    {...county}
                                                    label="County"
                                                />
                                            )}
                                            className="control-label"
                                        />
                                        <ValidatedSelect
                                            id="usCountyId"
                                            name="invoice.usCountyId"
                                            className="smallReactSelect form-control-sm ml-3"
                                            options={usCounties.filter(
                                                (c) => c.usStateId === county.usStateId,
                                            )}
                                            getOptionLabel={(option) => option.name}
                                            getOptionValue={(option) => option.id}
                                            value={county}
                                            hideClear
                                            onChange={(selection) => {
                                                const { invoice } = this.state;

                                                if (selection) {
                                                    invoice.usCountyId = selection.id;
                                                } else {
                                                    invoice.usCountyId = null;
                                                }

                                                this.setState({
                                                    invoice,
                                                });
                                            }}
                                        />
                                    </Flex>
                                )}

                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="invoiceJob"
                                        label="Operations Center"
                                    />

                                    {invoice?.invoiceStatusId
                                        <= InvoiceStatus.Draft && (
                                            <Input
                                                formGroupClassName="w-auto"
                                                id="invoiceJob"
                                                name="invoice.invoiceJob"
                                                className="ml-3"
                                                value={
                                                    (invoice ?? {}).invoiceJob ?? ''
                                                }
                                                onChange={this.onChange}
                                            />
                                        )}

                                    {invoice?.invoiceStatusId
                                        > InvoiceStatus.Draft && (
                                            <span
                                                id="invoiceJob"
                                                name="invoiceJob"

                                            >
                                                {invoice?.invoiceJob ?? ''}
                                            </span>
                                        )}
                                </Flex>

                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="dispatchOfficeLocationId"
                                        label="Class"
                                    />

                                    {invoice?.invoiceStatusId
                                        <= InvoiceStatus.Draft && (
                                            <div className="ml-3 w-auto">
                                                <ValidatedSelect
                                                    hideClear
                                                    id="dispatchOfficeLocationId"
                                                    name="invoice.dispatchOfficeLocationId"
                                                    styles={
                                                        SuperCompactSelectStyles
                                                    }
                                                    options={dispatchLocations}
                                                    value={
                                                        (
                                                            dispatchLocations ?? []
                                                        ).find(
                                                            (x) => x.value
                                                                === invoice.dispatchOfficeLocationId,
                                                        ) ?? ''
                                                    }
                                                    onChange={this.onSelectChange}
                                                    isDisabled={false}
                                                />
                                            </div>
                                        )}

                                    {invoice?.invoiceStatusId
                                        > InvoiceStatus.Draft && (
                                            <span
                                                id="dispatchOfficeLocationId"
                                                name="invoice.dispatchOfficeLocationId"

                                            >
                                                {invoice?.dispatchOfficeLocationName
                                                    ?? ''}
                                            </span>
                                        )}
                                </Flex>

                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="customer"
                                        label="Customer"
                                    />

                                    {invoice?.invoiceStatusId
                                        > InvoiceStatus.Draft
                                        && !invoice.overrideCustomerCompanyId
                                        > 0 && (
                                            <div
                                                className="ml-3 w-auto"
                                                style={{
                                                    paddingTop: '6px',
                                                }}
                                            >
                                                {invoice?.customerCompanyName
                                                    ?? ''}
                                            </div>
                                        )}

                                    {invoice?.invoiceStatusId
                                        > InvoiceStatus.Draft
                                        && invoice.overrideCustomerCompanyId
                                        > 0 && (
                                            <>
                                                <div
                                                    className="ml-3 w-auto"
                                                    style={{
                                                        paddingTop: '6px',
                                                        color: 'red',
                                                    }}
                                                >
                                                    <strike>
                                                        {invoice?.customerCompanyName
                                                            ?? ''}
                                                    </strike>
                                                </div>
                                                {' '}
                                                <div
                                                    className="ml-3 w-auto"
                                                    style={{
                                                        paddingTop: '6px',
                                                    }}
                                                >
                                                    {invoice?.overrideCustomerCompanyName
                                                        ?? ''}
                                                </div>
                                            </>
                                        )}

                                    {invoice?.invoiceStatusId
                                        <= InvoiceStatus.Draft && (
                                            <>
                                                {invoice.overrideCustomerCompanyId
                                                    > 0 && (
                                                        <div
                                                            className="ml-3 w-auto"
                                                            style={{
                                                                paddingTop: '6px',
                                                                color: 'red',
                                                            }}
                                                        >
                                                            <strike>
                                                                {invoice?.customerCompanyName
                                                                    ?? ''}
                                                            </strike>
                                                        </div>
                                                    )}

                                                <div
                                                    className="ml-3 w-auto"
                                                    style={{ paddingTop: '3px' }}
                                                >
                                                    <ValidatedSelect
                                                        hideClear
                                                        id="overrideCustomerCompanyId"
                                                        name="invoice.overrideCustomerCompanyId"
                                                        styles={
                                                            SuperCompactSelectStyles
                                                        }
                                                        options={customers}
                                                        value={
                                                            (customers ?? []).find(
                                                                (x) => (invoice.overrideCustomerCompanyId
                                                                    > 0
                                                                    ? x.value
                                                                    === invoice.overrideCustomerCompanyId
                                                                    : x.value
                                                                    === invoice.customerCompanyId),
                                                            ) ?? ''
                                                        }
                                                        onChange={
                                                            this
                                                                .onOverrideCustomerChanged
                                                        }
                                                        isDisabled={false}
                                                    />
                                                </div>

                                                {invoice.overrideCustomerCompanyId
                                                    > 0 && (
                                                        <>
                                                            &nbsp;&nbsp;
                                                            <button
                                                                type="button"
                                                                className="btn shadow-none"
                                                                title="Remove Override"
                                                                onClick={
                                                                    this
                                                                        .onOverrideDelete
                                                                }
                                                            >
                                                                <div className="mt-1">
                                                                    <i className="fa fa-trash" />
                                                                </div>
                                                            </button>
                                                        </>
                                                    )}
                                            </>
                                        )}
                                </Flex>

                                {Boolean(invoiceCustomer?.showPointOfContactOnInvoice) && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="pointOfContact"
                                            label="Point of Contact"
                                        />

                                        <Input
                                            formGroupClassName="w-auto"
                                            name="pointOfContact"
                                            value={invoice?.pointOfContact ?? ''}
                                            className="ml-3"
                                            onChange={(evt) => {
                                                const { value } = evt.target;
                                                const { invoice } = {
                                                    ...this.state,
                                                };
                                                invoice.pointOfContact = value;
                                                this.setState({
                                                    invoice,
                                                });
                                            }}
                                        />
                                    </Flex>
                                )}

                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="contract"
                                        label="Contract"
                                    />

                                    {invoice?.invoiceHasPO
                                        && invoice?.invoiceStatusId
                                        <= InvoiceStatus.Draft
                                        && !invoice?.useWorkRequest && (
                                            <Input
                                                id="purchaseOrderNumber"
                                                name="invoice.purchaseOrderNumber"
                                                className="ml-3"
                                                value={
                                                    (invoice ?? {})
                                                        .purchaseOrderNumber
                                                    ?? ''
                                                }
                                                onChange={this.onChange}
                                            />
                                        )}

                                    {invoice?.invoiceHasPO
                                        && invoice?.invoiceStatusId
                                        > InvoiceStatus.Draft
                                        && !invoice?.useWorkRequest && (
                                            <span
                                                id="purchaseOrderNumber"
                                                name="purchaseOrderNumber"

                                            >
                                                {invoice?.purchaseOrderNumber
                                                    ?? ''}
                                            </span>
                                        )}

                                    {invoice?.invoiceHasPO
                                        && invoice?.useWorkRequest && (
                                            <span
                                                id="contract"
                                                name="contract"

                                            >
                                                {invoice?.contractNumber ?? ''}
                                            </span>
                                        )}

                                    {!invoice?.invoiceHasPO && (
                                        <>
                                            {!invoice.overrideCustomerCompanyId
                                                > 0 && (
                                                    <span
                                                        id="contract"
                                                        name="contract"

                                                    >
                                                        {invoice?.contractNumber
                                                            ?? ''}
                                                    </span>
                                                )}

                                            {invoice.overrideCustomerCompanyId
                                                > 0
                                                && invoice?.invoiceStatusId
                                                > InvoiceStatus.Draft && (
                                                    <>
                                                        <div
                                                            className="ml-3 w-auto"
                                                            style={{
                                                                paddingTop:
                                                                    '6px',
                                                                color: 'red',
                                                            }}
                                                        >
                                                            <strike>
                                                                {invoice?.contractNumber
                                                                    ?? ''}
                                                            </strike>
                                                        </div>
                                                        {' '}
                                                        <div
                                                            className="ml-3 w-auto"
                                                            style={{
                                                                paddingTop:
                                                                    '6px',
                                                            }}
                                                        >
                                                            {invoice?.overrideContractNumber
                                                                ?? ''}
                                                        </div>
                                                    </>
                                                )}

                                            {invoice.overrideCustomerCompanyId
                                                > 0
                                                && invoice?.invoiceStatusId
                                                <= InvoiceStatus.Draft && (
                                                    <>
                                                        <div
                                                            className="ml-3 w-auto"
                                                            style={{
                                                                paddingTop:
                                                                    '6px',
                                                                color: 'red',
                                                            }}
                                                        >
                                                            <strike>
                                                                {invoice?.contractNumber
                                                                    ?? ''}
                                                            </strike>
                                                        </div>

                                                        <div className="ml-3 w-auto">
                                                            <ValidatedSelect
                                                                hideClear
                                                                id="overrideContractId"
                                                                name="invoice.overrideContractId"
                                                                styles={
                                                                    SuperCompactSelectStyles
                                                                }
                                                                placeholder="Contract..."
                                                                options={
                                                                    contractsFiltered
                                                                }
                                                                value={
                                                                    (
                                                                        contractsFiltered
                                                                        ?? []
                                                                    ).find(
                                                                        () => invoice.overrideContractId, // TODO - What is this?
                                                                    ) ?? ''
                                                                }
                                                                onChange={
                                                                    this
                                                                        .onOverrideContractChanged
                                                                }
                                                                isDisabled={
                                                                    false
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                        </>
                                    )}
                                </Flex>
                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="terms"
                                        label="Terms"
                                    />
                                    <span
                                        id="terms"
                                        name="terms"

                                    >
                                        {invoice?.terms ?? ''}
                                    </span>
                                </Flex>

                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="billingCompany"
                                        label="Billing Co."
                                    />
                                    <span
                                        id="billingCompany"
                                        name="billingCompany"

                                    >
                                        {invoice?.billingCompanyName ?? ''}
                                    </span>
                                </Flex>
                                <Flex alignItems="center">
                                    <FormLabel
                                        htmlFor="jobType"
                                        label="Job Type"
                                    />
                                    <span
                                        id="jobType"
                                        name="jobType"

                                    >
                                        {invoice?.jobType ?? ''}
                                    </span>
                                </Flex>
                                {(invoiceCustomer?.isIntercompany) && (
                                    <>
                                        <Flex alignItems="center">
                                            <FormLabel
                                                htmlFor="vbOfficeId"
                                                label="Vendor Bill Office"
                                            />
                                            <ValidatedSelect
                                                hideClear
                                                id="vbOfficeId"
                                                name="invoice.vbOfficeId"
                                                styles={
                                                    SuperCompactSelectStyles
                                                }
                                                options={vbOffices}
                                                value={
                                                    (vbOffices ?? []).find(
                                                        (x) => (x.value
                                                            === invoice.vbOfficeId),
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this
                                                        .onVendorBillOfficeChanged
                                                }
                                                isDisabled={invoice?.invoiceStatusId > InvoiceStatus.Draft}
                                            />
                                        </Flex>
                                        <Flex alignItems="center">
                                            <FormLabel
                                                htmlFor="endInvoiceOfficeId"
                                                label="End Invoice Office"
                                            />
                                            <ValidatedSelect
                                                hideClear
                                                id="endInvoiceOfficeId"
                                                name="invoice.endInvoiceOfficeId"
                                                styles={
                                                    SuperCompactSelectStyles
                                                }
                                                options={vbOffices}
                                                value={
                                                    (vbOffices ?? []).find(
                                                        (x) => (x.value
                                                            === invoice.endInvoiceOfficeId),
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this
                                                        .onEndInvoiceOfficeChanged
                                                }
                                                isDisabled={invoice?.invoiceStatusId > InvoiceStatus.Draft}
                                            />
                                        </Flex>
                                    </>
                                )}
                                {(invoiceCustomer?.invoiceHasInspectorName) && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="inspectorName"
                                            label="Inspector Name"
                                        />
                                        <Input
                                            name="inspectorName"
                                            value={invoice?.inspectorName ?? ''}
                                            className="ml-3"
                                            onChange={(
                                                evt,
                                            ) => {
                                                const { value } = evt
                                                    .target;
                                                const {
                                                    invoice,
                                                } = {
                                                    ...this
                                                        .state,
                                                };
                                                invoice.inspectorName = value;
                                                this.setState(
                                                    {
                                                        invoice,
                                                    },
                                                );
                                            }}
                                        />
                                    </Flex>
                                )}
                                {(invoiceContract?.requireProjectInformation) && (
                                    <>
                                        <Flex alignItems="center">
                                            <FormLabel
                                                htmlFor="projectName"
                                                label="Project Name"
                                            />
                                            <Input
                                                name="projectName"
                                                value={invoice?.projectName ?? ''}
                                                className="ml-3"
                                                onChange={(
                                                    evt,
                                                ) => {
                                                    const { value } = evt
                                                        .target;
                                                    const {
                                                        invoice,
                                                    } = {
                                                        ...this
                                                            .state,
                                                    };
                                                    invoice.projectName = value;
                                                    this.setState(
                                                        {
                                                            invoice,
                                                        },
                                                    );
                                                }}
                                            />
                                        </Flex>
                                        <Flex alignItems="center">
                                            <FormLabel
                                                htmlFor="projectManagerId"
                                                label="Project Manager"
                                            />
                                            <ValidatedSelect
                                                id="projectManagerId"
                                                name="invoice.projectManagerId"
                                                hideClear
                                                styles={
                                                    SuperCompactSelectStyles
                                                }
                                                formGroupClassName="ml-3"
                                                options={projectManagerContactOptions}
                                                value={(projectManagerContactOptions || []).find(
                                                    (x) => x.value === invoice?.projectManagerId,
                                                ) || ''}
                                                onChange={this.onSelectChange}
                                            />
                                        </Flex>
                                    </>
                                )}
                                {!isRentalInvoice && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="foremanId"
                                            label="Foreman"
                                        />
                                        <ValidatedSelect
                                            id="foremanId"
                                            name="invoice.foremanId"
                                            hideClear
                                            styles={
                                                SuperCompactSelectStyles
                                            }
                                            formGroupClassName="ml-3"
                                            options={companyContactsOptions}
                                            value={(companyContactsOptions || []).find(
                                                (x) => x.value === invoice?.foremanId,
                                            ) || ''}
                                            onChange={this.onSelectChange}
                                        />
                                    </Flex>
                                )}
                                {/* Invoicing Email Subject (optional) */}
                                {allowCustomizedEmailSubject && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="emailSubject"
                                            label="Email Subject"
                                        />
                                        {invoice?.invoiceStatusId <= InvoiceStatus.Draft ? (
                                            <Input
                                                formGroupClassName="w-auto"
                                                id="emailSubject"
                                                name="invoice.invoicingEmailSubject"
                                                className="ml-3"
                                                value={invoice?.invoicingEmailSubject ?? ''}
                                                onChange={this.onChange}
                                                maxLength={255}
                                                displayMaxLength={false}
                                            />
                                        ) : (
                                            <span
                                                id="emailSubject"
                                                name="emailSubject"

                                            >
                                                {invoice?.invoicingEmailSubject}
                                            </span>
                                        )}
                                    </Flex>
                                )}
                                {(invoiceCustomer?.showTauliaContactOnInvoice) && (
                                    <Flex alignItems="center">
                                        <FormLabel
                                            htmlFor="tauliaContact"
                                            label="Taulia Contact"
                                        />
                                        <Input
                                            formGroupClassName="w-auto"
                                            name="tauliaContact"
                                            value={invoice?.tauliaContact ?? ''}
                                            className="ml-3"
                                            onChange={(
                                                evt,
                                            ) => {
                                                const { value } = evt
                                                    .target;
                                                const {
                                                    invoice,
                                                } = {
                                                    ...this
                                                        .state,
                                                };
                                                invoice.tauliaContact = value;
                                                this.setState(
                                                    {
                                                        invoice,
                                                    },
                                                );
                                            }}
                                        />
                                    </Flex>
                                )}
                                <div className="timesheet">
                                    <div
                                        style={{
                                            marginTop: '3px',
                                            marginLeft: '3px',
                                        }}
                                    >
                                        <SmallButton
                                            type="button"
                                            disabled={
                                                showTimesheetsReorderWidget
                                            }
                                            onClick={this.onReorderTimesheets}
                                        >
                                            <div
                                                style={{
                                                    paddingLeft: '4px',
                                                    paddingTop: '4px',
                                                    paddingRight: '6px',
                                                    paddingBottom: '4px',
                                                }}
                                            >
                                                <i className="fa fa-sort" />
                                                &nbsp;&nbsp;Reorder
                                            </div>
                                        </SmallButton>
                                    </div>

                                    <table className="table table-sm table-bordered table-striped payroll-table">
                                        <thead className="text-muted">
                                            <tr>
                                                <th className="timesheetNumber">
                                                    Timesheet #
                                                </th>
                                                <th className="dispatchLocation">
                                                    Dispatch Location
                                                </th>
                                                {invoice?.allowMultipleWONumbers && (
                                                    <th className="workOrderNumber">
                                                        Workorder #
                                                    </th>
                                                )}
                                                <th className="timesheetDateStart">
                                                    Date/Start
                                                </th>
                                                <th className="timesheetForeman">
                                                    Foreman
                                                </th>
                                                <th className="timesheetCrewLead">
                                                    {crewLead}
                                                </th>
                                                <th className="timesheetClientCancellation">
                                                    Client Cancellation
                                                </th>
                                                <th className="timesheetEmergency">
                                                    Emergency
                                                </th>
                                                <th className="timesheetBillingOverride">
                                                    Billing Overrides
                                                </th>
                                                <th className="timesheetSignatureOverride">
                                                    Signature Overrides
                                                </th>
                                                {invoice?.invoiceStatusId
                                                    <= InvoiceStatus.Draft && !isRentalInvoice && (
                                                        <>
                                                            <th className="timesheetView" />
                                                            <th className="timesheetDelete" />
                                                        </>
                                                    )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(invoice.invoiceTimesheets ?? [])
                                                .length ?
                                                (
                                                    <tr>
                                                        <td
                                                            colSpan="11"
                                                            className="text-center"
                                                        >
                                                            No timesheets found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    invoice.invoiceTimesheets.map(
                                                        (ts) => (
                                                            <tr
                                                                key={
                                                                    ts.timesheetId
                                                                }
                                                            >
                                                                <td className="timesheetNumber">
                                                                    {
                                                                        ts.timesheetNumber
                                                                    }
                                                                </td>

                                                                <td
                                                                    className="dispatchLocationName"
                                                                    style={{
                                                                        backgroundColor:
                                                                            ts.dispatchLocationName !== dispatchOfficeLocationName
                                                                                ? 'red'
                                                                                : 'auto',
                                                                    }}
                                                                >
                                                                    {
                                                                        ts.dispatchLocationName
                                                                    }
                                                                </td>

                                                                {invoice?.allowMultipleWONumbers && (
                                                                    <td className="workOrderNumber">
                                                                        {
                                                                            ts.workOrderNumber
                                                                        }
                                                                    </td>
                                                                )}
                                                                <td className="timesheetDateStart">
                                                                    {
                                                                        formatOffset(ts.jobStartTimeDisplayOffset, MomentDateFormat.DateTimeShortWeekDayAnte, null, ts.jobStartTimeDisplay)
                                                                    }
                                                                </td>
                                                                <td className="timesheetForeman">
                                                                    {
                                                                        ts.foremanName
                                                                    }
                                                                </td>
                                                                <td className="timesheetCrewLead">
                                                                    {
                                                                        ts.crewLeadName
                                                                    }
                                                                </td>
                                                                <td
                                                                    className="timesheetClientCancellation"
                                                                    style={{
                                                                        backgroundColor:
                                                                            ts.isCancel
                                                                                ? 'yellow'
                                                                                : 'auto',
                                                                    }}
                                                                >
                                                                    {ts.isCancel
                                                                        ? 'Yes'
                                                                        : 'No'}
                                                                </td>
                                                                <td className="timesheetEmergency">
                                                                    {ts.isEmergency
                                                                        ? 'Yes'
                                                                        : 'No'}
                                                                </td>
                                                                <td className="timesheetBillingOverride">
                                                                    {
                                                                        ts.billingOverrideStatus
                                                                    }
                                                                </td>
                                                                <td className="timesheetSignatureOverride">
                                                                    {ts.hasSignatureOverride
                                                                        ? 'Yes'
                                                                        : 'No'}
                                                                </td>
                                                                {invoice?.invoiceStatusId
                                                                    <= InvoiceStatus.Draft && !isRentalInvoice && (
                                                                        <>
                                                                            <td className="timesheetView text-center">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn shadow-none"
                                                                                    title="View this timesheet"
                                                                                    onClick={() => {
                                                                                        this.onViewTimesheetClicked(
                                                                                            ts,
                                                                                        );
                                                                                    }}
                                                                                    disabled={ts.isQuickSale}
                                                                                >
                                                                                    <span className="mt-1">
                                                                                        <i className="fa fa-eye" />
                                                                                    </span>
                                                                                </button>
                                                                            </td>
                                                                            <td className="timesheetDelete text-center">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn shadow-none"
                                                                                    title="Remove this timesheet"
                                                                                    disabled={
                                                                                        (
                                                                                            invoice
                                                                                            ?? {}
                                                                                        )
                                                                                            .invoiceTimesheets
                                                                                            ?.length
                                                                                        == 1
                                                                                        && (
                                                                                            invoice
                                                                                            ?? {}
                                                                                        )
                                                                                            .invoiceStatusId
                                                                                        > InvoiceStatus.New
                                                                                    }
                                                                                    onClick={() => {
                                                                                        this.onRemoveTimesheetClicked(
                                                                                            ts,
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <span className="text-danger mt-1">
                                                                                        <i className="fa fa-trash" />
                                                                                    </span>
                                                                                </button>
                                                                            </td>
                                                                        </>
                                                                    )}
                                                            </tr>
                                                        ),
                                                    )
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </UncontrolledCollapseUnderlineHeader>

                        {(invoice.customFormContextIds ?? []).map((ctxId) => (
                            <CustomForms
                                key={`${ctxId}-${invoice.id}`}
                                label={`${Object.keys(CustomFormContext).find((k) => CustomFormContext[k] === ctxId) ?? ''} Forms`}
                                className="ml-2"
                                tooltipId={`forms-incomplete-icon-${ctxId}`}
                                companyId={invoice.customerCompanyId}
                                selectedFormContextId={ctxId}
                                formContextId={CustomFormContext.Invoice}
                                invoice={invoice}
                            />
                        ))}

                        {!!isRentalInvoice
                            && (
                                <UncontrolledCollapseUnderlineHeader
                                    headerText="Summary of Rental Charges"
                                >
                                    <div className="invoice">
                                        <table className="table table-sm table-bordered table-striped payroll-table">
                                            <thead className="text-muted">
                                                <tr>
                                                    <th className="invoiceFlatCharge">
                                                        Charge
                                                    </th>
                                                    <th className="invoiceFlatResource">
                                                        Resource
                                                    </th>
                                                    <th className="invoiceFlatDate">
                                                        Rental Start Date
                                                    </th>
                                                    <th className="invoiceFlatDate">
                                                        Rental End Date
                                                    </th>
                                                    <th className="invoiceFlatCharge">
                                                        Usage
                                                    </th>
                                                    <th className="invoiceFlatCharge">
                                                        Period
                                                    </th>
                                                    <th className="invoiceFlatCharge">
                                                        Charge Qty
                                                    </th>
                                                    <th className="invoiceFlatBill">
                                                        Bill Qty
                                                    </th>
                                                    <th className="invoiceFlatCalc">
                                                        Calc. Rate Type
                                                    </th>
                                                    <th className="invoiceFlatTax">
                                                        Tax
                                                    </th>
                                                    <th className="invoiceFlatRate">
                                                        Rate
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {!flatten(
                                                    (
                                                        invoice.invoiceTimesheets ?? []
                                                    ).filter(
                                                        (x) => !!x.isRentalTimesheet,
                                                    ).map((x) => x.invoiceCharges),
                                                ).length ? (
                                                    <tr>
                                                        <td
                                                            colSpan="9"
                                                            className="text-center"
                                                        >
                                                            No Billable Rental Charges
                                                            found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    flatten(
                                                        (
                                                            invoice.invoiceTimesheets
                                                            ?? []
                                                        )
                                                            .filter((x) => !!x.isRentalTimesheet)
                                                            .map((x) => x.invoiceCharges),
                                                    )
                                                        .filter(
                                                            (x) => x.chargeTypeUnitsId
                                                                == ChargeTypeUnits.Flat && x.invoiceChargeTypeGroupUID == null,
                                                        )
                                                        .map(
                                                            (
                                                                flatCharge,
                                                            ) => (
                                                                <tr
                                                                    key={`${flatCharge.billingDetailId
                                                                        }|${uuid()}`}
                                                                >
                                                                    <td className="invoiceFlatCharge">
                                                                        {
                                                                            flatCharge.chargeTypeName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatResource">
                                                                        {
                                                                            flatCharge.resourceName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatDate">
                                                                        {
                                                                            flatCharge.startDateDisplay
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatDate">
                                                                        {
                                                                            flatCharge.endDateDisplay
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatCharge">
                                                                        {parseFloat(
                                                                            flatCharge.usage,
                                                                        ).toFixed(
                                                                            2,
                                                                        )}
                                                                    </td>
                                                                    <td className="invoiceFlatResource">
                                                                        {
                                                                            flatCharge.chargeTypeUnitsName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatCharge">
                                                                        {parseFloat(
                                                                            flatCharge.chargeQty,
                                                                        ).toFixed(
                                                                            2,
                                                                        )}
                                                                    </td>
                                                                    <td className="invoiceFlatBill">
                                                                        {parseFloat(
                                                                            flatCharge.billQty,
                                                                        ).toFixed(
                                                                            2,
                                                                        )}
                                                                    </td>
                                                                    <td className="invoiceFlatCalc">
                                                                        {
                                                                            flatCharge.calculatedBillingRateTypeName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatTax">
                                                                        {flatCharge.isTaxable ? 'Yes' : 'No'}
                                                                    </td>
                                                                    <td className="invoiceFlatRate">
                                                                        <PriceDisplay amount={flatCharge.billingRate} />
                                                                    </td>
                                                                </tr>
                                                            ),
                                                        )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </UncontrolledCollapseUnderlineHeader>
                            )}
                        {(!isRentalInvoice || invoiceChargeTypeGroups?.length > 0)
                            && (
                                <CollapseUnderlineHeaderWithIcon
                                    headerText="Summary of Charge Type Groups"
                                    isOpen={hourlyChargesOpen}
                                    showIcon={isNew || invoice?.invoiceStatusId == InvoiceStatus.New}
                                    onIconClick={this.onAddChargeTypeGroup}
                                    toggleCollapse={() => this.setState({
                                        hourlyChargesOpen: !hourlyChargesOpen,
                                    })}
                                >
                                    <div className="invoice">
                                        <table className="table table-sm table-bordered table-striped payroll-table">
                                            <thead className="text-muted">
                                                <tr>
                                                    <th className="invoiceHourlyCharge">
                                                        Charge Type Group
                                                    </th>
                                                    <th className="invoiceHourlyCharge">
                                                        Date
                                                    </th>
                                                    <th className="invoiceHourlyCharge">
                                                        Quantity
                                                    </th>
                                                    <th className="invoiceFlatCalc">
                                                        Calc. Rate Type
                                                    </th>
                                                    <th className="invoiceFlatTax">
                                                        Tax
                                                    </th>
                                                    <th className="invoiceHourlyCharge">
                                                        Bill Rate
                                                    </th>
                                                    {(isNew || invoice?.invoiceStatusId == InvoiceStatus.New) && (
                                                        <>
                                                            <th className="invoiceHourlyCharge" />
                                                            <th className="invoiceHourlyCharge" />
                                                        </>
                                                    )}
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {!invoiceChargeTypeGroups.length ? (
                                                    <tr>
                                                        <td
                                                            colSpan="11"
                                                            className="text-center"
                                                        >
                                                            No charge type groups found.
                                                        </td>
                                                    </tr>
                                                ) : (invoiceChargeTypeGroups.map((ctGroup) => {
                                                    const details = ctGroupCharges.filter((x) => x.invoiceChargeTypeGroupUID == ctGroup.invoiceChargeTypeGroupUID);

                                                    return (
                                                        <tr
                                                            key={`${uuid()}`}
                                                        >
                                                            <td className="invoiceHourlyCharge">

                                                                <CollapseTableElement
                                                                    headerText={ctGroup.chargeTypeName}
                                                                    isOpen={ctGroup.isOpen}
                                                                    toggleCollapse={() => {
                                                                        ctGroup.isOpen = !ctGroup.isOpen;
                                                                        this.setState({
                                                                            ctGroup: !ctGroup,
                                                                        });
                                                                    }}
                                                                >
                                                                    <table
                                                                        className="table table-sm table-bordered payroll-table"
                                                                        style={{ marginTop: '5px' }}
                                                                    >
                                                                        <thead className="text-muted">
                                                                            <tr>
                                                                                <th className="invoiceHourlyCharge">
                                                                                    Charge
                                                                                </th>
                                                                                <th className="invoiceHourlyResource">
                                                                                    Resource
                                                                                </th>
                                                                                <th className="invoiceHourlyStart">
                                                                                    Start Time
                                                                                </th>
                                                                                <th className="invoiceHourlyStop">
                                                                                    Stop Time
                                                                                </th>
                                                                                <th className="invoiceHourlyBill">
                                                                                    Bill Qty
                                                                                </th>
                                                                                <th className="invoiceHourlyBill">
                                                                                    Other CT Groups
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {!details.length ? (
                                                                                <tr>
                                                                                    <td
                                                                                        colSpan="11"
                                                                                        className="text-center"
                                                                                    >
                                                                                        No charge type group detail.
                                                                                    </td>
                                                                                </tr>
                                                                            ) : (
                                                                                details.map((
                                                                                    ctDetail,
                                                                                    ctrDetailIndex,
                                                                                ) => (
                                                                                    <tr key={ctrDetailIndex}>
                                                                                        <td className="invoiceHourlyCharge">
                                                                                            {ctDetail.chargeTypeName}
                                                                                            {' '}
                                                                                            -
                                                                                            {ctDetail.calculatedBillingRateTypeName}

                                                                                        </td>
                                                                                        <td className="invoiceHourlyResource">
                                                                                            {
                                                                                                ctDetail.resourceName
                                                                                            }
                                                                                        </td>
                                                                                        <td className="invoiceHourlyStart">
                                                                                            {
                                                                                                ctDetail.startDisplay
                                                                                            }
                                                                                        </td>
                                                                                        <td className="invoiceHourlyStop">
                                                                                            {
                                                                                                ctDetail.endDisplay
                                                                                            }
                                                                                        </td>
                                                                                        <td className="invoiceHourlyBill">
                                                                                            {parseFloat(
                                                                                                ctDetail.billQty,
                                                                                            ).toFixed(2)}
                                                                                        </td>
                                                                                        <td className="invoiceHourlyBill">
                                                                                            {ctDetail.refOtherInvoices}
                                                                                        </td>
                                                                                    </tr>
                                                                                ))
                                                                            )}
                                                                        </tbody>
                                                                    </table>

                                                                </CollapseTableElement>

                                                            </td>
                                                            <td className="invoiceHourlyDate">
                                                                {
                                                                    ctGroup.dateDisplay
                                                                }
                                                            </td>
                                                            <td className="invoiceHourlyStop">
                                                                {
                                                                    parseFloat(ctGroup.chargeQty).toFixed(2)
                                                                }
                                                            </td>
                                                            <td className="invoiceHourlyStop">
                                                                {ctGroup.calculatedBillingRateTypeName}
                                                            </td>
                                                            <td className="invoiceHourlyStop">
                                                                {ctGroup.isTaxable ? 'Yes' : 'No'}
                                                            </td>
                                                            <td className="invoiceHourlyStart">
                                                                <FormatPrice amount={ctGroup.billingRate} />
                                                            </td>
                                                            {(isNew || invoice?.invoiceStatusId == InvoiceStatus.New) && (
                                                                <>
                                                                    <td
                                                                        className="timesheetView text-center"
                                                                        style={{ padding: 0 }}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            className="btn shadow-none"
                                                                            title="Edit this group"
                                                                            onClick={() => this.onEditChargeTypeGroup(ctGroup)}
                                                                        >
                                                                            <span className="mt-1">
                                                                                <i
                                                                                    className="fa fa-edit"
                                                                                    style={{ fontSize: '12px' }}
                                                                                />
                                                                            </span>
                                                                        </button>
                                                                    </td>
                                                                    <td
                                                                        className="timesheetDelete text-center"
                                                                        style={{ padding: 0 }}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            className="btn shadow-none"
                                                                            title="Remove this group"
                                                                            onClick={() => {
                                                                                this.onDeleteChargeTypeGroup(ctGroup);
                                                                            }}
                                                                        >
                                                                            <span className="text-danger mt-1">
                                                                                <i
                                                                                    className="fa fa-trash"
                                                                                    style={{ fontSize: '12px' }}
                                                                                />
                                                                            </span>
                                                                        </button>
                                                                    </td>
                                                                </>
                                                            )}
                                                        </tr>
                                                    );
                                                },
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CollapseUnderlineHeaderWithIcon>
                            )}
                        {(!isRentalInvoice || tenantSettings.combineRentalNonRentalChargesOnRentalInvoices)
                            && (
                                <>
                                    <UncontrolledCollapseUnderlineHeader
                                        headerText="Summary of Billable Hourly Charges"
                                    >
                                        <div className="invoice">
                                            <table className="table table-sm table-bordered table-striped payroll-table">
                                                <thead className="text-muted">
                                                    <tr>
                                                        <th className="invoiceHourlyCharge">
                                                            Charge
                                                        </th>
                                                        <th className="invoiceHourlyResource">
                                                            Resource
                                                        </th>
                                                        <th className="invoiceHourlyDate">
                                                            Date
                                                        </th>
                                                        <th className="invoiceHourlyStart">
                                                            Start Time
                                                        </th>
                                                        <th className="invoiceHourlyStop">
                                                            Stop Time
                                                        </th>
                                                        <th className="invoiceHourlyCharge">
                                                            Charge Qty
                                                        </th>
                                                        <th className="invoiceHourlyBill">
                                                            Bill Qty
                                                        </th>
                                                        <th className="invoiceHourlyEmergency">
                                                            Emergency
                                                        </th>
                                                        <th className="invoiceHourlyOverride">
                                                            Bill Override
                                                        </th>
                                                        <th className="invoiceHourlyCalc">
                                                            Calc. Rate Type
                                                        </th>
                                                        <th className="invoiceHourlyTax">
                                                            Tax
                                                        </th>
                                                        <th className="invoiceHourlyRate">
                                                            Rate
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {!charges.length ? (
                                                        <tr>
                                                            <td
                                                                colSpan="11"
                                                                className="text-center"
                                                            >
                                                                No Billable Hourly Charges
                                                                found.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        charges.map(
                                                            (
                                                                hourlyCharge,
                                                            ) => (
                                                                <tr
                                                                    key={`${hourlyCharge.billingDetailId
                                                                        }|${uuid()}`}
                                                                >
                                                                    <td className="invoiceHourlyCharge">
                                                                        {
                                                                            hourlyCharge.chargeTypeName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceHourlyResource">
                                                                        {
                                                                            hourlyCharge.resourceName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceHourlyDate">
                                                                        {
                                                                            hourlyCharge.dateDisplay
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceHourlyStart">
                                                                        {
                                                                            hourlyCharge.startDisplay
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceHourlyStop">
                                                                        {
                                                                            hourlyCharge.endDisplay
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceHourlyCharge">
                                                                        {parseFloat(
                                                                            hourlyCharge.chargeQty,
                                                                        ).toFixed(2)}
                                                                    </td>
                                                                    <td className="invoiceHourlyBill">
                                                                        {parseFloat(
                                                                            hourlyCharge.billQty,
                                                                        ).toFixed(2)}
                                                                    </td>
                                                                    <td className="invoiceHourlyEmergency">
                                                                        {hourlyCharge.emergency
                                                                            ? 'Yes'
                                                                            : 'No'}
                                                                    </td>
                                                                    <td className="invoiceHourlyOverride">
                                                                        {
                                                                            hourlyCharge.billingOverrideStatus
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceHourlyCalcRateType">
                                                                        {
                                                                            hourlyCharge.calculatedBillingRateTypeName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceHourlyTax">
                                                                        {hourlyCharge.isTaxable ? 'Yes' : 'No'}
                                                                    </td>
                                                                    <td className="invoiceHourlyRate">
                                                                        <PriceDisplay amount={hourlyCharge.billingRate} />
                                                                    </td>
                                                                </tr>
                                                            ),
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </UncontrolledCollapseUnderlineHeader>

                                    <UncontrolledCollapseUnderlineHeader
                                        headerText="Summary of Billable Flat Charge"
                                    >
                                        <div className="invoice">
                                            <table className="table table-sm table-bordered table-striped payroll-table">
                                                <thead className="text-muted">
                                                    <tr>
                                                        <th className="invoiceFlatCharge">
                                                            Charge
                                                        </th>
                                                        <th className="invoiceFlatResource">
                                                            Resource
                                                        </th>
                                                        <th className="invoiceFlatDate">
                                                            Date
                                                        </th>
                                                        <th className="invoiceFlatCharge">
                                                            Charge Qty
                                                        </th>
                                                        <th className="invoiceFlatBill">
                                                            Bill Qty
                                                        </th>
                                                        <th className="invoiceFlatEmergency">
                                                            Emergency
                                                        </th>
                                                        <th className="invoiceFlatOverride">
                                                            Bill Override
                                                        </th>
                                                        <th className="invoiceFlatCalc">
                                                            Calc. Rate Type
                                                        </th>
                                                        <th className="invoiceFlatTax">
                                                            Tax
                                                        </th>
                                                        <th className="invoiceFlatRate">
                                                            Rate
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {!flatten(
                                                        (
                                                            invoice.invoiceTimesheets ?? []
                                                        ).filter(
                                                            (x) => !x.isRentalTimesheet,
                                                        ).map((x) => x.invoiceCharges),
                                                    ).filter(
                                                        (x) => x.chargeTypeUnitsId
                                                            == ChargeTypeUnits.Flat,
                                                    ).length ? (
                                                        <tr>
                                                            <td
                                                                colSpan="9"
                                                                className="text-center"
                                                            >
                                                                No Billable Flat Charges
                                                                found.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        flatCharges.map(
                                                            (
                                                                flatCharge,
                                                            ) => (
                                                                <tr
                                                                    key={`${flatCharge.billingDetailId
                                                                        }|${uuid()}`}
                                                                >
                                                                    <td className="invoiceFlatCharge">
                                                                        {
                                                                            flatCharge.chargeTypeName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatResource">
                                                                        {
                                                                            flatCharge.resourceName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatDate">
                                                                        {
                                                                            flatCharge.dateDisplay
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatCharge">
                                                                        {parseFloat(
                                                                            flatCharge.chargeQty,
                                                                        ).toFixed(
                                                                            2,
                                                                        )}
                                                                    </td>
                                                                    <td className="invoiceFlatBill">
                                                                        {parseFloat(
                                                                            flatCharge.billQty,
                                                                        ).toFixed(
                                                                            2,
                                                                        )}
                                                                    </td>
                                                                    <td className="invoiceFlatEmergency">
                                                                        {flatCharge.emergency
                                                                            ? 'Yes'
                                                                            : 'No'}
                                                                    </td>
                                                                    <td className="invoiceFlatOverride">
                                                                        {flatCharge.billingOverrideStatus !== 'No' ?
                                                                            `Yes (${flatCharge.billingOverrideStatus})`
                                                                            : flatCharge.billingOverrideStatus}
                                                                    </td>
                                                                    <td className="invoiceFlatCalc">
                                                                        {
                                                                            flatCharge.calculatedBillingRateTypeName
                                                                        }
                                                                    </td>
                                                                    <td className="invoiceFlatTax">
                                                                        {flatCharge.isTaxable ? 'Yes' : 'No'}
                                                                    </td>
                                                                    <td className="invoiceFlatRate">
                                                                        {flatCharge.isCredit
                                                                            ? (<>
                                                                                {' '}
                                                                                (
                                                                                <PriceDisplay amount={flatCharge.billingRate} />
                                                                                )
                                                                                {' '}
                                                                            </>)
                                                                            : <PriceDisplay amount={flatCharge.billingRate} />
                                                                        }
                                                                    </td>
                                                                </tr>
                                                            ),
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </UncontrolledCollapseUnderlineHeader>

                                    <UncontrolledCollapseUnderlineHeader
                                        headerText="Summary of Billable Sale Charges"
                                    >
                                        <div className="invoice">
                                            <table className="table table-sm table-bordered table-striped payroll-table">
                                                <thead className="text-muted">
                                                    <tr>
                                                        <th className="invoiceFlatCharge">
                                                            Charge
                                                        </th>
                                                        <th className="invoiceFlatResource">
                                                            Resource
                                                        </th>
                                                        <th className="invoiceFlatDate">
                                                            Date
                                                        </th>
                                                        <th className="invoiceFlatCharge">
                                                            Charge Qty
                                                        </th>
                                                        <th className="invoiceFlatBill">
                                                            Bill Qty
                                                        </th>
                                                        <th className="invoiceFlatTax">
                                                            Tax
                                                        </th>
                                                        <th className="invoiceFlatRate">
                                                            Rate
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {!salesCharges.length ? (
                                                        <tr>
                                                            <td
                                                                colSpan="7"
                                                                className="text-center"
                                                            >
                                                                No Billable Sales found.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        salesCharges.map(
                                                            (salesCharge) => (
                                                                <tr
                                                                    key={`${salesCharge.billingDetailId}|${uuid()}`}
                                                                >
                                                                    <td className="invoiceFlatCharge">
                                                                        {salesCharge.chargeTypeName}
                                                                    </td>
                                                                    <td className="invoiceFlatResource">
                                                                        {salesCharge.resourceName}
                                                                    </td>
                                                                    <td className="invoiceFlatDate">
                                                                        {salesCharge.dateDisplay}
                                                                    </td>
                                                                    <td className="invoiceFlatCharge">
                                                                        {parseFloat(salesCharge.chargeQty).toFixed(2)}
                                                                    </td>
                                                                    <td className="invoiceFlatBill">
                                                                        {parseFloat(salesCharge.billQty).toFixed(2)}
                                                                    </td>
                                                                    <td className="invoiceFlatTax">
                                                                        {salesCharge.isTaxable ? 'Yes' : 'No'}
                                                                    </td>
                                                                    <td className="invoiceFlatRate">
                                                                        <PriceDisplay amount={salesCharge.billingRate} />
                                                                    </td>
                                                                </tr>
                                                            ),
                                                        )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </UncontrolledCollapseUnderlineHeader>

                                    <UncontrolledCollapseUnderlineHeader
                                        headerText="Summary of Billable Percentage Charge"
                                    >
                                        <div className="invoice">
                                            <table className="table table-sm table-bordered table-striped payroll-table">
                                                <thead className="text-muted">
                                                    <tr>
                                                        <th className="invoiceFlatCharge">
                                                            Charge
                                                        </th>
                                                        <th className="invoiceFlatDate">
                                                            Date
                                                        </th>
                                                        <th className="invoiceFlatCharge">
                                                            Charge Qty
                                                        </th>
                                                        <th className="invoiceFlatBill">
                                                            Bill Qty
                                                        </th>
                                                        <th className="invoiceFlatEmergency">
                                                            Emergency
                                                        </th>
                                                        <th className="invoiceFlatOverride">
                                                            Bill Override
                                                        </th>
                                                        <th className="invoiceFlatCalc">
                                                            Calc. Rate Type
                                                        </th>
                                                        <th className="invoiceFlatTax">
                                                            Tax
                                                        </th>
                                                        <th className="invoiceFlatRate">
                                                            Rate
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {!flatten(
                                                        (
                                                            invoice.invoiceTimesheets ?? []
                                                        ).filter(
                                                            (x) => !x.isRentalTimesheet,
                                                        ).map((x) => x.invoiceCharges),
                                                    ).filter(
                                                        (x) => x.chargeTypeUnitsId
                                                            == ChargeTypeUnits.Percentage,
                                                    ).length ? (
                                                        <tr>
                                                            <td
                                                                colSpan="9"
                                                                className="text-center"
                                                            >
                                                                No Billable Percentage Charges
                                                                found.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        flatten(
                                                            (
                                                                invoice.invoiceTimesheets
                                                                ?? []
                                                            ).map((x) => x.invoiceCharges),
                                                        )
                                                            .filter(
                                                                (x) => x.chargeTypeUnitsId
                                                                    == ChargeTypeUnits.Percentage,
                                                            )
                                                            .map(
                                                                (
                                                                    flatCharge,
                                                                ) => (
                                                                    <tr
                                                                        key={`${flatCharge.billingDetailId
                                                                            }|${uuid()}`}
                                                                    >
                                                                        <td className="invoiceFlatCharge">
                                                                            {
                                                                                flatCharge.chargeTypeName
                                                                            }
                                                                        </td>
                                                                        <td className="invoiceFlatDate">
                                                                            {
                                                                                flatCharge.dateDisplay
                                                                            }
                                                                        </td>
                                                                        <td className="invoiceFlatCharge">
                                                                            {parseFloat(
                                                                                flatCharge.chargeQty,
                                                                            ).toFixed(
                                                                                2,
                                                                            )}
                                                                        </td>
                                                                        <td className="invoiceFlatBill">
                                                                            {parseFloat(
                                                                                flatCharge.billQty,
                                                                            ).toFixed(
                                                                                2,
                                                                            )}
                                                                        </td>
                                                                        <td className="invoiceFlatEmergency">
                                                                            {flatCharge.emergency
                                                                                ? 'Yes'
                                                                                : 'No'}
                                                                        </td>
                                                                        <td className="invoiceFlatOverride">
                                                                            {flatCharge.billingOverrideStatus !== 'No' ?
                                                                                `Yes (${flatCharge.billingOverrideStatus})`
                                                                                : flatCharge.billingOverrideStatus}
                                                                        </td>
                                                                        <td className="invoiceFlatCalc">
                                                                            {
                                                                                flatCharge.calculatedBillingRateTypeName
                                                                            }
                                                                        </td>
                                                                        <td className="invoiceFlatTax">
                                                                            {flatCharge.isTaxable ? 'Yes' : 'No'}
                                                                        </td>
                                                                        <td className="invoiceFlatRate">
                                                                            {
                                                                                `${flatCharge.billingRate}%`
                                                                            }
                                                                        </td>
                                                                    </tr>
                                                                ),
                                                            )
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </UncontrolledCollapseUnderlineHeader>
                                </>
                            )}

                        {invoice?.invoiceStatusId >= InvoiceStatus.Draft && (
                            <>
                                <UncontrolledCollapseUnderlineHeader
                                    headerText="Charge Details"
                                >
                                    <div className="invoice">
                                        <SmallButton
                                            type="button"
                                            disabled={showReorderWidget}
                                            onClick={
                                                this
                                                    .onReorderInvoiceChargeDetails
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faSort}
                                                className="mr-2"
                                            />
                                            Reorder
                                        </SmallButton>
                                        <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                            <thead className="text-muted">
                                                <tr>
                                                    <th className="isadjusted" />
                                                    {invoice.useCTRReport
                                                        == 'True' && (
                                                            <th className="serviceItem">
                                                                Service Item
                                                            </th>
                                                        )}
                                                    {invoice.allowMultipleWONumbers && (
                                                        <th className="serviceItem">
                                                            Workorder Number
                                                        </th>
                                                    )}
                                                    <th className="invoiceChargeQty">
                                                        System Qty
                                                    </th>
                                                    <th className="invoiceChargeQty">
                                                        Invoice Qty
                                                    </th>
                                                    {!!isRentalInvoice && (
                                                        <>
                                                            <th className="invoiceChargeQty">
                                                                System Usage
                                                            </th>
                                                            <th className="invoiceChargeQty">
                                                                Invoice Usage
                                                            </th>
                                                        </>
                                                    )}
                                                    <th className="invoiceChargeDescription">
                                                        System Description
                                                    </th>
                                                    <th className="invoiceChargeDescription">
                                                        Invoice Description
                                                    </th>
                                                    <th className="invoiceChargeRate">
                                                        System Rate
                                                    </th>
                                                    <th className="invoiceChargeRate">
                                                        Invoice Rate
                                                    </th>
                                                    <th className="invoiceChargeAmount">
                                                        System Amount
                                                    </th>
                                                    <th className="invoiceUserChargeAmount">
                                                        Invoice Amount
                                                    </th>
                                                    <th className="invoiceUserChargeTax">
                                                        Tax
                                                    </th>
                                                    {associateInventoryItem && (
                                                        <th className="invoiceUserQuickBooksInventoryItem">
                                                            Inventory Item
                                                        </th>
                                                    )}
                                                    {associateInventoryItem && (
                                                        <th className="invoiceUserQuickBooksSalesAccount">
                                                            Sales Account
                                                        </th>
                                                    )}
                                                    <th
                                                        style={{ width: '5%' }}
                                                    />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {!(
                                                    invoice.invoiceChargeDetails.filter((x) => x.current)
                                                    ?? []
                                                ).length ? (
                                                    <tr>
                                                        <td
                                                            colSpan="4"
                                                            className="text-center"
                                                        >
                                                            No Charge Details found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    invoice.invoiceChargeDetails.map(
                                                        (
                                                            chargeDetail,
                                                            charge_index,
                                                        ) => {
                                                            const readOnly = invoice?.invoiceStatusId == InvoiceStatus.Approved
                                                                || invoice?.invoiceStatusId == InvoiceStatus.Exported;

                                                            if (!chargeDetail.current) {
                                                                return null;
                                                            }

                                                            if (!chargeDetail.removed) {
                                                                const amt = chargeDetail.userChargeAmount ?? 0;
                                                                chargeDetailTotal += chargeDetail.isCredit ? -amt : amt;
                                                            }

                                                            if (chargeDetail.invoiceChargeDetailTypeId == InvoiceChargeDetailType.Header) {
                                                                return (
                                                                    <tr
                                                                        key={chargeDetail.id}
                                                                        className={`timesheet-details-table-row${chargeDetail.removed
                                                                            ? ' strikeout'
                                                                            : ''
                                                                            }`}
                                                                    >
                                                                        <td>
                                                                            <span>
                                                                                <Badge
                                                                                    color="danger"
                                                                                    className="p-1"
                                                                                >
                                                                                    HDR
                                                                                </Badge>
                                                                            </span>
                                                                        </td>
                                                                        <td colSpan="4">
                                                                            <Textarea
                                                                                name="userDescription"
                                                                                value={chargeDetail.userDescription ?? ''}
                                                                                onChange={(
                                                                                    evt,
                                                                                ) => {
                                                                                    const { value } = evt
                                                                                        .target;
                                                                                    const {
                                                                                        invoice,
                                                                                    } = {
                                                                                        ...this
                                                                                            .state,
                                                                                    };
                                                                                    invoice.invoiceChargeDetails[
                                                                                        charge_index
                                                                                    ].userDescription = value;
                                                                                    this.setState(
                                                                                        {
                                                                                            invoice,
                                                                                        },
                                                                                    );
                                                                                }}
                                                                            />
                                                                        </td>
                                                                        <td className="invoiceChargeDelete text-center">
                                                                            {invoice?.invoiceStatusId != InvoiceStatus.Exported && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn shadow-none"
                                                                                    title="Remove this header"
                                                                                    onClick={() => {
                                                                                        this.onRemoveInvoiceChargeDetailClicked(
                                                                                            chargeDetail,
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <span className="text-danger mt-1">
                                                                                        <i className="fa fa-eraser" />
                                                                                    </span>
                                                                                </button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            }

                                                            if (chargeDetail.invoiceChargeDetailTypeId == InvoiceChargeDetailType.Annotation) {
                                                                return (
                                                                    <tr
                                                                        key={chargeDetail.id}
                                                                        className={`timesheet-details-table-row${chargeDetail.removed
                                                                            ? ' strikeout'
                                                                            : ''
                                                                            }`}
                                                                    >
                                                                        <td>
                                                                            <span>
                                                                                <Badge
                                                                                    color="primary"
                                                                                    className="p-1"
                                                                                >
                                                                                    TXT
                                                                                </Badge>
                                                                            </span>
                                                                        </td>
                                                                        <td colSpan="4">
                                                                            <Textarea
                                                                                name="userDescription"
                                                                                value={chargeDetail.userDescription ?? ''}
                                                                                onChange={(
                                                                                    evt,
                                                                                ) => {
                                                                                    const { value } = evt
                                                                                        .target;
                                                                                    const {
                                                                                        invoice,
                                                                                    } = {
                                                                                        ...this
                                                                                            .state,
                                                                                    };
                                                                                    invoice.invoiceChargeDetails[
                                                                                        charge_index
                                                                                    ].userDescription = value;
                                                                                    this.setState(
                                                                                        {
                                                                                            invoice,
                                                                                        },
                                                                                    );
                                                                                }}
                                                                            />
                                                                        </td>
                                                                        <td className="invoiceChargeDelete text-center">
                                                                            {!!chargeDetail.isAdjustment && invoice?.invoiceStatusId
                                                                                != InvoiceStatus.Exported && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn shadow-none"
                                                                                        title="Remove this annotation"
                                                                                        onClick={() => {
                                                                                            this.onRemoveInvoiceChargeDetailClicked(
                                                                                                chargeDetail,
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <span className="text-danger mt-1">
                                                                                            <i className="fa fa-eraser" />
                                                                                        </span>
                                                                                    </button>
                                                                                )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            }

                                                            if (chargeDetail.invoiceChargeDetailTypeId == InvoiceChargeDetailType.LineItem) {
                                                                return (
                                                                    <tr
                                                                        key={chargeDetail.id}
                                                                        className={`timesheet-details-table-row${chargeDetail.removed
                                                                            ? ' strikeout'
                                                                            : ''
                                                                            }`}
                                                                    >
                                                                        <td>
                                                                            {!!chargeDetail.isAdjustment && (
                                                                                <span className="invoice-lineitem-isadjusted">
                                                                                    <Badge className="p-1">
                                                                                        ADJ
                                                                                    </Badge>
                                                                                </span>
                                                                            )}
                                                                            {((!chargeDetail.isAdjustment && chargeDetail.chargeQty !== chargeDetail.userChargeQty)
                                                                                || chargeDetail.billingRate !== chargeDetail.userBillingRate) && (
                                                                                    <Badge
                                                                                        color="success"
                                                                                        className="p-1"
                                                                                    >
                                                                                        EDT
                                                                                    </Badge>
                                                                                )}
                                                                        </td>
                                                                        {invoice.allowMultipleWONumbers && (
                                                                            <td width="225">
                                                                                {readOnly && (
                                                                                    <span>{chargeDetail.workOrderNumber}</span>
                                                                                )}

                                                                                {!readOnly && (
                                                                                    <Input
                                                                                        type="text"
                                                                                        name="workOrderNumber"
                                                                                        value={chargeDetail.workOrderNumber ?? ''}
                                                                                        onChange={(
                                                                                            evt,
                                                                                        ) => {
                                                                                            const { value } = evt
                                                                                                .target;
                                                                                            const {
                                                                                                invoice,
                                                                                            } = {
                                                                                                ...this
                                                                                                    .state,
                                                                                            };
                                                                                            invoice.invoiceChargeDetails[
                                                                                                charge_index
                                                                                            ].workOrderNumber = value;
                                                                                            this.setState(
                                                                                                {
                                                                                                    invoice,
                                                                                                },
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </td>
                                                                        )}
                                                                        {invoice.useCTRReport == 'True' && readOnly && (
                                                                            <td>
                                                                                {
                                                                                    invoice
                                                                                        .invoiceChargeDetails[
                                                                                        charge_index
                                                                                    ]
                                                                                        .serviceItem
                                                                                }
                                                                            </td>
                                                                        )}

                                                                        {invoice.useCTRReport == 'True' && !readOnly && (
                                                                            <td width="250">
                                                                                <ValidatedSelect
                                                                                    id={`serviceItem-${chargeDetail.id}`}
                                                                                    name={`serviceItem-${chargeDetail.id}`}
                                                                                    options={
                                                                                        serviceItems
                                                                                    }
                                                                                    value={
                                                                                        (serviceItems ?? []).find(
                                                                                            (
                                                                                                s,
                                                                                            ) => s.value
                                                                                                == invoice
                                                                                                    .invoiceChargeDetails[
                                                                                                    charge_index
                                                                                                ]
                                                                                                    .serviceItem,
                                                                                        )
                                                                                        ?? ''
                                                                                    }
                                                                                    onChange={(
                                                                                        evt,
                                                                                    ) => {
                                                                                        const { value, id } = evt;
                                                                                        const {
                                                                                            invoice,
                                                                                        } = {
                                                                                            ...this
                                                                                                .state,
                                                                                        };
                                                                                        invoice.invoiceChargeDetails[
                                                                                            charge_index
                                                                                        ].serviceItem = value;
                                                                                        invoice.invoiceChargeDetails[
                                                                                            charge_index
                                                                                        ].serviceItemId = id;
                                                                                        this.setState(
                                                                                            {
                                                                                                invoice,
                                                                                            },
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                        )}
                                                                        <td className="invoiceChargeQty">
                                                                            {parseFloat(
                                                                                chargeDetail.chargeQty,
                                                                            ).toFixed(
                                                                                2,
                                                                            )}
                                                                        </td>

                                                                        <td
                                                                            className="invoiceUserChargeQty"
                                                                            width="150"
                                                                        >
                                                                            {!readOnly && (
                                                                                <NumericInput
                                                                                    className="form-control-sm text-right"
                                                                                    name="userChargeQty"
                                                                                    required
                                                                                    disabled={chargeDetail.billingUnitName == ChargeTypeUnitNames.Percentage}
                                                                                    value={chargeDetail.userChargeQty ?? ''}
                                                                                    onChange={(
                                                                                        evt,
                                                                                    ) => {
                                                                                        const { value } = evt
                                                                                            .target;
                                                                                        const {
                                                                                            invoice,
                                                                                        } = {
                                                                                            ...this
                                                                                                .state,
                                                                                        };
                                                                                        invoice.invoiceChargeDetails[charge_index].userChargeQty = Number(value ?? 0) || 0;

                                                                                        const qty = Number(invoice.invoiceChargeDetails[charge_index].userChargeQty ?? 0) || 0;
                                                                                        const usage = Number(invoice.invoiceChargeDetails[charge_index].userUsage ?? 1) || 1;
                                                                                        const rate = Number(invoice.invoiceChargeDetails[charge_index].userBillingRate ?? 0) || 0;

                                                                                        invoice.invoiceChargeDetails[charge_index].userChargeAmount = qty * usage * rate;

                                                                                        this.setState({ invoice });
                                                                                        this.updatePercentageSurcharge();
                                                                                        this.redistributeExpandedAmounts(invoice, -1, invoice.invoiceChargeDetails[charge_index].id);
                                                                                    }}
                                                                                    type="number"
                                                                                    min={0}
                                                                                    step={0.01}
                                                                                    preventNegative
                                                                                />
                                                                            )}
                                                                            {readOnly && (
                                                                                <span>
                                                                                    {parseFloat(
                                                                                        chargeDetail.userChargeQty,
                                                                                    ).toFixed(
                                                                                        2,
                                                                                    )}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        {!!isRentalInvoice && (
                                                                            <>
                                                                                <td className="invoiceChargeQty">
                                                                                    {parseFloat(
                                                                                        chargeDetail.usage,
                                                                                    ).toFixed(
                                                                                        2,
                                                                                    )}
                                                                                </td>

                                                                                <td
                                                                                    className="invoiceUserChargeQty"
                                                                                    width="150"
                                                                                >
                                                                                    {!readOnly && (
                                                                                        <NumericInput
                                                                                            className="form-control-sm text-right"
                                                                                            name="userUsage"
                                                                                            required
                                                                                            disabled={chargeDetail.billingUnitName == ChargeTypeUnitNames.Percentage}
                                                                                            value={chargeDetail.userUsage ?? ''}
                                                                                            onChange={(
                                                                                                evt,
                                                                                            ) => {
                                                                                                const { value } = evt
                                                                                                    .target;
                                                                                                const {
                                                                                                    invoice,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                invoice.invoiceChargeDetails[charge_index].userUsage = Number(value ?? 0) || 0;

                                                                                                const qty = Number(invoice.invoiceChargeDetails[charge_index].userChargeQty ?? 0) || 0;
                                                                                                const usage = Number(invoice.invoiceChargeDetails[charge_index].userUsage ?? 1) || 1;
                                                                                                const rate = Number(invoice.invoiceChargeDetails[charge_index].userBillingRate ?? 0) || 0;

                                                                                                invoice.invoiceChargeDetails[charge_index].userChargeAmount = qty * usage * rate;

                                                                                                this.setState({ invoice });
                                                                                                this.updatePercentageSurcharge();
                                                                                                this.redistributeExpandedAmounts(invoice, -1, invoice.invoiceChargeDetails[charge_index].id);
                                                                                            }}
                                                                                            type="number"
                                                                                            min={0}
                                                                                            step={0.01}
                                                                                            preventNegative
                                                                                        />
                                                                                    )}
                                                                                    {readOnly && (
                                                                                        <span>
                                                                                            {parseFloat(
                                                                                                chargeDetail.userUsage,
                                                                                            ).toFixed(
                                                                                                2,
                                                                                            )}
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                            </>
                                                                        )}

                                                                        <td className="invoiceChargeDescription">
                                                                            {chargeDetail.description}
                                                                        </td>

                                                                        <td className="invoiceChargeDescription">
                                                                            {!readOnly && (
                                                                                <Textarea
                                                                                    name="userDescription"
                                                                                    value={chargeDetail.userDescription ?? ''}
                                                                                    onChange={(
                                                                                        evt,
                                                                                    ) => {
                                                                                        const { value } = evt
                                                                                            .target;
                                                                                        const {
                                                                                            invoice,
                                                                                        } = {
                                                                                            ...this
                                                                                                .state,
                                                                                        };
                                                                                        invoice.invoiceChargeDetails[
                                                                                            charge_index
                                                                                        ].userDescription = value;
                                                                                        this.setState(
                                                                                            {
                                                                                                invoice,
                                                                                            },
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            {readOnly && (
                                                                                <span>
                                                                                    {chargeDetail.userDescription}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td className="invoiceBillingRate">
                                                                            {chargeDetail.isCredit
                                                                                ? (<>
                                                                                    {' '}
                                                                                    (
                                                                                    <FormatPrice amount={chargeDetail.billingRate} />
                                                                                    )
                                                                                    {' '}
                                                                                </>)
                                                                                : <FormatPrice amount={chargeDetail.billingRate} />
                                                                            }
                                                                        </td>
                                                                        <td
                                                                            className="invoiceUserChargeRate"
                                                                            width="150"
                                                                        >
                                                                            {!readOnly && (
                                                                                <NumericInput
                                                                                    className="form-control-sm text-right"
                                                                                    name="userBillingRate"
                                                                                    required
                                                                                    disabled={chargeDetail.billingUnitName == ChargeTypeUnitNames.Percentage}
                                                                                    value={chargeDetail.userBillingRate ?? ''}
                                                                                    onChange={(
                                                                                        evt,
                                                                                    ) => {
                                                                                        const { value } = evt
                                                                                            .target;
                                                                                        const {
                                                                                            invoice,
                                                                                        } = {
                                                                                            ...this
                                                                                                .state,
                                                                                        };
                                                                                        invoice.invoiceChargeDetails[charge_index].userBillingRate = Number(value ?? 0) || 0;

                                                                                        const qty = Number(invoice.invoiceChargeDetails[charge_index].userChargeQty ?? 0) || 0;
                                                                                        const usage = Number(invoice.invoiceChargeDetails[charge_index].userUsage ?? 1) || 1;
                                                                                        const rate = Number(invoice.invoiceChargeDetails[charge_index].userBillingRate ?? 0) || 0;

                                                                                        invoice.invoiceChargeDetails[charge_index].userChargeAmount = qty * usage * rate;

                                                                                        this.setState({ invoice });
                                                                                        this.updatePercentageSurcharge();
                                                                                        this.redistributeExpandedAmounts(invoice, -1, invoice.invoiceChargeDetails[charge_index].id);
                                                                                    }}
                                                                                    type="number"
                                                                                    min={0}
                                                                                    step={0.001}
                                                                                    preventNegative
                                                                                    fractionDigits={3}
                                                                                />
                                                                            )}
                                                                            {readOnly && (
                                                                                <span>
                                                                                    {chargeDetail.isCredit
                                                                                        ? (<>
                                                                                            {' '}
                                                                                            (
                                                                                            <FormatPrice amount={chargeDetail.userBillingRate} />
                                                                                            )
                                                                                            {' '}
                                                                                        </>)
                                                                                        : <FormatPrice amount={chargeDetail.userBillingRate} />
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td className="invoiceChargeAmount">
                                                                            {chargeDetail.isCredit
                                                                                ? <>({Number(chargeDetail.chargeAmount).toFixed(2)})</>
                                                                                : Number(chargeDetail.chargeAmount).toFixed(2)
                                                                            }
                                                                        </td>
                                                                        <td className="invoiceUserChargeAmount">
                                                                            {chargeDetail.isCredit
                                                                                ? <>({Number(chargeDetail.userChargeAmount).toFixed(2)})</>
                                                                                : Number(chargeDetail.userChargeAmount).toFixed(2)
                                                                            }
                                                                        </td>
                                                                        <td
                                                                            className="invoiceUserChargeTax"
                                                                        >
                                                                            {!readOnly && (
                                                                                <FormCheckbox
                                                                                    id={`${charge_index}.isTaxable`}
                                                                                    name={`${charge_index}.isTaxable`}
                                                                                    checked={chargeDetail.isTaxable ?? ''}
                                                                                    onChange={(e) => {
                                                                                        const {
                                                                                            invoice,
                                                                                        } = {
                                                                                            ...this
                                                                                                .state,
                                                                                        };
                                                                                        invoice.invoiceChargeDetails[
                                                                                            charge_index
                                                                                        ].isTaxable = e.target.checked;
                                                                                        this.setState(
                                                                                            {
                                                                                                invoice,
                                                                                            },
                                                                                        );
                                                                                    }}
                                                                                    disabled={chargeDetail.billingUnitName == ChargeTypeUnitNames.Percentage}
                                                                                />
                                                                            )}
                                                                            {readOnly && (
                                                                                <span>
                                                                                    {chargeDetail.isTaxable ? 'Yes' : 'No'}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        {associateInventoryItem && (
                                                                            <td
                                                                                className="invoiceUserQuickBooksInventoryItem"
                                                                                width="150"
                                                                            >
                                                                                {!readOnly && (
                                                                                    <ValidatedSelect
                                                                                        id={`userQuickBooksInventoryItem-${chargeDetail.id}`}
                                                                                        name={`userQuickBooksInventoryItem-${chargeDetail.id}`}
                                                                                        options={
                                                                                            inventoryItems
                                                                                        }
                                                                                        value={
                                                                                            (
                                                                                                inventoryItems
                                                                                                ?? []
                                                                                            ).find(
                                                                                                (
                                                                                                    s,
                                                                                                ) => s.value
                                                                                                    == invoice
                                                                                                        .invoiceChargeDetails[
                                                                                                        charge_index
                                                                                                    ]
                                                                                                        .userQuickBooksInventoryItem,
                                                                                            )
                                                                                            ?? ''
                                                                                        }
                                                                                        onChange={(
                                                                                            evt,
                                                                                        ) => {
                                                                                            const { value } = evt;
                                                                                            const {
                                                                                                invoice,
                                                                                            } = {
                                                                                                ...this
                                                                                                    .state,
                                                                                            };
                                                                                            invoice.invoiceChargeDetails[
                                                                                                charge_index
                                                                                            ].userQuickBooksInventoryItem = value;
                                                                                            this.setState(
                                                                                                {
                                                                                                    invoice,
                                                                                                },
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                {readOnly && (
                                                                                    <span>
                                                                                        {
                                                                                            chargeDetail.userQuickBooksInventoryItem
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        )}
                                                                        {associateInventoryItem && (
                                                                            <td
                                                                                className="invoiceUserQuickBooksSalesAccount"
                                                                                width="150"
                                                                            >
                                                                                {!readOnly && (
                                                                                    <ValidatedSelect
                                                                                        id={`userQuickBooksSalesAccount-${chargeDetail.id}`}
                                                                                        name={`userQuickBooksSalesAccount-${chargeDetail.id}`}
                                                                                        options={
                                                                                            salesAccounts
                                                                                        }
                                                                                        value={
                                                                                            (
                                                                                                salesAccounts
                                                                                                ?? []
                                                                                            ).find(
                                                                                                (
                                                                                                    s,
                                                                                                ) => s.value
                                                                                                    == invoice
                                                                                                        .invoiceChargeDetails[
                                                                                                        charge_index
                                                                                                    ]
                                                                                                        .userQuickBooksSalesAccount,
                                                                                            )
                                                                                            ?? ''
                                                                                        }
                                                                                        onChange={(
                                                                                            evt,
                                                                                        ) => {
                                                                                            const { value } = evt;
                                                                                            const {
                                                                                                invoice,
                                                                                            } = {
                                                                                                ...this
                                                                                                    .state,
                                                                                            };
                                                                                            invoice.invoiceChargeDetails[
                                                                                                charge_index
                                                                                            ].userQuickBooksSalesAccount = value;
                                                                                            this.setState(
                                                                                                {
                                                                                                    invoice,
                                                                                                },
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                {readOnly && (
                                                                                    <span>
                                                                                        {
                                                                                            chargeDetail.userQuickBooksSalesAccount
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        )}
                                                                        <td className="invoiceChargeDelete text-center">
                                                                            {!chargeDetail.isAdjustment
                                                                                && !readOnly && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`btn shadow-none details-remove-button ${chargeDetail.removed
                                                                                            ? ' removed'
                                                                                            : ''
                                                                                            }`}
                                                                                        title="Remove this invoiceCharge"
                                                                                        onClick={() => {
                                                                                            this.onRemoveInvoiceChargeDetailClicked(
                                                                                                chargeDetail,
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <span className="text-danger mt-1">
                                                                                            <i className="fa fa-trash" />
                                                                                        </span>
                                                                                    </button>
                                                                                )}
                                                                            {!!chargeDetail.isAdjustment
                                                                                && !readOnly && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn shadow-none"
                                                                                        title="Remove this invoiceCharge"
                                                                                        onClick={() => {
                                                                                            this.onRemoveInvoiceChargeDetailClicked(
                                                                                                chargeDetail,
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <span className="text-danger mt-1">
                                                                                            <i className="fa fa-eraser" />
                                                                                        </span>
                                                                                    </button>
                                                                                )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            }

                                                            return null;
                                                        },
                                                    )
                                                )}
                                                <tr>
                                                    <td
                                                        colSpan="8"
                                                        align="right"
                                                    >
                                                        Total:&nbsp;&nbsp;
                                                    </td>
                                                    <td>
                                                        {parseFloat(chargeDetailTotal).toFixed(2)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    {!!(adjustments ?? []).length && (
                                        <div className="invoice">
                                            {true && (
                                                <>
                                                    <small className="text-warning font-weight-bold">
                                                        INVOICE CUSTOMIZATION
                                                    </small>
                                                    <table className="table table-sm table-bordered table-striped payroll-table">
                                                        <thead className="text-muted">
                                                            <tr>
                                                                <th className="isadjusted" />
                                                                {invoice.useCTRReport
                                                                    == 'True' && (
                                                                        <th className="serviceItem">
                                                                            Service
                                                                            Item
                                                                        </th>
                                                                    )}
                                                                <th
                                                                    className="invoiceChargeQty"
                                                                    width="150"
                                                                >
                                                                    Qty
                                                                </th>
                                                                {!!isRentalInvoice && (
                                                                    <th className="invoiceChargeQty">
                                                                        Invoice Usage
                                                                    </th>
                                                                )}
                                                                <th className="invoiceChargeDescription">
                                                                    Description
                                                                </th>
                                                                <th
                                                                    className="invoiceChargeRate"
                                                                    width="150"
                                                                >
                                                                    Rate
                                                                </th>
                                                                <th className="invoiceChargeAmount">
                                                                    Amount
                                                                </th>
                                                                <th className="invoiceChargeTax">
                                                                    Tax
                                                                </th>
                                                                {associateInventoryItem && (
                                                                    <th className="invoiceUserQuickBooksInventoryItem">
                                                                        Inventory
                                                                        Item
                                                                    </th>
                                                                )}
                                                                {associateInventoryItem && (
                                                                    <th className="invoiceUserQuickBooksSalesAccount">
                                                                        Sales
                                                                        Accountt
                                                                    </th>
                                                                )}
                                                                <th
                                                                    style={{
                                                                        width: '5%',
                                                                    }}
                                                                />
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {!(
                                                                adjustments
                                                                ?? []
                                                            ).length ? (
                                                                <tr>
                                                                    <td
                                                                        colSpan="4"
                                                                        className="text-center"
                                                                    >
                                                                        No
                                                                        adjustments
                                                                        found.
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                adjustments.map(
                                                                    (
                                                                        adjustment,
                                                                        i,
                                                                    ) => {
                                                                        if (
                                                                            adjustment.invoiceChargeDetailTypeId
                                                                            == InvoiceChargeDetailType.Header
                                                                        ) {
                                                                            return (
                                                                                <tr key={adjustment.id}>
                                                                                    <td>
                                                                                        <span>
                                                                                            <Badge
                                                                                                color="danger"
                                                                                                className="p-1"
                                                                                            >
                                                                                                HDR
                                                                                            </Badge>
                                                                                        </span>
                                                                                    </td>
                                                                                    <td
                                                                                        className="invoiceChargeDescription"
                                                                                        colSpan="2"
                                                                                    >
                                                                                        <Textarea
                                                                                            name="description"
                                                                                            value={
                                                                                                adjustment.description
                                                                                                ?? ''
                                                                                            }
                                                                                            onChange={(
                                                                                                evt,
                                                                                            ) => {
                                                                                                const { value } = evt
                                                                                                    .target;
                                                                                                const {
                                                                                                    adjustments,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                adjustments[
                                                                                                    i
                                                                                                ].description = value;
                                                                                                this.setState(
                                                                                                    {
                                                                                                        adjustments,
                                                                                                    },
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                    </td>
                                                                                    <td className="invoiceChargeDelete text-center">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn shadow-none"
                                                                                            title="Remove this Charge Detail"
                                                                                            onClick={() => {
                                                                                                const {
                                                                                                    adjustments,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                this.revertChargesForGroup(adjustments[i].invoiceChargeTypeGroupUID);
                                                                                                adjustments.splice(
                                                                                                    i,
                                                                                                    1,
                                                                                                );
                                                                                                this.setState(
                                                                                                    {
                                                                                                        adjustments,
                                                                                                    },
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            <span className="text-danger mt-1">
                                                                                                <i className="fa fa-trash" />
                                                                                            </span>
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        }

                                                                        if (adjustment.invoiceChargeDetailTypeId == InvoiceChargeDetailType.Annotation) {
                                                                            return (
                                                                                <tr key={adjustment.id}>
                                                                                    <td>
                                                                                        <span>
                                                                                            <Badge
                                                                                                color="primary"
                                                                                                className="p-1"
                                                                                            >
                                                                                                TXT
                                                                                            </Badge>
                                                                                        </span>
                                                                                    </td>
                                                                                    <td
                                                                                        className="invoiceChargeDescription"
                                                                                        colSpan="2"
                                                                                    >
                                                                                        <Textarea
                                                                                            name="description"
                                                                                            value={
                                                                                                adjustment.description
                                                                                                ?? ''
                                                                                            }
                                                                                            onChange={(
                                                                                                evt,
                                                                                            ) => {
                                                                                                const { value } = evt
                                                                                                    .target;
                                                                                                const {
                                                                                                    adjustments,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                adjustments[
                                                                                                    i
                                                                                                ].description = value;
                                                                                                this.setState(
                                                                                                    {
                                                                                                        adjustments,
                                                                                                    },
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                    </td>
                                                                                    <td className="invoiceChargeDelete text-center">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn shadow-none"
                                                                                            title="Remove this Charge Detail"
                                                                                            onClick={() => {
                                                                                                const {
                                                                                                    adjustments,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                this.revertChargesForGroup(adjustments[i].invoiceChargeTypeGroupUID);
                                                                                                adjustments.splice(
                                                                                                    i,
                                                                                                    1,
                                                                                                );
                                                                                                this.setState(
                                                                                                    {
                                                                                                        adjustments,
                                                                                                    },
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            <span className="text-danger mt-1">
                                                                                                <i className="fa fa-trash" />
                                                                                            </span>
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        }

                                                                        if (adjustment.invoiceChargeDetailTypeId == InvoiceChargeDetailType.LineItem) {
                                                                            return (
                                                                                <tr
                                                                                    key={adjustment.id}
                                                                                    className={`timesheet-details-table-row${adjustment.removed
                                                                                        ? ' strikeout'
                                                                                        : ''
                                                                                        }`}
                                                                                >
                                                                                    <td>
                                                                                        <span className="invoice-lineitem-isadjusted">
                                                                                            <Badge className="p-1">
                                                                                                ADJ
                                                                                            </Badge>
                                                                                        </span>
                                                                                    </td>

                                                                                    {invoice.useCTRReport == 'True' && (
                                                                                        <td width="350">
                                                                                            <ValidatedSelect
                                                                                                id={`serviceItemADJ-${adjustment.id}`}
                                                                                                name={`serviceItemADJ-${adjustment.id}`}
                                                                                                options={
                                                                                                    serviceItems
                                                                                                }
                                                                                                value={
                                                                                                    (
                                                                                                        serviceItems
                                                                                                        ?? []
                                                                                                    ).find(
                                                                                                        (
                                                                                                            s,
                                                                                                        ) => s.value
                                                                                                            == adjustments[
                                                                                                                i
                                                                                                            ]
                                                                                                                .serviceItem,
                                                                                                    )
                                                                                                    ?? ''
                                                                                                }
                                                                                                onChange={(
                                                                                                    evt,
                                                                                                ) => {
                                                                                                    const { value, id } = evt;

                                                                                                    adjustments[
                                                                                                        i
                                                                                                    ].serviceItem = value;
                                                                                                    adjustments[
                                                                                                        i
                                                                                                    ].serviceItemId = id;
                                                                                                    this.setState(
                                                                                                        {
                                                                                                            adjustments,
                                                                                                        },
                                                                                                    );
                                                                                                }}
                                                                                            />
                                                                                        </td>
                                                                                    )}

                                                                                    <td className="invoiceChargeQty">
                                                                                        <NumericInput
                                                                                            className="form-control-sm text-right"
                                                                                            name="chargeQty"
                                                                                            required
                                                                                            value={adjustment.chargeQty ?? ''}
                                                                                            onChange={(
                                                                                                ev,
                                                                                            ) => this.onAdjustmentChanged(
                                                                                                i,
                                                                                                ev,
                                                                                            )}
                                                                                            type="number"
                                                                                            min={0}
                                                                                            step={0.01}
                                                                                            preventNegative
                                                                                        />
                                                                                    </td>
                                                                                    {Boolean(isRentalInvoice) && (
                                                                                        <td className="invoiceChargeRate">
                                                                                            <NumericInput
                                                                                                className="form-control-sm text-right"
                                                                                                name="userUsage"
                                                                                                required
                                                                                                value={adjustment.userUsage ?? ''}
                                                                                                onChange={(ev) => this.onAdjustmentChanged(i, ev)}
                                                                                                min={0}
                                                                                                step={0.01}
                                                                                            />
                                                                                        </td>
                                                                                    )}
                                                                                    <td className="invoiceChargeDescription position-relative">
                                                                                        <Textarea
                                                                                            required
                                                                                            name="description"
                                                                                            value={
                                                                                                adjustment.description
                                                                                                ?? ''
                                                                                            }
                                                                                            onChange={(
                                                                                                evt,
                                                                                            ) => {
                                                                                                const { value } = evt
                                                                                                    .target;
                                                                                                const {
                                                                                                    adjustments,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                adjustments[
                                                                                                    i
                                                                                                ].description = value;
                                                                                                this.setState(
                                                                                                    {
                                                                                                        adjustments,
                                                                                                    },
                                                                                                );
                                                                                            }}
                                                                                            error="Adjustment description is required."
                                                                                        />

                                                                                        <Button
                                                                                            color="link"
                                                                                            className="position-absolute"
                                                                                            onClick={() => this.openAdjustmentModal(i)}
                                                                                            style={{
                                                                                                top: 0,
                                                                                                right: 0,
                                                                                            }}
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faPlusCircle} />
                                                                                        </Button>
                                                                                    </td>
                                                                                    <td className="invoiceChargeRate">
                                                                                        <NumericInput
                                                                                            className="form-control-sm text-right"
                                                                                            name="billingRate"
                                                                                            required
                                                                                            value={
                                                                                                adjustment.billingRate
                                                                                                ?? ''
                                                                                            }
                                                                                            onChange={(
                                                                                                ev,
                                                                                            ) => this.onAdjustmentChanged(
                                                                                                i,
                                                                                                ev,
                                                                                            )}
                                                                                            min={0}
                                                                                            step={0.001}
                                                                                            preventNegative
                                                                                            fractionDigits={3}
                                                                                        />
                                                                                    </td>

                                                                                    <td className="invoiceChargeAmount">
                                                                                        {adjustment.isCredit
                                                                                            ? (<>
                                                                                                {' '}
                                                                                                (
                                                                                                <PriceDisplay amount={adjustment.chargeAmount} />
                                                                                                )
                                                                                                {' '}
                                                                                            </>)
                                                                                            : <PriceDisplay amount={adjustment.chargeAmount} />
                                                                                        }
                                                                                    </td>
                                                                                    <td
                                                                                        className="invoiceChargeTax"
                                                                                    >
                                                                                        <FormCheckbox
                                                                                            id={`${i}.adjustment.isTaxable`}
                                                                                            name={`${i}.adjustment.isTaxable`}
                                                                                            checked={adjustment.isTaxable ?? false}
                                                                                            onChange={(e) => {
                                                                                                const adjustments = [...this.state.adjustments];
                                                                                                adjustments[i].isTaxable = e.target.checked;
                                                                                                this.setState({ adjustments });
                                                                                            }}
                                                                                            disabled={adjustment.billingUnitName == ChargeTypeUnitNames.Percentage}
                                                                                        />
                                                                                    </td>
                                                                                    {associateInventoryItem && (
                                                                                        <td
                                                                                            className="invoiceUserQuickBooksInventoryItem"
                                                                                            width="150"
                                                                                        >
                                                                                            <ValidatedSelect
                                                                                                id={`userQuickBooksInventoryItemADJ-${adjustment.id}`}
                                                                                                name={`userQuickBooksInventoryItemADJ-${adjustment.id}`}
                                                                                                options={
                                                                                                    inventoryItems
                                                                                                }
                                                                                                value={
                                                                                                    (
                                                                                                        inventoryItems
                                                                                                        ?? []
                                                                                                    ).find(
                                                                                                        (
                                                                                                            s,
                                                                                                        ) => s.value
                                                                                                            == adjustments[
                                                                                                                i
                                                                                                            ]
                                                                                                                .userQuickBooksInventoryItem,
                                                                                                    )
                                                                                                    ?? ''
                                                                                                }
                                                                                                onChange={(
                                                                                                    evt,
                                                                                                ) => {
                                                                                                    const { value } = evt;
                                                                                                    const {
                                                                                                        adjustments,
                                                                                                    } = {
                                                                                                        ...this
                                                                                                            .state,
                                                                                                    };
                                                                                                    adjustments[
                                                                                                        i
                                                                                                    ].userQuickBooksInventoryItem = value;
                                                                                                    this.setState(
                                                                                                        {
                                                                                                            adjustments,
                                                                                                        },
                                                                                                    );
                                                                                                }}
                                                                                            />
                                                                                        </td>
                                                                                    )}
                                                                                    {associateInventoryItem && (
                                                                                        <td
                                                                                            className="invoiceUserQuickBooksSalesAccount"
                                                                                            width="150"
                                                                                        >
                                                                                            <ValidatedSelect
                                                                                                id={`userQuickBooksSalesAccountADJ-${adjustment.id}`}
                                                                                                name={`userQuickBooksSalesAccountADJ-${adjustment.id}`}
                                                                                                options={
                                                                                                    salesAccounts
                                                                                                }
                                                                                                value={
                                                                                                    (
                                                                                                        salesAccounts
                                                                                                        ?? []
                                                                                                    ).find(
                                                                                                        (
                                                                                                            s,
                                                                                                        ) => s.value
                                                                                                            == adjustments[
                                                                                                                i
                                                                                                            ]
                                                                                                                .userQuickBooksSalesAccount,
                                                                                                    )
                                                                                                    ?? ''
                                                                                                }
                                                                                                onChange={(
                                                                                                    evt,
                                                                                                ) => {
                                                                                                    const { value } = evt;
                                                                                                    const {
                                                                                                        adjustments,
                                                                                                    } = {
                                                                                                        ...this
                                                                                                            .state,
                                                                                                    };
                                                                                                    adjustments[
                                                                                                        i
                                                                                                    ].userQuickBooksSalesAccount = value;
                                                                                                    this.setState(
                                                                                                        {
                                                                                                            adjustments,
                                                                                                        },
                                                                                                    );
                                                                                                }}
                                                                                            />
                                                                                        </td>
                                                                                    )}
                                                                                    <td className="invoiceChargeDelete text-center">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn shadow-none"
                                                                                            title="Remove this Charge Detail"
                                                                                            onClick={() => {
                                                                                                const {
                                                                                                    adjustments,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                this.revertChargesForGroup(adjustments[i].invoiceChargeTypeGroupUID);
                                                                                                adjustments.splice(
                                                                                                    i,
                                                                                                    1,
                                                                                                );
                                                                                                this.setState(
                                                                                                    {
                                                                                                        adjustments,
                                                                                                    },
                                                                                                );
                                                                                                this.updatePercentageSurcharge();
                                                                                            }}
                                                                                        >
                                                                                            <span className="text-danger mt-1">
                                                                                                <i className="fa fa-trash" />
                                                                                            </span>
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        }

                                                                        return null;
                                                                    },
                                                                )
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <FlexCenterRow>
                                        {invoice?.invoiceStatusId
                                            != InvoiceStatus.Approved
                                            && invoice?.invoiceStatusId
                                            != InvoiceStatus.Exported && (
                                                <>
                                                    <Button
                                                        className="mb-2"
                                                        size="sm"
                                                        type="button"
                                                        color="secondary"
                                                        onClick={() => {
                                                            const {
                                                                adjustments,
                                                            } = {
                                                                ...this.state,
                                                            };
                                                            const newRecord = new InvoiceChargeDetailAdjustment();
                                                            newRecord.invoiceChargeDetailTypeId = InvoiceChargeDetailType.LineItem;
                                                            adjustments.push(
                                                                newRecord,
                                                            );
                                                            this.setState({
                                                                adjustments,
                                                            });
                                                        }}
                                                    >
                                                        <i className="fa fa-pen-square fa-lg mr-2" />
                                                        Add Adjustment
                                                    </Button>
                                                    &nbsp;&nbsp;
                                                    <Button
                                                        className="mb-2"
                                                        size="sm"
                                                        type="button"
                                                        color="secondary"
                                                        onClick={() => {
                                                            const {
                                                                adjustments,
                                                            } = {
                                                                ...this.state,
                                                            };
                                                            const newRecord = new InvoiceChargeDetailAdjustment();
                                                            newRecord.invoiceChargeDetailTypeId = InvoiceChargeDetailType.Header;

                                                            adjustments.push(
                                                                newRecord,
                                                            );
                                                            this.setState({
                                                                adjustments,
                                                            });
                                                        }}
                                                    >
                                                        <i className="fa fa-heading fa-lg mr-2" />
                                                        Add Header
                                                    </Button>
                                                    &nbsp;&nbsp;
                                                    <Button
                                                        className="mb-2"
                                                        size="sm"
                                                        type="button"
                                                        color="secondary"
                                                        onClick={() => {
                                                            const {
                                                                adjustments,
                                                            } = {
                                                                ...this.state,
                                                            };
                                                            const newRecord = new InvoiceChargeDetailAdjustment();
                                                            newRecord.invoiceChargeDetailTypeId = InvoiceChargeDetailType.Annotation;

                                                            adjustments.push(
                                                                newRecord,
                                                            );
                                                            this.setState({
                                                                adjustments,
                                                            });
                                                        }}
                                                    >
                                                        <i className="fa fa-comment-dots fa-lg mr-2" />
                                                        Add Text
                                                    </Button>
                                                </>
                                            )}
                                    </FlexCenterRow>

                                    {this.renderAdjustmentDescriptionModal()}
                                </UncontrolledCollapseUnderlineHeader>

                                {invoice.invoiceChargeDetails.filter((cd) => !cd.current && cd.parentInvoiceChargeId != null).length > 0 && (
                                    <UncontrolledCollapseUnderlineHeader
                                        headerText="Charge Group Details"
                                    >
                                        <div className="invoice">
                                            <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                                <thead className="text-muted">
                                                    <tr>
                                                        <th className="invoiceChargeQty">
                                                            Qty
                                                        </th>
                                                        <th className="invoiceChargeQty">
                                                            Usage
                                                        </th>
                                                        <th className="invoiceChargeDescription">
                                                            System Description
                                                        </th>
                                                        <th className="invoiceChargeDescription">
                                                            Invoice Description
                                                        </th>
                                                        <th className="invoiceChargeRate">
                                                            System Rate
                                                        </th>
                                                        <th className="invoiceChargeRate">
                                                            Invoice Rate
                                                        </th>
                                                        <th className="invoiceChargeAmount">
                                                            System Amount
                                                        </th>
                                                        <th className="invoiceUserChargeAmount">
                                                            Invoice Amount
                                                        </th>
                                                        {associateInventoryItem && (
                                                            <th className="invoiceUserQuickBooksInventoryItem">
                                                                Inventory Item
                                                            </th>
                                                        )}
                                                        <th>Lock</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoice.invoiceChargeDetails.map(
                                                        (
                                                            parent,
                                                        ) => {
                                                            const readOnly = invoice?.invoiceStatusId == InvoiceStatus.Approved
                                                                || invoice?.invoiceStatusId == InvoiceStatus.Exported;
                                                            const chargesExpanded = invoice.invoiceChargeDetails.filter(
                                                                (x) => x.parentInvoiceChargeId == parent.id,
                                                            );
                                                            if (parent.removed || !parent.isExpanded || chargesExpanded.length == 0) {
                                                                return null;
                                                            }
                                                            return (
                                                                <React.Fragment key={parent.id}>
                                                                    <tr
                                                                        key={parent.id}
                                                                    >
                                                                        <td colSpan="9">
                                                                            <b>{parent.userDescription ?? ''}</b>
                                                                        </td>
                                                                        <td></td>
                                                                    </tr>
                                                                    {chargesExpanded.map((expanded) => {
                                                                        const expandedIndex = invoice.invoiceChargeDetails.findIndex(
                                                                            (x) => x.id === expanded.id,
                                                                        );
                                                                        return (
                                                                            <tr
                                                                                key={`expanded-${expanded.id}`}
                                                                                className="expanded-charge-row"
                                                                            >
                                                                                <td
                                                                                    className="invoiceUserChargeQty"
                                                                                    width="150"
                                                                                >
                                                                                    <span>
                                                                                        {parseFloat(
                                                                                            expanded.userChargeQty,
                                                                                        ).toFixed(
                                                                                            2,
                                                                                        )}
                                                                                    </span>
                                                                                </td>
                                                                                <td
                                                                                    className="invoiceUserChargeQty"
                                                                                    width="150"
                                                                                >
                                                                                    <span>
                                                                                        {parseFloat(
                                                                                            expanded.userUsage,
                                                                                        ).toFixed(
                                                                                            2,
                                                                                        )}
                                                                                    </span>
                                                                                </td>

                                                                                <td className="invoiceChargeDescription">
                                                                                    {expanded.description}
                                                                                </td>

                                                                                <td className="invoiceChargeDescription">
                                                                                    {!readOnly && (
                                                                                        <Textarea
                                                                                            name="userDescription"
                                                                                            value={expanded.userDescription ?? ''}
                                                                                            onChange={(
                                                                                                evt,
                                                                                            ) => {
                                                                                                const { value } = evt
                                                                                                    .target;
                                                                                                const {
                                                                                                    invoice,
                                                                                                } = {
                                                                                                    ...this
                                                                                                        .state,
                                                                                                };
                                                                                                invoice.invoiceChargeDetails[
                                                                                                    expandedIndex
                                                                                                ].userDescription = value;
                                                                                                this.setState(
                                                                                                    {
                                                                                                        invoice,
                                                                                                    },
                                                                                                );
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    {readOnly && (
                                                                                        <span>
                                                                                            {expanded.userDescription}
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="invoiceBillingRate">
                                                                                    {expanded.isCredit
                                                                                        ? (<>
                                                                                            {' '}
                                                                                            (
                                                                                            <FormatPrice amount={expanded.billingRate} />
                                                                                            )
                                                                                            {' '}
                                                                                        </>)
                                                                                        : <FormatPrice amount={expanded.billingRate} />
                                                                                    }
                                                                                </td>
                                                                                <td
                                                                                    className="invoiceUserChargeRate"
                                                                                    width="150"
                                                                                >
                                                                                    {!readOnly && (
                                                                                        <NumericInput
                                                                                            className="form-control-sm text-right"
                                                                                            name="userBillingRate"
                                                                                            required
                                                                                            disabled={expanded.locked}
                                                                                            value={expanded.userBillingRate ?? ''}
                                                                                            onChange={(evt) => {
                                                                                                const { value } = evt.target;
                                                                                                const { invoice } = { ...this.state };
                                                                                                invoice.invoiceChargeDetails[expandedIndex].userBillingRate = Number(value ?? 0) || 0;
                                                                                                this.redistributeExpandedAmounts(invoice, expandedIndex, expanded.parentInvoiceChargeId);
                                                                                            }}
                                                                                            type="number"
                                                                                            min={0}
                                                                                            step={0.001}
                                                                                            preventNegative
                                                                                            fractionDigits={3}
                                                                                        />
                                                                                    )}
                                                                                    {readOnly && (
                                                                                        <span>
                                                                                            {expanded.isCredit
                                                                                                ? (<>
                                                                                                    {' '}
                                                                                                    (
                                                                                                    <FormatPrice amount={expanded.userBillingRate} />
                                                                                                    )
                                                                                                    {' '}
                                                                                                </>)
                                                                                                : <FormatPrice amount={expanded.userBillingRate} />
                                                                                            }
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="invoiceChargeAmount">
                                                                                    {expanded.isCredit
                                                                                        ? <>({Number(expanded.chargeAmount).toFixed(2)})</>
                                                                                        : Number(expanded.chargeAmount).toFixed(2)
                                                                                    }
                                                                                </td>
                                                                                <td className="invoiceUserChargeAmount">
                                                                                    {expanded.isCredit
                                                                                        ? <>({Number(expanded.userChargeAmount).toFixed(2)})</>
                                                                                        : Number(expanded.userChargeAmount).toFixed(2)
                                                                                    }
                                                                                </td>
                                                                                {associateInventoryItem && (
                                                                                    <td
                                                                                        className="invoiceUserQuickBooksInventoryItem"
                                                                                        width="150"
                                                                                    >
                                                                                        {!readOnly && (
                                                                                            <ValidatedSelect
                                                                                                id={`userQuickBooksInventoryItem-${expanded.id}`}
                                                                                                name={`userQuickBooksInventoryItem-${expanded.id}`}
                                                                                                options={
                                                                                                    inventoryItems
                                                                                                }
                                                                                                value={
                                                                                                    (
                                                                                                        inventoryItems
                                                                                                        ?? []
                                                                                                    ).find(
                                                                                                        (
                                                                                                            s,
                                                                                                        ) => s.value
                                                                                                            == invoice
                                                                                                                .invoiceChargeDetails[
                                                                                                                expandedIndex
                                                                                                            ]
                                                                                                                .userQuickBooksInventoryItem,
                                                                                                    )
                                                                                                    ?? ''
                                                                                                }
                                                                                                onChange={(
                                                                                                    evt,
                                                                                                ) => {
                                                                                                    const { value } = evt;
                                                                                                    const {
                                                                                                        invoice,
                                                                                                    } = {
                                                                                                        ...this
                                                                                                            .state,
                                                                                                    };
                                                                                                    invoice.invoiceChargeDetails[
                                                                                                        expandedIndex
                                                                                                    ].userQuickBooksInventoryItem = value;
                                                                                                    this.setState(
                                                                                                        {
                                                                                                            invoice,
                                                                                                        },
                                                                                                    );
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                        {readOnly && (
                                                                                            <span>
                                                                                                {
                                                                                                    expanded.userQuickBooksInventoryItem
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                    </td>
                                                                                )}
                                                                                <td>
                                                                                    <IconButton
                                                                                        title="Lock Rate Recalculation"
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const {
                                                                                                invoice,
                                                                                            } = {
                                                                                                ...this
                                                                                                    .state,
                                                                                            };
                                                                                            invoice.invoiceChargeDetails[
                                                                                                expandedIndex
                                                                                            ].locked = !invoice.invoiceChargeDetails[
                                                                                                expandedIndex
                                                                                            ].locked;
                                                                                            this.setState(
                                                                                                {
                                                                                                    invoice,
                                                                                                },
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            icon={!invoice.invoiceChargeDetails[
                                                                                                expandedIndex
                                                                                            ].locked ? faLockOpen : faLock}
                                                                                        />
                                                                                    </IconButton>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })
                                                                    }
                                                                    <tr>
                                                                        <td colSpan="6">
                                                                        </td>
                                                                        <td>
                                                                            <b>{Number(parent.chargeAmount).toFixed(2)}</b>
                                                                        </td>
                                                                        <td>
                                                                            <b>{Number(parent.userChargeAmount).toFixed(2)}</b>
                                                                        </td>
                                                                        <td></td>
                                                                        <td></td>
                                                                    </tr>
                                                                </React.Fragment>
                                                            );
                                                        },
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </UncontrolledCollapseUnderlineHeader>
                                )}

                                <UncontrolledCollapseUnderlineHeader
                                    headerText="Invoice Summary"
                                >
                                    <div className="invoice">
                                        <table className="table table-sm table-bordered table-striped payroll-table">
                                            <thead className="text-muted">
                                                <tr>
                                                    <th className="invoiceRateType">
                                                        Rate Type Description
                                                    </th>
                                                    <th className="invoiceCalBillQty">
                                                        Calc Bill Qty
                                                    </th>
                                                    <th className="invoiceBillQty">
                                                        Bill Qty
                                                    </th>
                                                    <th className="invoiceVarianceQty">
                                                        Variance
                                                    </th>
                                                    <th className="invoiceCalcBillAmt">
                                                        Calc Bill Amt
                                                    </th>
                                                    <th className="invoiceBillAmt">
                                                        Bill Amt
                                                    </th>
                                                    <th className="invoiceVarianceAmt">
                                                        Variance
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {!(
                                                    invoice.invoiceSummaries
                                                    ?? []
                                                ).length ? (
                                                    <tr>
                                                        <td
                                                            colSpan="7"
                                                            className="text-center"
                                                        >
                                                            No Invoice Summaries
                                                            found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    invoice.invoiceSummaries.map(
                                                        (
                                                            summary,
                                                            charge_index,
                                                        ) => (
                                                            <tr
                                                                key={
                                                                    charge_index
                                                                }
                                                            >
                                                                <td className="invoiceRateType">
                                                                    {
                                                                        summary.rateTypeDescription
                                                                    }
                                                                </td>
                                                                <td className="invoiceCalBillQty">
                                                                    {parseFloat(
                                                                        summary.calcBillQty,
                                                                    ).toFixed(
                                                                        2,
                                                                    )}
                                                                </td>
                                                                <td className="invoiceBillQty">
                                                                    {parseFloat(
                                                                        summary.billQty,
                                                                    ).toFixed(
                                                                        2,
                                                                    )}
                                                                </td>
                                                                <td className="invoiceVarianceQty">
                                                                    {summary.varianceQty
                                                                        ? parseFloat(
                                                                            summary.varianceQty,
                                                                        ).toFixed(
                                                                            2,
                                                                        )
                                                                        : '---'}
                                                                </td>
                                                                <td className="invoiceCalcBillAmt">
                                                                    {summary.isCredit
                                                                        ? (<>
                                                                            {' '}
                                                                            (
                                                                            <PriceDisplay amount={summary.calcBillAmount} />
                                                                            )
                                                                            {' '}
                                                                        </>)
                                                                        : <PriceDisplay amount={summary.calcBillAmount} />
                                                                    }
                                                                </td>
                                                                <td className="invoiceBillAmt">
                                                                    {summary.isCredit
                                                                        ? (<>
                                                                            {' '}
                                                                            (
                                                                            <PriceDisplay amount={summary.billAmount} />
                                                                            )
                                                                            {' '}
                                                                        </>)
                                                                        : <PriceDisplay amount={summary.billAmount} />
                                                                    }
                                                                </td>
                                                                <td className="invoiceVarianceAmt">
                                                                    {summary.varianceAmount ? (
                                                                        <>
                                                                            {summary.isCredit && '('}
                                                                            <PriceDisplay amount={summary.varianceAmount} />
                                                                            {summary.isCredit && ')'}
                                                                        </>
                                                                    ) : (
                                                                        '---'
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ),
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </UncontrolledCollapseUnderlineHeader>
                            </>
                        )}
                        <FlexCenterRow className="pb-3">
                            {invoice?.invoiceStatusId
                                == InvoiceStatus.Approved && (
                                    <Button
                                        type="button"
                                        className="mr-3"
                                        color="secondary"
                                        disabled={!!printing}
                                        onClick={this.onPrint}
                                    >
                                        <FontAwesomeIcon
                                            size="lg"
                                            icon={
                                                printing ? faCircleNotch : faPrint
                                            }
                                            className="mr-2"
                                        />
                                        Print
                                    </Button>
                                )}
                            {!isNew
                                && invoice?.invoiceStatusId
                                <= InvoiceStatus.New && (
                                    <Button
                                        type="button"
                                        className="mr-3"
                                        color="success"
                                        disabled={!!showConfirmDraftModal}
                                        onClick={this.toggleConfirmDraftModal}
                                    >
                                        <FontAwesomeIcon
                                            size="lg"
                                            icon={
                                                showConfirmDraftModal
                                                    ? faCircleNotch
                                                    : faSave
                                            }
                                            className="mr-2"
                                        />
                                        {showConfirmDraftModal
                                            ? 'Saving, Please Wait...'
                                            : 'Create Draft'}
                                    </Button>
                                )}
                            {!isNew
                                && invoice?.invoiceStatusId
                                == InvoiceStatus.Draft && (
                                    <Button
                                        type="button"
                                        className="mr-3"
                                        color="success"
                                        disabled={!!approving}
                                        onClick={this.onApprove}
                                    >
                                        <FontAwesomeIcon
                                            size="lg"
                                            icon={
                                                approving
                                                    ? faCircleNotch
                                                    : faSave
                                            }
                                            className="mr-2"
                                        />
                                        {approving
                                            ? 'Approving, Please Wait...'
                                            : 'Approve Invoice'}
                                    </Button>
                                )}

                            <Button
                                type="submit"
                                className="mr-3"
                                color="primary"
                                disabled={!!saving}
                            >
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={saving ? faCircleNotch : faSave}
                                    className="mr-2"
                                />
                                {saving
                                    ? 'Saving, Please Wait...'
                                    : 'Save Updates'}
                            </Button>

                            {isNew && (
                                <Button
                                    type="button"
                                    className="mr-3"
                                    color="danger"
                                    disabled={!!cancelling}
                                    onClick={this.onClose}
                                >
                                    <FontAwesomeIcon
                                        size="lg"
                                        icon={
                                            cancelling ? faCircleNotch : faSave
                                        }
                                        className="mr-2"
                                    />
                                    {cancelling
                                        ? 'Cancelling, Please Wait...'
                                        : 'Cancel Updates'}
                                </Button>
                            )}

                            {!isNew
                                && invoice?.invoiceStatusId
                                == InvoiceStatus.Approved && (
                                    <Button
                                        type="button"
                                        className="mr-3"
                                        color="success"
                                        disabled={!!reverting}
                                        onClick={this.onRevert}
                                    >
                                        <FontAwesomeIcon
                                            size="lg"
                                            icon={faArrowAltCircleLeft}
                                            className="mr-2"
                                        />
                                        {showDeleteInvoiceModal
                                            ? 'Deleting, Please Wait...'
                                            : 'Return to Draft'}
                                    </Button>
                                )}

                            {!isRentalInvoice && !isNew
                                && invoice?.invoiceStatusId
                                !== InvoiceStatus.New
                                && invoice?.invoiceStatusId
                                !== InvoiceStatus.Exported && (
                                    <Button
                                        type="button"
                                        className="mr-3"
                                        color="warning"
                                        disabled={!!showRestartInvoiceModal}
                                        onClick={
                                            this.toggleRestartInvoiceModal
                                        }
                                    >
                                        <FontAwesomeIcon
                                            size="lg"
                                            icon={faRecycle}
                                            className="mr-2"
                                        />
                                        {showRestartInvoiceModal
                                            ? 'Restarting, Please Wait...'
                                            : 'Restart Invoice'}
                                    </Button>
                                )}

                            {!isNew
                                && invoice?.invoiceStatusId
                                !== InvoiceStatus.Exported && (
                                    <Button
                                        type="button"
                                        className="mr-3"
                                        color="danger"
                                        disabled={!!showDeleteInvoiceModal}
                                        onClick={
                                            this.toggleDeleteInvoiceModal
                                        }
                                    >
                                        <FontAwesomeIcon
                                            size="lg"
                                            icon={
                                                showDeleteInvoiceModal
                                                    ? faCircleNotch
                                                    : faTrash
                                            }
                                            className="mr-2"
                                        />
                                        {showDeleteInvoiceModal
                                            ? 'Deleting, Please Wait...'
                                            : 'Delete Invoice'}
                                    </Button>
                                )}
                        </FlexCenterRow>
                    </SlideForm>
                    {Boolean(invoice?.id) && (
                        <InvoiceHistoryForm
                            show={this.state.showInvoiceHistory}
                            invoiceId={invoice.id}
                            onClose={this.onCloseInvoiceHistory}
                        />
                    )}
                    <SlideForm
                        loading={false}
                        show={this.state.showTimesheetsReorderWidget}
                        id="reorderTimesheetsWidget"
                        formIcon={faFileContract}
                        formTitle="Reorder Timesheets"
                        ref={this.timesheetsWidgetRef}
                        onClose={() => {
                            this.setState(
                                (state) => (
                                    (state.showTimesheetsReorderWidget = false),
                                    state
                                ),
                            );
                        }}
                    >
                        <ReorderListWidget
                            entities={this.state.selectedSortableTimesheets}
                            entityName="sortableText"
                            customColors={false}
                            setUpdatedEntities={(items) => {
                                this.setState({
                                    selectedSortableTimesheets: items,
                                });
                            }}
                            ref={this.timesheetsWidgetRef}
                            onSaveCallback={() => {
                                const { invoice } = { ...this.state };
                                let updated = [...selectedSortableTimesheets];
                                updated = updated.map((x, index) => ({ ...x, ...{ order: (index += 1) } }));
                                invoice.invoiceTimesheets = updated;
                                this.setState({
                                    invoice,
                                    showTimesheetsReorderWidget: false,
                                });

                                this.sortChargeDetailsByTimesheetOrder();

                                this.save();
                            }}
                        />
                    </SlideForm>
                    <SlideForm
                        loading={false}
                        show={this.state.showAddChargeTypeGroupsForm}
                        id="addChargeTypeGroupForm"
                        formIcon={faFileContract}
                        formTitle="Add Charge Type Group"
                        ref={this.addChargeTypeGroupRef}
                        onClose={() => {
                            this.setState(
                                (state) => (
                                    (state.showAddChargeTypeGroupsForm = false),
                                    state
                                ),
                            );
                        }}
                        backdrop={false}
                    >
                        <FormGroup>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="chargeGroupDate"
                                    label="Job Date:"
                                />
                                {editedGroupDetails?.length == 0
                                    && (
                                        <Input
                                            id="chargeGroupDate"
                                            name="chargeGroupDate"
                                            value={moment(this.state.addCTGroupDate).format('YYYY-MM-DD')}
                                            onChange={(ev) => {
                                                const val = ev?.target?.value;
                                                this.setState((state) => ((state.addCTGroupDate = moment(val).format('M/D/YYYY')), state));
                                            }}
                                            required
                                            type="date"
                                        />
                                    )}

                                {editedGroupDetails?.length > 0
                                    && (
                                        <span
                                            id="invoiceDate"
                                            name="invoiceDate"

                                            style={{ marginLeft: '2px' }}
                                        >
                                            {this.state.addCTGroupDate}
                                        </span>
                                    )}
                            </FormGroup>

                            <FormLabel
                                label="Charge Group:"
                                htmlFor="chargeTypeDropdownSelect"
                            />

                            {editedGroupDetails?.length == 0
                                && (
                                    <ValidatedSelect
                                        minMenuHeight={100}
                                        maxMenuHeight={200}
                                        id="chargeTypeDropdownSelect"
                                        name="chargeTypeDropdown"
                                        hideClear
                                        options={chargeTypeGroups}
                                        getOptionLabel={(option) => option.invoiceDescription}
                                        getOptionValue={(option) => option.id}
                                        value={this.state.selectedChargeTypeGroup}
                                        onChange={(selection) => {
                                            this.setState(
                                                {
                                                    selectedChargeTypeGroup: selection,
                                                    overrideChargeTypeRules: !!(selection?.hasTotalCharges && selection?.numTotalCharges),
                                                },
                                            );
                                        }}
                                    />
                                )}

                            {editedGroupDetails?.length > 0
                                && <span className="text-muted">{this.state.selectedChargeTypeGroup?.invoiceDescription}</span>}

                            {this.state.selectedChargeTypeGroup != null
                                && (
                                    <div
                                        className="invoice-details"
                                        style={{ marginTop: '7px', paddingTop: '4px', paddingBottom: '0px' }}
                                    >

                                        <Flex alignItems="center">
                                            <FormLabel
                                                label="Requirements"
                                            />
                                            {this.state.selectedChargeTypeGroup?.hasTotalCharges &&
                                                this.state.selectedChargeTypeGroup?.numTotalCharges ?
                                                (
                                                    <>
                                                        {this.state.selectedChargeTypeGroup?.chargeTypes?.map(
                                                            (ctGroup, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="text-muted mt-1 mr-2"
                                                                >
                                                                    {ctGroup.description}
                                                                </div>
                                                            ),
                                                        )}
                                                        <div className="text-muted mt-1 mr-2">
                                                            Total Number of Charges:{' '}
                                                            {this.state.selectedChargeTypeGroup?.numTotalCharges}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        {this.state.selectedChargeTypeGroup?.chargeTypes?.map(
                                                            (ctGroup, index) => (
                                                                <React.Fragment key={index}>
                                                                    {index > 0 && <span style={{ marginTop: '5px' }}>,&nbsp;</span>}
                                                                    <span
                                                                        className="text-muted"
                                                                        style={{ marginTop: '5px' }}
                                                                    >
                                                                        {ctGroup.description} ({ctGroup.quantity})
                                                                    </span>
                                                                </React.Fragment>
                                                            ),
                                                        )}
                                                    </>
                                                )}
                                        </Flex>
                                    </div>
                                )}

                        </FormGroup>

                        {this.state.addCTGroupDate != null && this.state.selectedChargeTypeGroup != null
                            && (
                                <>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn shadow-none"
                                            title="Add Charges"
                                            style={{ paddingLeft: '2px' }}
                                            onClick={() => {
                                                this.onAddChargeTypeGroupCharge_Add();
                                            }}
                                        >
                                            <span>
                                                <i
                                                    className="fa fa-plus-circle"
                                                    style={{ fontSize: '12px' }}
                                                />
                                                &nbsp;Add Charges
                                            </span>
                                        </button>
                                    </div>

                                    <table className="table table-sm table-bordered table-striped payroll-table">
                                        <thead className="text-muted">
                                            <tr>
                                                <th className="invoiceHourlyCharge">
                                                    Charge
                                                </th>
                                                <th className="invoiceHourlyResource">
                                                    Resource
                                                </th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!editedGroupDetails?.length ? (
                                                <tr>
                                                    <td
                                                        colSpan="11"
                                                        className="text-center"
                                                    >
                                                        No Charges Assigned

                                                    </td>
                                                </tr>
                                            ) : (

                                                editedGroupDetails?.map(
                                                    (
                                                        ctDetail,
                                                    ) => (
                                                        <tr
                                                            key={`${ctDetail.billingDetailId
                                                                }|${uuid()}`}
                                                        >
                                                            <td className="invoiceHourlyCharge">
                                                                {ctDetail.chargeTypeName}
                                                                {' '}
                                                                -
                                                                {ctDetail.calculatedBillingRateTypeName}
                                                            </td>
                                                            <td className="invoiceHourlyResource">
                                                                {
                                                                    ctDetail.resourceName
                                                                }
                                                            </td>
                                                            <td style={{ padding: 0 }}>
                                                                <button
                                                                    type="button"
                                                                    className="btn shadow-none"
                                                                    title="Remove this charge"
                                                                    onClick={() => {
                                                                        ctDetail.invoiceChargeTypeGroupUID = null;

                                                                        this.setState({
                                                                            ctDetail,
                                                                        });
                                                                    }}
                                                                >
                                                                    <span className="text-danger mt-1">
                                                                        <i
                                                                            className="fa fa-trash"
                                                                            style={{ fontSize: '12px' }}
                                                                        />
                                                                    </span>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ),
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </>
                            )}

                        <div />
                        {!(
                            this.state.selectedChargeTypeGroup?.hasTotalCharges &&
                            this.state.selectedChargeTypeGroup?.numTotalCharges
                        ) &&
                            (
                                <FormGroup>
                                    <FormCheckbox
                                        id="overrideChargeTypeRules"
                                        checked={overrideChargeTypeRules}
                                        onChange={(e) => {
                                            overrideChargeTypeRules = e.target.checked;
                                            this.setState(
                                                {
                                                    overrideChargeTypeRules,
                                                },
                                            );
                                        }}
                                        labelText="Override charge type rules"
                                    />
                                </FormGroup>
                            )}

                        <FormGroup>
                            <FormCheckbox
                                id="expandOnCrossDispatch"
                                checked={!!this.state.selectedChargeTypeGroup?.expandOnCrossDispatch}
                                onChange={(e) => {
                                    this.setState(
                                        {
                                            selectedChargeTypeGroup: {
                                                ...selectedChargeTypeGroup,
                                                expandOnCrossDispatch: e.target.checked,
                                            },
                                        },
                                    );
                                }}
                                labelText="Expand on Invoice When Cross-Dispatched Charges Exist"
                            />
                            {!!this.state.selectedChargeTypeGroup?.expandOnCrossDispatch && editedGroupDetails.some((x) => !x.billingRate || x.billingRate === 0) && (
                                <TipAlert
                                    containerClassName="mt-2"
                                    tipText="Warning"
                                    label="Some charge types in this group do not have billing rates configured. Billing rates will be required to approve the invoice.
                                        To prevent this warning on future invoices, add the missing rates to the contract."
                                />
                            )}
                        </FormGroup>
                        <FlexCenterRow className="pb-3">
                            <Button
                                disabled={!!saving}
                                color="success"
                                onClick={() => {
                                    this.onSaveChargeTypeGroup();
                                }}
                            >
                                Add
                            </Button>
                        </FlexCenterRow>

                    </SlideForm>

                    <SlideForm
                        loading={false}
                        show={this.state.showEditChargeTypeGroupsForm}
                        id="editChargeTypeGroupForm"
                        formIcon={faFileContract}
                        formTitle="Edit Charge Type Group"
                        ref={this.editChargeTypeGroupRef}
                        onClose={() => {
                            this.setState(
                                (state) => (
                                    (state.showEditChargeTypeGroupsForm = false),
                                    state
                                ),
                            );
                        }}
                    >

                        <FormGroup>
                            <div
                                className="invoice-details"
                                style={{ paddingTop: '4px', paddingBottom: '0px' }}
                            >
                                <Flex alignItems="center">
                                    <FormLabel
                                        label="Charge Type Group"
                                    />
                                    <span
                                        className="text-muted"
                                        style={{ marginLeft: '16px', marginTop: '5px' }}
                                    >
                                        {this.state.selectedCTGroup?.chargeTypeName}
                                    </span>
                                </Flex>
                                <Flex alignItems="center">
                                    <FormLabel
                                        label="Job Date"
                                    />
                                    <span
                                        className="text-muted"
                                        style={{ marginLeft: '16px', marginTop: '5px' }}
                                    >
                                        {this.state.selectedCTGroup?.dateDisplay}
                                    </span>
                                </Flex>
                            </div>
                        </FormGroup>

                        <div>
                            <button
                                type="button"
                                className="btn shadow-none"
                                title="Add Charges"
                                style={{ paddingLeft: '2px' }}
                                onClick={() => {
                                    this.onAddChargeTypeGroupCharge_Edit();
                                }}
                            >
                                <span>
                                    <i
                                        className="fa fa-plus-circle"
                                        style={{ fontSize: '12px' }}
                                    />
                                    &nbsp;Add Charges
                                </span>
                            </button>
                        </div>

                        <table className="table table-sm table-bordered table-striped payroll-table">
                            <thead className="text-muted">
                                <tr>
                                    <th className="invoiceHourlyCharge">
                                        Charge
                                    </th>
                                    <th className="invoiceHourlyResource">
                                        Resource
                                    </th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {!editedGroupDetails?.length ? (
                                    <tr>
                                        <td
                                            colSpan="11"
                                            className="text-center"
                                        >
                                            No Charges Assigned

                                        </td>
                                    </tr>
                                ) : (

                                    editedGroupDetails?.map(
                                        (
                                            ctDetail,
                                        ) => (
                                            <tr
                                                key={`${ctDetail.billingDetailId
                                                    }|${uuid()}`}
                                            >
                                                <td className="invoiceHourlyCharge">
                                                    {ctDetail.chargeTypeName}
                                                    {' '}
                                                    -
                                                    {ctDetail.calculatedBillingRateTypeName}
                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    {
                                                        ctDetail.resourceName
                                                    }
                                                </td>
                                                <td style={{ padding: 0 }}>
                                                    <button
                                                        type="button"
                                                        className="btn shadow-none"
                                                        title="Remove this charge"
                                                        onClick={() => {
                                                            const updatedGroupDetails = editedGroupDetails.filter(
                                                                (item) => item.billingDetailId !== ctDetail.billingDetailId,
                                                            );
                                                            const updatedCharge = {
                                                                ...ctDetail,
                                                                invoiceChargeTypeGroupUID: null,
                                                            };
                                                            const updatedUnusedCharges = [
                                                                ...(this.state.editedUnusedCharges || []),
                                                                updatedCharge,
                                                            ];

                                                            this.setState({
                                                                editedGroupDetails: updatedGroupDetails,
                                                                editedUnusedCharges: updatedUnusedCharges,
                                                            });
                                                        }}
                                                    >
                                                        <span className="text-danger mt-1">
                                                            <i
                                                                className="fa fa-trash"
                                                                style={{ fontSize: '12px' }}
                                                            />
                                                        </span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ),
                                    )
                                )}
                            </tbody>
                        </table>
                        {!shouldAutoCheckOverride && (
                            <FormGroup>
                                <FormCheckbox
                                    id="overrideChargeTypeRules"
                                    checked={effectiveOverride}
                                    onChange={(e) => {
                                        overrideChargeTypeRules = e.target.checked;
                                        this.setState(
                                            {
                                                overrideChargeTypeRules,
                                            },
                                        );
                                    }}
                                    labelText="Override charge type rules"
                                />
                            </FormGroup>
                        )}
                        <FormGroup>
                            <FormCheckbox
                                id="expandOnCrossDispatch"
                                checked={!!this.state.selectedCTGroup?.expandOnCrossDispatch}
                                onChange={(e) => {
                                    this.setState(
                                        {
                                            selectedCTGroup: {
                                                ...selectedCTGroup,
                                                expandOnCrossDispatch: e.target.checked,
                                            },
                                        },
                                    );
                                }}
                                labelText="Expand on Invoice When Cross-Dispatched Charges Exist"
                            />
                            {!!this.state.selectedCTGroup?.expandOnCrossDispatch && editedGroupDetails.some((x) => !x.billingRate || x.billingRate === 0) && (
                                <TipAlert
                                    containerClassName="mt-2"
                                    tipText="Warning"
                                    label="Some charge types in this group do not have billing rates configured. Billing rates will be required to approve the invoice."
                                />
                            )}
                        </FormGroup>

                        <FlexCenterRow className="pb-3">
                            <Button
                                disabled={!!saving}
                                color="success"
                                onClick={() => {
                                    this.onSaveChargeTypeGroup();
                                }}
                            >
                                Update
                            </Button>
                        </FlexCenterRow>

                    </SlideForm>
                    <SlideForm
                        loading={false}
                        show={this.state.showReorderWidget}
                        id="reorderInvoiceDetailsWidget"
                        formIcon={faFileContract}
                        formTitle="Reorder Invoice Details"
                        ref={this.invoiceDetailsWidgetRef}
                        onClose={() => {
                            this.setState(
                                (state) => (
                                    (state.showReorderWidget = false), state
                                ),
                            );
                        }}
                    >
                        <ReorderListWidget
                            entities={this.state.selectedSortableInvoiceDetails}
                            customColors
                            entityName={[
                                'id',
                                'workOrderNumber',
                                'userDescription',
                            ]}
                            entityColorIndex="invoiceChargeDetailTypeId"
                            entityColorMap={['success', 'danger', 'primary']}
                            setUpdatedEntities={(items) => {
                                this.setState({
                                    selectedSortableInvoiceDetails: items,
                                });
                            }}
                            ref={this.invoiceDetailsWidgetRef}
                            onSaveCallback={() => {
                                const { invoice, selectedSortableInvoiceDetails } = { ...this.state };
                                const fullList = [...invoice.invoiceChargeDetails];
                                selectedSortableInvoiceDetails.forEach((sortedItem, index) => {
                                    const originalItem = fullList.find((x) => x.id === sortedItem.id);
                                    if (originalItem) {
                                        originalItem.order = index + 1;
                                    }
                                });
                                invoice.invoiceChargeDetails = fullList;
                                this.setState({
                                    invoice,
                                    showReorderWidget: false,
                                });

                                this.save();
                            }}
                        />
                    </SlideForm>

                    <ModalForm
                        isOpen={showAddTimesheetModal}
                        onSubmit={() => this.addContractTimesheet()}
                        toggle={() => this.toggleAddTimesheetModal()}
                        title="Add Timesheet"
                        SaveButtonProps={{
                            label: 'Add',
                            saving: isAddingContractTimesheet,
                            disabled: !!saving
                                || !!loading
                                || !!isAddingContractTimesheet
                                || !!isLoadingContractTimesheets,
                        }}
                    >
                        {!isNew
                            && invoice?.invoiceStatusId
                            == InvoiceStatus.Draft && (
                                <FormGroup>
                                    <FormText
                                        style={{ marginBottom: '20px' }}
                                    >
                                        Adding or removing any timesheets
                                        when in the draft stage will remove
                                        and recalculate all Invoice Charge
                                        Details and the Invoice Summary. The
                                        Invoice status will reset to New.
                                    </FormText>
                                </FormGroup>
                            )}
                        {!!isLoadingContractTimesheets && (
                            <>
                                <FontAwesomeIcon
                                    icon={faCircleNotch}
                                    className="fa-spin mr-2"
                                    size="sm"
                                />
                                <span>
                                    Loading timesheets...
                                </span>
                            </>

                        )}

                        {!isLoadingContractTimesheets && (
                            <>
                                <FormGroup>
                                    <span>
                                        Select a timesheet (filtered by the parent
                                        contract and work order number)
                                    </span>
                                </FormGroup>

                                <ValidatedSelect
                                    required
                                    label="Timesheet #"
                                    placeholder="Select a Timesheet"
                                    id="selectedTimesheet"
                                    name="selectedTimesheet"
                                    isClearable
                                    options={contractTimesheets}
                                    value={
                                        (contractTimesheets ?? []).find(
                                            (x) => selectedContractTimesheet?.timesheetId
                                                === x.timesheetId,
                                        ) ?? ''
                                    }
                                    getOptionLabel={(option) => option.jobDateTimesheetNumberDisplay}
                                    getOptionValue={(option) => option.timesheetId}
                                    onChange={
                                        this.onSelectedContractTimesheetChanged
                                    }
                                    error={this.state.validationError ?? 'Please select a timesheet'}
                                />
                            </>
                        )}
                    </ModalForm>

                    <ModalForm
                        size="lg"
                        zIndex={10002}
                        isOpen={showAddChargeTypeGroupModal}
                        onSubmit={() => this.onAddChargesToGroup()}
                        toggle={() => this.toggleAddChargeTypeGroupModal()}
                        title="Add Charges To Group"
                        SaveButtonProps={{
                            label: 'Add',
                        }}
                    >
                        <table className="table table-sm table-bordered table-striped payroll-table">
                            <thead className="text-muted">
                                <tr>
                                    <th />
                                    <th className="invoiceHourlyCharge">
                                        Job Date
                                    </th>
                                    <th className="invoiceHourlyCharge">
                                        Charge
                                    </th>
                                    <th className="invoiceHourlyResource">
                                        Resource
                                    </th>
                                    <th className="invoiceHourlyResource">
                                        Charge Quantity
                                    </th>

                                </tr>
                            </thead>
                            <tbody>
                                {!this.state.editedUnusedCharges?.length ? (
                                    <tr>
                                        <td
                                            colSpan="11"
                                            className="text-center"
                                        >
                                            No Charges Available
                                        </td>
                                    </tr>
                                ) : (

                                    this.state.editedUnusedCharges?.map(
                                        (
                                            ctDetail,
                                        ) => (
                                            <tr
                                                key={`${ctDetail.billingDetailId
                                                    }|${uuid()}`}
                                            >
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={ctDetail.isSelected == true}
                                                        onChange={(x) => {
                                                            ctDetail.isSelected = x.currentTarget.checked;

                                                            this.setState({
                                                                ctDetail,
                                                            });
                                                        }}
                                                    />
                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    {
                                                        ctDetail.dateDisplay
                                                    }
                                                </td>
                                                <td className="invoiceHourlyCharge">
                                                    {ctDetail.chargeTypeName}
                                                    {' '}
                                                    -
                                                    {ctDetail.calculatedBillingRateTypeName}
                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    {
                                                        ctDetail.resourceName
                                                    }
                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    {
                                                        ctDetail.chargeQty
                                                    }
                                                </td>

                                            </tr>
                                        ),
                                    )
                                )}
                            </tbody>
                        </table>
                    </ModalForm>

                    <ModalForm
                        isOpen={showDeleteInvoiceModal}
                        onSubmit={() => this.onDelete()}
                        toggle={() => this.toggleDeleteInvoiceModal()}
                        title="Invoice: Delete Invoice"
                        SaveButtonProps={{
                            label: 'Yes, delete it',
                            savingLabel: 'Deleting',
                            color: 'danger',
                            saving: deleting,
                            icon: faTrash,
                        }}
                    >
                        {isRentalInvoice ? (
                            <>
                                This action will delete this invoice.

                                {Boolean(invoice.isRentalProjectClosed) && (
                                    <TipAlert
                                        containerClassName="mt-2"
                                        tipText="Warning"
                                        label="The project for this invoice is closed - deleting this invoice will re-open the project."
                                    />
                                )}
                                {this.renderBillingChargesTable()}
                            </>
                        ) : (
                            <>
                                This action will delete this invoice, and
                                release timesheets back to a non invoice status.
                            </>
                        )}
                    </ModalForm>

                    <TimesheetDetails
                        ref={this.timesheetDetailsRef}
                        show={this.state.timesheetDetailsOpen}
                        toggleShow={(open) => this.setState({ timesheetDetailsOpen: open })}
                        onClose={this.onTimesheetDetailsClosed}
                        onlyBillable
                        isBilling
                    />


                    <AddressFormNew
                        key={`addr-${invoice.id}-${invoice.countryId}`}
                        id="jobAddressForm"
                        ref={this.addressFormRef}
                        contractUsesPrevailingWage={invoice?.prevailingWage || invoice?.requirePaySchedule}
                        isUnionWork={invoice?.isUnionWork}
                        contractId={invoice?.contractId}
                        workTypeId={invoice?.contractUnionWorkTypeId}
                        countryId={invoice?.countryId}
                        countryCode={invoice?.countryCode}
                        onSaveCallback={(address) => this.onSaveAddress(address)}
                        usCounties={this.state.usCounties}
                        timeZone={invoice?.dispatchCompanyTimeZone?.timeZone}
                    />
                </>
            )
        );
    }
}
